// src/components/Articles.en.js
import { IM, BM } from './Maths';
import { tex } from '../utils/tex';
import React from "react";
import YouTubeEmbed from "./YouTubeEmbed";
import { trackVideoPlay } from "../hooks/useAnalytics";
import { withUtm } from "../utils/utm";
import MktMacro from "../assets/mkt-macro-en.png";
import MktGeo from "../assets/mkt-geo-en.png";
import MktBonds from "../assets/mkt-bonds-en.png";
import MktSectors from "../assets/mkt-sectors-en.png";
import CoverDerivatiPillar from "../assets/kb-cover-derivati-pillar-en.jpg";
import CoverFutures1 from "../assets/kb-cover-futures1-en.jpg";
import CoverFutures2 from "../assets/kb-cover-futures2-en.jpg";
import CoverRiskLab from "../assets/kb-cover-risk-lab-en.png";
import CoverCertificati from "../assets/kb-cover-certificati-en.jpg";
import CoverMetriche from "../assets/kb-cover-metriche.jpg";
import CoverMarketUpdate from "../assets/kb-cover-marketupdate-en.jpg";
import CoverEtfRace from "../assets/kb-cover-etfrace.jpg";
// --- in cima al file, dopo gli altri import ---
import EtfCorrHeatmap from "../assets/etf-corr-heatmap.png";
import EtfDendrogram from "../assets/etf-dendrogram.png";
import EtfAnnualHeatmap from "../assets/etf-annual-heatmap.JPG";
import EconomicRegimeClock from "../assets/economic-regime-clock.jpg";
import dlAnnualized from "../assets/dl-annualized.png";
import dlAllocEpochs from "../assets/dl-alloc-epochs.png";
import dlWeightsAvg from "../assets/dl-weights-avg.png";
import fig1 from "../assets/fig1_dl-etf-global-pred-long.png";
import fig2 from "../assets/fig2_dl-etf-global-pred-short.png";
import fig3 from "../assets/fig3_dl-etf-global-bull-sim-hist.png";
import fig4 from "../assets/fig4_dl-etf-global-bull-sim-movements-hist.png";
import fig5 from "../assets/fig5_dl-etf-global-bull-sim-synth.png";
import fig6 from "../assets/fig6_dl-etf-global-bull-sim-movements-synth.png";
import fig7 from "../assets/fig7_dl-etf-global-covid-sim-hist.png";
import fig8 from "../assets/fig8_dl-etf-global-covid-sim-movements-hist.png";
import fig9 from "../assets/fig9_dl-etf-global-covid-sim-synth.png";
import fig10 from "../assets/fig10_dl-etf-global-covid-sim-movements-synth.png";
// --- IMPORT FIGURE (salva i PNG in: src/assets/fx-hedged/) ---
import fxFig1 from "../assets/headgedSP500.png"; // S&P 500: hedged vs unhedged vs perfect hedge
import fxFig2 from "../assets/headgedGlobal.png"; // MSCI World: hedged vs unhedged vs perfect hedge
import fxFig3 from "../assets/headgedTLT.png"; // Treasury 20+ anni: hedged vs unhedged vs perfect hedge
import MTC from "../assets/MontecarloSimulationCover.JPG";
import DLIntroCover from "../assets/DLIntroCover.png";
import EcoClockCover from "../assets/EcoClockCover.png";
import IndFinCover from "../assets/IndFinCover.png";
import EtfClusterCover   from "../assets/EtfClusterCover.png";
import HedgedCover from "../assets/HedgedCover.png";
import FactorCover from "../assets/FactorCover.png";
import PsyCover from "../assets/PsyCover.png";
import HumanCapitalCover from "../assets/HumanCapCover.png";
import DLSyntCover from "../assets/DLSyntCover.png";
import AlphaBetaCover from "../assets/AlphaBetaCover.png";
import AlphaBetaPlot from "../assets/AlphaBetaPlot.png";
// nel file dove usi le formule
import 'katex/dist/katex.min.css';


/**
 * NOTE SEO:
 * - Aggiunti: slug, author, datePublished, dateUpdated, coverImage, canonical
 * - Esporta anche getArticleBySlug(slug)
 */

const DOMAIN = "https://alphaweb-93f02.web.app";// <-- metti il tuo dominio produzione

const articles = [

  // EN: ETF Portfolio Simulator — methodological and mathematical guide
  {
    slug: "etf-portfolio-simulator-methodological-guide",
    title: "ETF Portfolio Simulator: Methodological and Mathematical Guide",
    metaDescription:
      "ETF portfolio simulator: how it works, what metrics it uses (Sharpe, Sortino, drawdown), assumptions and limitations. Educational tool to compare PIC vs PAC and understand the risk/return trade-off.",
    tags: ["quant", "etf", "education"],
    readTime: "12 min",
    author: "Editorial Team",
    datePublished: "2026-01-2",
    dateUpdated: "2026-01-2",
    coverImage: MTC,
    canonical: `${DOMAIN}/en/kb/etf-portfolio-simulator-methodological-guide`,
    content: (
      <div>
        <h1>ETF Portfolio Simulator: Methodological and Mathematical Guide</h1>

        <p>
          This guide provides a comprehensive review of the results generated by{" "}
          <strong>AlphaFrame’s Quantitative Portfolio Simulator</strong> after updating the code and contribution
          inputs. Unlike the previous version, the simulator now clearly separates metrics computed on the{" "}
          <em>portfolio index</em> (performance without cash flows) from those computed on the{" "}
          <em>investor’s wealth</em> (which includes periodic deposits). We use an initial capital of 10,000 USD,
          a 20-year horizon and, for PAC scenarios, a monthly deposit of 1,000 USD. PIC scenarios have no monthly
          deposits. To try the simulator, visit{" "}
          <a href={withUtm("https://alphaweb-93f02.web.app/en/tools", { source: "site", medium: "article", campaign: "simulatore-etf" })} target="_blank" rel="noopener noreferrer">
            the Tools page (English)
          </a>{" "}
          and watch the demo video below, showing how to configure scenarios and interpret the outputs.
        </p>

        <YouTubeEmbed
          videoId="aWrw2zvIjGw"
          title="ETF Portfolio Simulator (AlphaFrame): backtest & Monte Carlo explained"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <h2>1. Methodology and formulas</h2>

        <h3>Historical backtest</h3>
        <p>
          <strong>EoM alignment</strong> – ETF prices are aligned to the last day of each month (End-of-Month) to
          ensure comparability across time series.
        </p>
        <p>
          <strong>Logarithmic returns</strong> – Each ETF’s monthly log-return is:
        </p>
        <p>
          <BM>{tex`r_t \;=\; \ln\!\left(\frac{p_t}{p_{t-1}}\right)`}</BM>
        </p>
        <p>
          The portfolio’s log-return is the weighted average of the ETFs’ log-returns (implicit monthly rebalancing).
        </p>
        <p>
          <strong>Portfolio index</strong> – Starting from 1, the index evolves as:
        </p>
        <p>
          <BM>{tex`I_t \;=\; I_{t-1}\,\exp\!\big(r^{(p)}_t\big), \quad I_0 = 1`}</BM>
        </p>
        <p>
          representing performance without cash flows.
        </p>
        <p>
          <strong>Wealth with contributions</strong> – The investor’s wealth series evolves as:
        </p>
        <p>
          <BM>{tex`W_t \;=\; \big(W_{t-1} + D\big)\,\exp\!\big(r^{(p)}_t\big), \quad W_0 = \text{initial capital}`}</BM>
        </p>
        <p>
          where <IM>{tex`D`}</IM> is the monthly deposit (0 for PIC, 1,000 USD for PAC). After 20 years, total invested
          capital is <IM>{tex`10{,}000 + 20\times 12\times 1{,}000 = 250{,}000\ \text{USD}`}</IM> for PAC and 10,000 USD
          for PIC.
        </p>

        <p><strong>Index (portfolio) metrics</strong></p>
        <ul>
          <li><strong>CAGR</strong> of the portfolio.</li>
          <li>
            <strong>Sharpe</strong> and <strong>Sortino</strong> ratios (risk-adjusted returns):
            <BM>{tex`\text{Sharpe}_\text{ann} \;=\; \frac{\mu_m}{\sigma_m}\,\sqrt{12}, \qquad \text{Sortino}_\text{ann} \;=\; \frac{\mu_m}{\sigma_{m,\text{down}}}\,\sqrt{12}`}</BM>
          </li>
          <li>
            <strong>Maximum Drawdown (MDD)</strong>, <strong>Ulcer Index</strong> and time under water (average and
            maximum months below the previous high).
          </li>
        </ul>

        <p><strong>Investor (wealth) metrics</strong></p>
        <ul>
          <li>Invested capital (initial plus deposits).</li>
          <li>Final capital (wealth at the end of the backtest).</li>
          <li>
            MDD, Ulcer Index and underwater durations on <IM>{tex`W_t`}</IM> (periodic deposits reduce the depth and
            length of drawdowns).
          </li>
        </ul>

        <h3>Monte Carlo simulation</h3>
        <p>
          To capture future uncertainty, monthly returns are drawn from a{" "}
          <em>multivariate normal</em> distribution parameterised by means, standard deviations and the correlation matrix
          of historical log-returns. Each simulation spans 240 months (20 years); we compute:
        </p>
        <ul>
          <li>
            <em>Index</em>: median, 5th and 95th percentiles of the final index; CAGR, Sharpe, Sortino, MDD and Ulcer
            Index on the median path.
          </li>
          <li>
            <em>Investor</em>: invested capital (10,000 USD + 20×12×1,000 USD = 250,000 USD for PAC, 10,000 USD for PIC),
            median final wealth, CAGR, MDD on the median path and 5th/95th percentiles of final wealth.
          </li>
        </ul>

        <h3>Interpreting the metrics</h3>
        <p>
          <strong>CAGR</strong> – geometric average growth, implicitly penalising volatility; in MC we also measure CAGR
          on median wealth relative to total contributions.
        </p>
        <p>
          <strong>Sharpe</strong> – return per unit of total risk; values &gt;1 are excellent, ~0.5 moderate, &lt;0.2 weak.
        </p>
        <p>
          <strong>Sortino</strong> – focuses on downside deviation; a Sortino &gt; Sharpe suggests upside-skewed volatility.
        </p>
        <p>
          <strong>MDD</strong> – deepest cumulative loss from a peak; warns about the severity of interim declines.
        </p>
        <p>
          <strong>Ulcer Index</strong> – square root of the mean of squared percentage drawdowns; sensitive to duration of stress.
        </p>
        <p>
          <strong>Time under water</strong> – average and maximum consecutive months below the previous high.
        </p>
        <p>
          <strong>Risk-adjusted return</strong> – we use{" "}
          <IM>{tex`\text{CAGR} / \text{MDD}`}</IM> (akin to Calmar) to compare portfolios.
        </p>

        <h2>2. Scenarios and weights</h2>
        <p>We analyse six allocation and contribution combinations:</p>
        <table>
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Selected ETF(s)</th>
              <th>Monthly deposit</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tech PAC</td>
              <td>Only Global Tech</td>
              <td>1,000 USD</td>
              <td>sector-concentrated with deposits</td>
            </tr>
            <tr>
              <td>Tech PIC</td>
              <td>Only Global Tech</td>
              <td>0 USD</td>
              <td>lump-sum technology</td>
            </tr>
            <tr>
              <td>Utilities PAC</td>
              <td>Only Global Utilities</td>
              <td>1,000 USD</td>
              <td>defensive with deposits</td>
            </tr>
            <tr>
              <td>Utilities PIC</td>
              <td>Only Global Utilities</td>
              <td>0 USD</td>
              <td>defensive lump-sum</td>
            </tr>
            <tr>
              <td>Equal PAC</td>
              <td>Ten equally weighted ETFs</td>
              <td>1,000 USD</td>
              <td>diversified with deposits</td>
            </tr>
            <tr>
              <td>Equal PIC</td>
              <td>Same ten equally weighted ETFs</td>
              <td>0 USD</td>
              <td>diversified lump-sum</td>
            </tr>
          </tbody>
        </table>

        <h2>3. Backtest results</h2>
        <h3>3.1 Portfolio (index without flows)</h3>
        <table>
          <thead>
            <tr>
              <th>Scenario</th>
              <th>CAGR</th>
              <th>Sharpe</th>
              <th>Sortino</th>
              <th>MDD</th>
              <th>Ulcer Idx</th>
              <th>Avg TUW</th>
              <th>Max TUW</th>
              <th>CAGR/MDD</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tech PAC</td>
              <td>13.93 %</td>
              <td>0.672</td>
              <td>0.623</td>
              <td>52.53 %</td>
              <td>13.82 %</td>
              <td>5.1</td>
              <td>52</td>
              <td>0.29</td>
            </tr>
            <tr>
              <td>Tech PIC</td>
              <td>13.93 %</td>
              <td>0.672</td>
              <td>0.623</td>
              <td>52.53 %</td>
              <td>13.82 %</td>
              <td>5.1</td>
              <td>52</td>
              <td>0.46</td>
            </tr>
            <tr>
              <td>Utilities PAC</td>
              <td>5.19 %</td>
              <td>0.344</td>
              <td>0.302</td>
              <td>43.85 %</td>
              <td>16.21 %</td>
              <td>10.8</td>
              <td>102</td>
              <td>0.12</td>
            </tr>
            <tr>
              <td>Utilities PIC</td>
              <td>5.19 %</td>
              <td>0.344</td>
              <td>0.302</td>
              <td>43.85 %</td>
              <td>16.21 %</td>
              <td>10.8</td>
              <td>102</td>
              <td>0.16</td>
            </tr>
            <tr>
              <td>Equal PAC</td>
              <td>6.80 %</td>
              <td>0.418</td>
              <td>0.375</td>
              <td>51.73 %</td>
              <td>13.27 %</td>
              <td>5.7</td>
              <td>65</td>
              <td>0.18</td>
            </tr>
            <tr>
              <td>Equal PIC</td>
              <td>6.80 %</td>
              <td>0.418</td>
              <td>0.375</td>
              <td>51.73 %</td>
              <td>13.27 %</td>
              <td>5.7</td>
              <td>65</td>
              <td>0.13</td>
            </tr>
          </tbody>
        </table>

        <h3>3.2 Investor (wealth with contributions)</h3>
        <table>
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Invested capital</th>
              <th>Final capital</th>
              <th>MDD (wealth)</th>
              <th>Ulcer Idx</th>
              <th>Avg TUW</th>
              <th>Max TUW</th>
              <th>CAGR/MDD</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tech PAC</td>
              <td>233,000 USD</td>
              <td>1,539,124 USD</td>
              <td>33.55 %</td>
              <td>7.69 %</td>
              <td>2.74</td>
              <td>18</td>
              <td>0.20</td>
            </tr>
            <tr>
              <td>Tech PIC</td>
              <td>10,000 USD</td>
              <td>112,898 USD</td>
              <td>52.53 %</td>
              <td>13.82 %</td>
              <td>5.1</td>
              <td>52</td>
              <td>0.46</td>
            </tr>
            <tr>
              <td>Utilities PAC</td>
              <td>233,000 USD</td>
              <td>495,284 USD</td>
              <td>20.29 %</td>
              <td>4.36 %</td>
              <td>3.06</td>
              <td>12</td>
              <td>0.09</td>
            </tr>
            <tr>
              <td>Utilities PIC</td>
              <td>10,000 USD</td>
              <td>25,602 USD</td>
              <td>43.85 %</td>
              <td>16.21 %</td>
              <td>10.8</td>
              <td>102</td>
              <td>0.16</td>
            </tr>
            <tr>
              <td>Equal PAC</td>
              <td>233,000 USD</td>
              <td>578,743 USD</td>
              <td>25.87 %</td>
              <td>5.22 %</td>
              <td>2.81</td>
              <td>15</td>
              <td>0.16</td>
            </tr>
            <tr>
              <td>Equal PIC</td>
              <td>10,000 USD</td>
              <td>36,364 USD</td>
              <td>51.73 %</td>
              <td>13.27 %</td>
              <td>5.7</td>
              <td>65</td>
              <td>0.13</td>
            </tr>
          </tbody>
        </table>

        <h2>4. Monte Carlo simulation results</h2>

        <h3>4.1 Portfolio (index without flows)</h3>
        <table>
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Median final index</th>
              <th>CAGR</th>
              <th>Sharpe</th>
              <th>Sortino</th>
              <th>MDD</th>
              <th>Ulcer Idx</th>
              <th>5th perc.</th>
              <th>95th perc.</th>
              <th>CAGR/MDD</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tech PAC</td>
              <td>12.01</td>
              <td>13.23 %</td>
              <td>0.64</td>
              <td>0.67</td>
              <td>45.33 %</td>
              <td>16.60 %</td>
              <td>2.48</td>
              <td>49.83</td>
              <td>0.29</td>
            </tr>
            <tr>
              <td>Tech PIC</td>
              <td>14.58</td>
              <td>14.34 %</td>
              <td>0.73</td>
              <td>0.86</td>
              <td>31.31 %</td>
              <td>10.18 %</td>
              <td>2.70</td>
              <td>52.85</td>
              <td>0.46</td>
            </tr>
            <tr>
              <td>Utilities PAC</td>
              <td>2.72</td>
              <td>5.13 %</td>
              <td>0.34</td>
              <td>0.33</td>
              <td>41.13 %</td>
              <td>18.22 %</td>
              <td>0.99</td>
              <td>8.88</td>
              <td>0.12</td>
            </tr>
            <tr>
              <td>Utilities PIC</td>
              <td>2.79</td>
              <td>5.27 %</td>
              <td>0.33</td>
              <td>0.34</td>
              <td>32.86 %</td>
              <td>11.75 %</td>
              <td>1.11</td>
              <td>8.72</td>
              <td>0.16</td>
            </tr>
            <tr>
              <td>Equal PAC</td>
              <td>4.29</td>
              <td>7.56 %</td>
              <td>0.46</td>
              <td>0.50</td>
              <td>41.93 %</td>
              <td>15.82 %</td>
              <td>1.34</td>
              <td>11.67</td>
              <td>0.18</td>
            </tr>
            <tr>
              <td>Equal PIC</td>
              <td>3.64</td>
              <td>6.67 %</td>
              <td>0.43</td>
              <td>0.43</td>
              <td>50.17 %</td>
              <td>17.82 %</td>
              <td>1.15</td>
              <td>10.62</td>
              <td>0.13</td>
            </tr>
          </tbody>
        </table>

        <h3>4.2 Investor (wealth with contributions)</h3>
        <table>
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Invested capital</th>
              <th>Median final wealth</th>
              <th>CAGR</th>
              <th>MDD (wealth)</th>
              <th>5th perc.</th>
              <th>95th perc.</th>
              <th>CAGR/MDD</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tech PAC</td>
              <td>250,000 USD</td>
              <td>1,255,019 USD</td>
              <td>8.40 %</td>
              <td>42.11 %</td>
              <td>462,437 USD</td>
              <td>3,696,600 USD</td>
              <td>0.20</td>
            </tr>
            <tr>
              <td>Tech PIC</td>
              <td>10,000 USD</td>
              <td>145,824 USD</td>
              <td>14.34 %</td>
              <td>31.31 %</td>
              <td>26,983 USD</td>
              <td>528,456 USD</td>
              <td>0.46</td>
            </tr>
            <tr>
              <td>Utilities PAC</td>
              <td>250,000 USD</td>
              <td>449,264 USD</td>
              <td>2.97 %</td>
              <td>31.58 %</td>
              <td>226,127 USD</td>
              <td>1,001,630 USD</td>
              <td>0.09</td>
            </tr>
            <tr>
              <td>Utilities PIC</td>
              <td>10,000 USD</td>
              <td>27,938 USD</td>
              <td>5.27 %</td>
              <td>32.86 %</td>
              <td>11,061 USD</td>
              <td>87,241 USD</td>
              <td>0.16</td>
            </tr>
            <tr>
              <td>Equal PAC</td>
              <td>250,000 USD</td>
              <td>574,446 USD</td>
              <td>4.25 %</td>
              <td>26.28 %</td>
              <td>275,875 USD</td>
              <td>1,377,700 USD</td>
              <td>0.16</td>
            </tr>
            <tr>
              <td>Equal PIC</td>
              <td>10,000 USD</td>
              <td>36,364 USD</td>
              <td>6.67 %</td>
              <td>50.17 %</td>
              <td>11,453 USD</td>
              <td>106,204 USD</td>
              <td>0.13</td>
            </tr>
          </tbody>
        </table>

        <h2>5. Comparative insights</h2>
        <ul>
          <li>
            <strong>Concentrated vs defensive</strong> – 100 % Tech maximises returns but increases drawdowns and outcome
            dispersion; Utilities lowers volatility but with modest CAGR; Equal-weight is a middle ground.
          </li>
          <li>
            <strong>PIC vs PAC</strong> – Monthly deposits reduce drawdowns and underwater durations, yet dilute wealth
            CAGR; PIC harnesses compounding more but requires patience through longer drawdowns.
          </li>
        </ul>

        <h2>6. Conclusions</h2>
        <p>
          By separating <em>index</em> and <em>wealth</em> metrics, the AlphaFrame simulator clarifies the risk/return
          trade-off. Monte Carlo results emphasise the breadth of plausible outcomes (5th–95th percentiles). Allocation
          and contribution choices should align with objectives, risk tolerance and the ability to withstand extended
          drawdowns.
        </p>

        <h3>Source</h3>
        <ul>
          <li>
            Free ETF Portfolio Simulator –{" "}
            <a href="https://alphaweb-93f02.web.app/en/tools" target="_blank" rel="noopener noreferrer">
              alphaweb-93f02.web.app/en/tools
            </a>
          </li>
        </ul>

        <p>
          <strong>Try it now:</strong>{" "}
          <a href="https://alphaweb-93f02.web.app/en/tools" target="_blank" rel="noopener noreferrer">
            open the Tools page
          </a>{" "}
          and watch the{" "}
          <a href="https://youtu.be/misCKs7346o" target="_blank" rel="noopener noreferrer">
            tutorial video
          </a>{" "}
          to see how to set up PIC/PAC scenarios and read the outputs step by step.
        </p>
      </div>
    ),
  },


  // Article 2: Building a Well-Diversified ETF Portfolio
  {
    slug: "building-a-well-diversified-etf-portfolio",
    title: "Building a Well-Diversified ETF Portfolio",
    metaDescription:
      "How to build an efficient ETF portfolio using correlations and hierarchical clustering. Practical examples, figures and a Colab notebook to replicate the analysis.",
    tags: ["etf", "education", "quant"],
    readTime: "12 min",
    author: "Editorial Team",
    datePublished: "2025-08-22",
    dateUpdated: "2025-08-22",
    coverImage: EtfClusterCover,
    canonical: `${DOMAIN}/en/kb/building-a-well-diversified-etf-portfolio`,
    content: (
      <div>
        <h1>Building a Well-Diversified ETF Portfolio</h1>

        <h2>Introduction</h2>
        <p>
          The objective of this analysis is to show how to compose a diversified and efficient ETF portfolio, eliminating redundant exposures and maximizing
          the risk/return ratio. We will start from a broad set of ETFs covering various economic sectors, asset classes (equities, bonds, commodities) and geographic areas,
          and then use quantitative tools (correlations and hierarchical clustering) to reduce unnecessary overlap. In other words, we will seek
          to understand which ETFs are truly necessary to achieve optimal diversification and how these financial instruments relate to each other statistically.
          The path we will follow reprises the content of one of my YouTube videos, enriched here with references to institutional sources and technical details
          useful for deepening the topic. The analysis is completely reproducible via Python code executable on Google Colab (without the need for local installations)
          which I will share and explain later.
        </p>

        <p>
          Video:{" "}
          <a href="https://youtu.be/avTItKC9iag" target="_blank" rel="noopener noreferrer">
            ETF Portfolio and Diversification – Video
          </a>{" "}
          • Colab Notebook:{" "}
          <a
            href="https://colab.research.google.com/github/paolobalasso/YoutubeTrading/blob/main/USETFsDataExploration.ipynb#scrollTo=3dDuvLacVODy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open on Google Colab
          </a>
        </p>

        <YouTubeEmbed
          videoId="lqZsAXruWzM"
          title="How to optimize your ETF portfolio: correlation and redundancy"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <h2>Data and ETF Categories</h2>
        <p>
          An ETF (Exchange-Traded Fund) is a type of exchange-traded investment fund and passive management that is designed to replicate the performance of a market index.
          <a
            href="https://it.wikipedia.org/wiki/Exchange-traded_fund"
            target="_blank"
            rel="noopener noreferrer"
          >
            (Wikipedia)
          </a>{" "}
          In practice, through a single ETF it is possible to invest in a diversified basket of assets (stocks, bonds, etc.), obtaining broad exposure with a single operation.
          ETFs available on the market are divided into various categories, for example by sector, geographic area or type of investment (equities, bonds, commodities). In
          our dataset we consider a set of 27 very heterogeneous US ETFs, with information for each such as ticker (symbol), TER (total expense ratio), market capitalization,
          ISIN code, reference broker, sector, asset class, income distribution policy (accumulating vs. distributing with quarterly or monthly frequency) and the index replication methodology
          (physical sampling, synthetic, etc.). Most of these funds are sector equities, but bond ETFs, commodities ETFs and regional equity market ETFs are also included.
        </p>

        <p>For clarity, here are the main categories of ETFs in the dataset:</p>
        <ul>
          <li>
            <strong>Equity</strong>: funds that invest in baskets of equities.
            They can focus on specific sectors (e.g. technology, energy, financial, healthcare, consumer staples, utilities, etc.) or geographic areas (e.g. MSCI
            Europe, MSCI EM emerging markets, MSCI EAFE for developed markets outside the US, etc.).
          </li>
          <li>
            <strong>Fixed Income</strong>: funds that invest in fixed-income securities.
            In the dataset we find ETFs on US Treasury Bonds of various maturities (short 1–3 years, medium 7–10 years, long 20+ years), corporate investment grade bonds,
            high yield bonds (speculative grade), and emerging market sovereigns. There are also inflation-linked bonds and <em>convertible securities</em> ETFs.
          </li>
          <li>
            <strong>Commodities</strong>: funds that follow commodity markets. For example, a physically-backed gold ETF, and some ETFs linked to baskets of agricultural or energy commodities (futures contracts).
          </li>
          <li>
            <strong>Multi-asset/Other</strong>: includes instruments that do not fall neatly into the above categories. For example, in the dataset we have a balanced convertible securities ETF (which combines equity and bond characteristics).
          </li>
        </ul>
        <p>
          This rich dataset allows us to analyze very different investments. Each row of the dataset represents a specific ETF and, thanks to the variety of asset
          classes and sectors covered, we can study how each ETF behaves relative to the others in terms of return correlation. The starting hypothesis is that it is not necessary
          to hold all these ETFs at the same time: some may prove redundant, moving very similarly to others. By identifying these overlaps, we can
          simplify the portfolio while retaining only the ETFs that provide a real diversification benefit.
        </p>

        <h2>Correlation Analysis</h2>
        <figure>
          <img
            src={EtfCorrHeatmap}
            alt="Figure 1: Correlation matrix (heatmap) between 27 ETFs"
            style={{ width: "100%", height: "auto" }}
          />
          <figcaption>
            <em>Figure 1</em>: Correlation matrix (heatmap) of historical returns between the 27 ETFs considered. Lighter/red cells indicate high positive correlation, darker/blue cells indicate low or negative correlation.
          </figcaption>
        </figure>
        <p>
          As a first step, we calculate the correlation matrix between the returns of the various ETFs, visualizing it in the heatmap of Figure 1. Correlation measures the similarity between two return series: it ranges between −1 and 1, where values near 1 indicate that two ETFs move almost in unison (perfect positive relationship), while values close to −1 indicate they move in opposite directions (inverse relationship). Values around 0 denote an absence of linear relationship. In our case we see correlations ranging from a maximum of about +0.99 (almost perfectly positive) to a minimum of about −0.35 (moderately inverse). This latter extreme corresponds to the correlation between the very long-term US Treasury Bond ETF (20+ years) and the Financial sector ETF: this means that historically, when long-term government bonds rose, financial stocks tended to fall, and vice versa. This reflects known dynamics of <em>flight-to-quality</em>: during periods of economic uncertainty or market turbulence, investors often abandon riskier equities and move into assets considered safer such as Treasury bonds, causing the latter to rise in price while equities fall.
          <a
            href="https://www.pimco.com/it/it/insights/negative-correlations-positive-allocations"
            target="_blank"
            rel="noopener noreferrer"
          >
            (PIMCO)
          </a>
          In fact, on a general level, the correlation between the equity and bond markets tends to be low or negative during periods of macroeconomic stability (low inflation around 2%): this allows these two asset classes to balance each other in a portfolio,
          reducing overall volatility for the investor.
          <a
            href="https://www.pimco.com/it/it/insights/negative-correlations-positive-allocations"
            target="_blank"
            rel="noopener noreferrer"
          >
            (PIMCO)
          </a>
        </p>
        <p>
          We also observe that other bond ETFs (in particular those on Treasury bonds of various maturities) show negative or near-zero correlations with equity ETFs. Quantitatively, over long horizons, the correlation between government bonds and equities has been close to zero (e.g. 2000–2020 ~0.11 between the global Treasury index and the world equity index), while High Yield is much more correlated with equities (often ~0.7–0.8).{" "}
          <a
            href="https://cdn.janushenderson.com/webdocs/113140_0521_CaseForHighYieldBond_ITA.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            (Janus Henderson)
          </a>
        </p>
        <p>
          Another interesting aspect emerging from the heatmap is the role of gold. The gold ETF shows virtually zero correlation with equity ETFs and only slight positive correlation with Treasuries and inflation-linked bonds: this confirms the role of a <em>safe haven</em> and useful diversifier, providing protection during extreme market events.{" "}
          <a
            href="https://www.wallstreetitalia.com/oro-i-cinque-motivi-per-puntare-sul-bene-rifugio-per-eccellenza/"
            target="_blank"
            rel="noopener noreferrer"
          >
            (State Street / World Gold Council via WSI)
          </a>
        </p>

        <h2>Hierarchical Clustering to Eliminate Redundancies</h2>
        <figure>
          <img
            src={EtfDendrogram}
            alt="Figure 2: Hierarchical clustering dendrogram for ETFs (distance 1−correlation)"
            style={{ width: "100%", height: "auto" }}
          />
          <figcaption>
            <em>Figure 2</em>: Resulting dendrogram from hierarchical clustering on ETFs (distance based on <code>1 − correlation</code>). Colors highlight clusters when cutting the tree at threshold 0.14.
          </figcaption>
        </figure>
        <p>
          The correlation matrix (27×27) is dense; hierarchical clustering groups the ETFs based on the similarity of their movements using the distance{" "}
          <code>d<sub>ij</sub> = 1 − ρ<sub>ij</sub></code>. Reading the dendrogram, for example MSCI Europe and MSCI EAFE are joined at almost zero distance (correlation ~0.99). Developed geographic ETFs form a cluster; emerging markets are further away. Healthcare and Consumer Defensive appear joined at low distance (defensive sectors). Gold remains isolated at the top (maximum diversification). Treasuries of various maturities form a distinct group from equities. To select a lean set, you can cut the tree at 0.14 (≈ correlation 0.86) and keep only one representative ETF per cluster (the one with the best historical return). “Cluster-based” selection approaches are also proposed in the literature to improve diversification with fewer securities.{" "}
          <a
            href="https://fsc.stevens.edu/network-and-clustering-based-portfolio-optimization-enhancing-risk-adjusted-performance-through-diversification/"
            target="_blank"
            rel="noopener noreferrer"
          >
            (Stevens Institute of Technology)
          </a>
        </p>

        <h2>Return and Risk of Selected ETFs</h2>
        <p>
          After eliminating duplicates, we evaluate for each ETF the compounded annual return, volatility, Sharpe ratio and max drawdown. At the top we often find <strong>Technology</strong> (highest returns but also high risk): an “offensive” engine that can suffer sharp drawdowns in negative phases.{" "}
          <a
            href="https://www.alliancebernstein.com/it/it/adviser/insights/investment-insights/redefining-offense-and-defense-in-equities-the-evolution-of-technology-and-healthcare.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            (AllianceBernstein)
          </a>
          . <strong>Healthcare</strong> shows solid return with more contained volatility: a defensive sector that tends to protect in difficult times{" "}
          <a
            href="https://www.alliancebernstein.com/it/it/adviser/insights/investment-insights/redefining-offense-and-defense-in-equities-the-evolution-of-technology-and-healthcare.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            (AB)
          </a>
          . On the bond front, <strong>High Yield</strong> historically has interesting returns with volatility lower than stocks, but high correlation with equities (~0.7–0.8), thus less diversification power in crises{" "}
          <a
            href="https://cdn.janushenderson.com/webdocs/113140_0521_CaseForHighYieldBond_ITA.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            (Janus Henderson)
          </a>
          . <strong>Treasuries</strong> have more modest returns, but low/negative correlation with risk assets and therefore reduce overall portfolio risk{" "}
          <a
            href="https://www.pimco.com/it/it/insights/negative-correlations-positive-allocations"
            target="_blank"
            rel="noopener noreferrer"
          >
            (PIMCO)
          </a>
          . <strong>Gold</strong> adds decorrelation and improves risk-adjusted returns on large portfolios{" "}
          <a
            href="https://www.wallstreetitalia.com/oro-i-cinque-motivi-per-puntare-sul-bene-rifugio-per-eccellenza/"
            target="_blank"
            rel="noopener noreferrer"
          >
            (WGC/SSGA via WSI)
          </a>
          .
        </p>

        <figure>
          <img
            src={EtfAnnualHeatmap}
            alt="Figure 3: Heatmap of annual returns for selected ETFs"
            style={{ width: "100%", height: "auto" }}
          />
          <figcaption>
            <em>Figure 3</em>: Heatmap of annual returns (columns: year; rows: selected ETFs). Green cells indicate years of strong return; red cells indicate negative years.
          </figcaption>
        </figure>

        <h2>Reproducing the Analysis with Google Colab</h2>
        <p>
          A key part of this project is the ability for anyone to replicate the analysis and customize it, using the Python code available in a Google Colab notebook.
          Google Colab is an online platform that allows you to execute Jupyter Notebook code directly in the browser, without installing anything on your PC, leveraging cloud resources.
        </p>
        <ol>
          <li>
            <strong>Accessing the notebook</strong>: open{" "}
            <a
              href="https://colab.research.google.com/github/paolobalasso/YoutubeTrading/blob/main/USETFsDataExploration.ipynb#scrollTo=3dDuvLacVODy"
              target="_blank"
              rel="noopener noreferrer"
            >
              this Colab link
            </a>
            . You will find ready-to-use code and text cells.
          </li>
          <li>
            <strong>Importing libraries</strong>: run the first cells (pandas, numpy, matplotlib, scipy, utility functions).
          </li>
          <li>
            <strong>Loading the data</strong>: the metadata table (ticker, TER, sector, etc.) is read from file; check the preview.
          </li>
          <li>
            <strong>Historical prices</strong>: load the price series; you can extend or shorten the horizon (e.g. 1–10+ years) by modifying the parameters.
          </li>
          <li>
            <strong>Price vs. Total Return</strong>: didactic chart with reinvested dividends for a chosen ETF.
          </li>
          <li>
            <strong>Correlations</strong>: calculation and heatmap of returns (daily or monthly).
          </li>
          <li>
            <strong>Hierarchical clustering</strong>: parameter <code>distance_threshold</code> (e.g. 0.14 ≈ correlation 0.86) to define clusters; automatic selection of the best ETF per cluster.
          </li>
          <li>
            <strong>Visualizations</strong>: metric tables and interactive Plotly graph (returns vs volatility).
          </li>
        </ol>

        <h2>Conclusions</h2>
        <p>
          In this study we demonstrated a <em>data-driven</em> approach to constructing a well-diversified ETF portfolio. Starting from a heterogeneous set of funds,
          correlations and clustering highlighted exposure duplications. Eliminating them leaves a more restrained selection in which each ETF plays a distinct role (equities for regions/sectors, government and corporate bonds, gold, etc.). The key is to balance defensive components (gold, Treasuries) and offensive ones (tech, high yield) according to your profile.
        </p>
        <p>
          Often you don't need to hold dozens of ETFs: it is more effective to choose a few well-targeted funds that cover truly complementary areas. In future insights we will start from this shortlist to address the topic of optimal weights.
        </p>
        <p>
          Questions or doubts? Try the notebook, experiment and{" "}
          <a href="/contact">write to me</a>. And if you prefer the video format, here is the{" "}
          <a href="https://youtu.be/avTItKC9iag" target="_blank" rel="noopener noreferrer">
            complete tutorial on YouTube
          </a>
          .
        </p>

        <h2>Sources</h2>
        <ul>
          <li>
            Wikipedia – Exchange-Traded Fund (ETF):{" "}
            <a
              href="https://it.wikipedia.org/wiki/Exchange-traded_fund"
              target="_blank"
              rel="noopener noreferrer"
            >
              it.wikipedia.org
            </a>
          </li>
          <li>
            PIMCO – Negative correlation between equities and bonds:{" "}
            <a
              href="https://www.pimco.com/it/it/insights/negative-correlations-positive-allocations"
              target="_blank"
              rel="noopener noreferrer"
            >
              pimco.com
            </a>
          </li>
          <li>
            State Street Global Advisors / World Gold Council – Gold as a safe haven:{" "}
            <a
              href="https://www.wallstreetitalia.com/oro-i-cinque-motivi-per-puntare-sul-bene-rifugio-per-eccellenza/"
              target="_blank"
              rel="noopener noreferrer"
            >
              wallstreetitalia.com
            </a>
          </li>
          <li>
            AllianceBernstein – Technology vs Healthcare:{" "}
            <a
              href="https://www.alliancebernstein.com/it/it/adviser/insights/investment-insights/redefining-offense-and-defense-in-equities-the-evolution-of-technology-and-healthcare.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              alliancebernstein.com
            </a>
          </li>
          <li>
            Janus Henderson Investors – High Yield vs Equities/Treasury:{" "}
            <a
              href="https://cdn.janushenderson.com/webdocs/113140_0521_CaseForHighYieldBond_ITA.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              cdn.janushenderson.com
            </a>
          </li>
          <li>
            Stevens Institute of Technology – Clustering and portfolios:{" "}
            <a
              href="https://fsc.stevens.edu/network-and-clustering-based-portfolio-optimization-enhancing-risk-adjusted-performance-through-diversification/"
              target="_blank"
              rel="noopener noreferrer"
            >
              fsc.stevens.edu
            </a>
          </li>
        </ul>
      </div>
    ),
  },

  // Article 3: Understanding Economic Regimes with the Investment Clock
  {
    slug: "understanding-economic-regimes-investment-clock",
    title: "Understanding Economic Regimes with the Investment Clock",
    metaDescription:
      "A comprehensive guide to economic regimes with the Investment Clock: definitions, indicators (real GDP, core inflation), allocations for each phase and links to Colab notebook and video. Includes a figure and two explanatory tables.",
    tags: ["macro", "education", "quant"],
    readTime: "10 min",
    author: "Editorial Team",
    datePublished: "2025-08-22",
    dateUpdated: "2025-08-22",
    coverImage: EcoClockCover,
    canonical: `${DOMAIN}/en/kb/understanding-economic-regimes-investment-clock`,
    content: (
      <div>
        <h1>Understanding Economic Regimes with the Investment Clock</h1>

        <p><strong>Disclaimer:</strong> the information provided is purely educational and does not constitute investment recommendations. Before adopting any financial strategy it is essential to consult a qualified financial advisor.</p>

        <h2>Introduction and Objectives</h2>
        <p>
          The objective of this approach is to classify the phase of the economic cycle we are in by means of macroeconomic indicators and, consequently, to set an allocation across asset classes that historically maximized performance in each scenario. The methodology presented, inspired by Merrill Lynch’s Investment Clock, aims to provide a systematic and reproducible framework, supported by code (Google Colab), to carry out macro-financial analyses on your own.
        </p>

        <h2>The Theoretical Model: the Investment Clock</h2>
        <p>
          The Investment Clock model originates from the studies of Merrill Lynch and was later developed by Trevor Greetham of Royal London Asset Management. The idea is to represent the economic cycle on a dial with two axes: inflation on the horizontal axis and economic growth on the vertical axis. According to RLAM, the economy goes through periods of expansion and contraction, overheating and cooling, with inflation rising or falling; each phase favors different asset classes.
        </p>
        <p><strong>The phases are four:</strong></p>
        <ul>
          <li><strong>Reflation (Reflection)</strong> – weak growth and falling inflation (typical of a bear market phase).</li>
          <li><strong>Recovery</strong> – accelerating growth and still low inflation (“Goldilocks”).</li>
          <li><strong>Overheat</strong> – strong growth bumping into supply constraints, causing inflation to rise.</li>
          <li><strong>Stagflation</strong> – slow/negative growth and high inflation (often preceded by energy shocks).</li>
        </ul>
        <p>The Investment Clock diagram summarizes which asset classes and sectors tend to perform best in each phase, based on over four decades of historical data.</p>

        {/* Figure 1: image */}
        <figure>
          <img
            src={EconomicRegimeClock}
            alt="Figure 1 – The Business Cycle seen as an Investment Clock"
            style={{ width: "100%", height: "auto" }}
          />
          <figcaption>
            <em>Figure 1 – The “Business Cycle” seen as an Investment Clock:</em> on the left the reflation phase with government bonds as favored asset; this is followed by recovery (equities), overheat (commodities) and finally stagflation (cash).
          </figcaption>
        </figure>

        <h2>Preferred Asset Classes in Each Phase</h2>
        <p><em>According to an RLAM (2024) insight on “ClockWise”:</em></p>
        <ul>
          <li><strong>Reflation</strong> – government bonds: better returns when growth is weak and inflation is falling.</li>
          <li><strong>Recovery</strong> – stocks: perform best when growth is strong and inflation continues to fall.</li>
          <li><strong>Overheat</strong> – commodities: record high returns when both growth and inflation are increasing.</li>
          <li><strong>Stagflation</strong> – cash and again commodities: cash becomes the defensive choice while commodities can remain performing.</li>
        </ul>
        <p>
          It is important to emphasize that these asset associations derive from historical observations and do not guarantee future results: markets are complex and correlations can vary. This allocation is a first simplified version that contemplates only 1 asset per economic regime. The difficulty of precisely defining when the economic regime changes and has led to more distributed versions over multiple assets.
        </p>

        <h2>Macro Indicators Used</h2>
        <h3>Gross Domestic Product (GDP)</h3>
        <p>
          Nominal GDP measures the value of goods and services produced at current prices, while real GDP adjusts the figure for inflation. The IMF explains that, to determine real GDP, the nominal value must be adjusted with a price deflator: in this way it is possible to distinguish whether the increase in value derives from greater production or only from higher prices. The growth rate of real GDP is therefore a fundamental indicator for assessing the health of the economy.
        </p>

        <h3>Inflation and Consumer Price Index</h3>
        <p>
          The Consumer Price Index (CPI) measures the average changes in prices of a basket of goods and services; however, to better evaluate long-term trends, central banks use core inflation, which excludes the most volatile components (food and energy). The Federal Reserve notes that central bankers focus on core inflation, e.g. the core PCE deflator, because it removes temporary fluctuations and provides a clearer indication of the underlying trend. Concentrating monetary policy on core inflation helps avoid overreacting to temporary shocks; core inflation “tries to separate the signal from the noise,” providing a better picture of inflationary pressures and serving as a guide as to where headline inflation itself will head.
        </p>

        <h3>Recessions and the Business Cycle</h3>
        <p>
          In determining the phases of the cycle, our analysis considers a “recession” as a period in which real GDP records two consecutive quarters of contraction. However, it must be remembered that this is a convenient criterion: the St. Louis Fed explains that the National Bureau of Economic Research (NBER) uses various indicators (real income, employment, industrial production etc.) to officially declare a recession. It is still true, however, that “two negative quarters” are frequently associated with recessions.
        </p>

        <h2>Classification Method and Historical Results</h2>
        <p>
          The critical point concerns the definition of the conditions that define the economic phases. The technical recession is associated with “two negative quarters”. In our case, the economic phase is determined by evaluating both real GDP and core inflation to see if they are above or below certain thresholds. Clearly these become key parameters to define. In the transcript provided, 2.5% is adopted as the growth threshold and 3% as the inflation threshold: above these values one speaks of “high” growth/inflation, below of “low”. Crossing the two variables yields the four regimes (Reflation: low growth and low inflation; Recovery: high growth and low inflation; Overheat: high growth and high inflation; Stagflation: low growth and high inflation).
        </p>
        <p>
          The historical analysis from 1947 to today (as indicated in the transcript) shows that:
        </p>
        <ul>
          <li>About 61% of the time real GDP growth was above 3% compared to the previous year.</li>
          <li>About 53% of the time inflation (core) was above 3%.</li>
          <li>The rarest phase turns out to be stagflation; followed by contraction and then, almost equally, recovery and overheat (~30–31% each).</li>
          <li>The recessions of the 1980s and the 2008 financial crisis fall into the Reflation phase (low growth, contained inflation), while recent years show a combination of high inflation and moderate growth similar, in terms of behavior, to the early 1980s.</li>
        </ul>

        <h2>Asset Allocation for Each Phase</h2>
        <p>
          An asset allocation scheme known in the Investment Clock world is that proposed by Corey Rittenhouse in the 1980s, taken up in various studies and then reworked by managers such as Ray Dalio (All Weather portfolio). The idea is to assign to each phase a combination of assets that historically maximized returns and minimized risk. In the four-asset version (stocks, bonds, commodities, cash), the weights are 100% when you are certain to be in that phase, as shown in the following table (Figure 2):
        </p>

        {/* Table 1: “Pure” allocation for regime (qualitative, as in text) */}
        <figure>
          <table className="kb-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px" }}>Regime</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px" }}>Dominant Assets</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "8px" }}>Reflation</td>
                <td style={{ padding: "8px" }}>Government bonds; cash</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Recovery</td>
                <td style={{ padding: "8px" }}>Stocks (core); possible bond support</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Overheat</td>
                <td style={{ padding: "8px" }}>Commodities; stocks</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Stagflation</td>
                <td style={{ padding: "8px" }}>Cash; commodities</td>
              </tr>
            </tbody>
          </table>
          <figcaption>
            <em>Figure 2 – “Pure” allocation for each of the four regimes:</em> stocks and bonds for Recovery; commodities and stocks in Overheat; cash and commodities in Stagflation; bonds and cash in Reflation.
          </figcaption>
        </figure>

        <p>
          If you are uncertain of the scenario, you can assign a probability to each phase. For example, assuming the same probability (25%) for every scenario, you get a permanent portfolio with 25% in each asset (stocks, bonds, commodities, gold/cash). This approach is similar to the concept of Harry Browne’s permanent portfolio or Ray Dalio’s All Weather.
        </p>
        <p>
          For greater diversification, Rittenhouse proposes a six-asset matrix (stocks, emerging market bonds, commodities, corporate bonds, Treasury bonds and inflation-linked bonds). In the following table (Figure 3), the rows represent assets, the columns the regimes, and the values express the percentage to invest if you are certain to be in that regime:
        </p>

        {/* Table 2: All Weather static portfolio (uniform probabilities, 6 assets) */}
        <figure>
          <table className="kb-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px" }}>Asset</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px" }}>Weight with uniform probabilities</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "8px" }}>Stocks</td>
                <td style={{ padding: "8px" }}>20%</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Emerging Market Bonds</td>
                <td style={{ padding: "8px" }}>15%</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Commodities</td>
                <td style={{ padding: "8px" }}>14%</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Corporate Bonds</td>
                <td style={{ padding: "8px" }}>6.25%</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Treasury Bonds</td>
                <td style={{ padding: "8px" }}>25%</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Inflation-Linked Bonds</td>
                <td style={{ padding: "8px" }}>20%</td>
              </tr>
            </tbody>
          </table>
          <figcaption>
            <em>Figure 3 – Allocation with 6 assets (static version with uniform probabilities):</em> indicative composition inspired by the Rittenhouse/All Weather scheme. The allocations for individual regimes can be defined in the Colab notebook.
          </figcaption>
        </figure>

        <h2>Final Considerations</h2>
        <ul>
          <li>The Investment Clock model offers a framework to understand the position in the economic cycle and guide asset allocation.</li>
          <li>The use of real GDP and core inflation allows phases to be classified transparently.</li>
          <li>The sheets of Corey Rittenhouse (4 or 6 assets) show how to allocate the portfolio depending on the regime; combining the phases with probabilities yields a robust and balanced portfolio.</li>
        </ul>
        <p>
          It must be emphasized that these associations derive from historical analysis and do not guarantee future results. Every investor should consider their time horizon, risk tolerance and consult a professional before implementing strategies based on these models.
        </p>

        <h2>Useful Links</h2>
        <ul>
          <li>
            <strong>Colab Notebook (methodology):</strong> EconomicRegime.ipynb —{" "}
            <a href="https://colab.research.google.com/github/paolobalasso/YoutubeTrading/blob/main/EconomicRegime.ipynb#scrollTo=hs1XKYDccmTP" target="_blank" rel="noopener noreferrer">
              Open on Google Colab
            </a>
          </li>
          <li>
            <strong>YouTube Video:</strong>{" "}
            <a href="https://youtu.be/vxvqfH1qfuE" target="_blank" rel="noopener noreferrer">
              https://youtu.be/vxvqfH1qfuE
            </a>
          </li>
        </ul>

        <YouTubeEmbed
          videoId="vxvqfH1qfuE"
          title="Economic regimes and the Investment Clock — video"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <h2>Main Sources</h2>
        <ul>
          <li>RLAM “ClockWise” (assets per phase of the cycle) — <a href="https://www.rlam.com" target="_blank" rel="noopener noreferrer">rlam.com</a></li>
          <li>Definitions of real GDP and core inflation — <a href="https://www.imf.org" target="_blank" rel="noopener noreferrer">imf.org</a> · <a href="https://www.federalreserve.gov" target="_blank" rel="noopener noreferrer">federalreserve.gov</a></li>
          <li>NBER recession criteria — <a href="https://www.stlouisfed.org" target="_blank" rel="noopener noreferrer">stlouisfed.org</a></li>
        </ul>
      </div>
    ),
  },

  // Article 4: Independent Financial Advice: Definition, Principles and Benefits
  {
    slug: "independent-financial-advice-definition-principles-benefits",
    title: "Independent Financial Advice: Definition, Principles and Benefits",
    metaDescription:
      "What independent financial advice is according to TUF and MiFID II, differences from traditional advice, principles, practical advantages and institutional references (OCF, CONSOB, FCA).",
    tags: ["education", "advice", "regulation"],
    readTime: "6 min",
    author: "Editorial Team",
    datePublished: "2025-08-22",
    dateUpdated: "2025-08-22",
    coverImage: IndFinCover,
    canonical: `${DOMAIN}/en/kb/independent-financial-advice-definition-principles-benefits`,
    content: (
      <div>
        <h1>What Is Independent Financial Advice?</h1>

        <p>
          In Italy, financial advice is regulated by the{" "}
          <strong>Consolidated Law on Finance</strong> (TUF) and the European{" "}
          <strong>MiFID II</strong> directive. The single register kept by the{" "}
          <strong>OCF</strong> (Organismo di vigilanza e tenuta dell’Albo dei Consulenti Finanziari)
          distinguishes three sections: <em>financial advisers authorized for off-premises offer</em>, <em>independent financial advisers</em> and <em>financial advisory companies</em>.
          Independent advisers and companies are the operators that provide{" "}
          <strong>advice on an independent basis</strong>; they do not place products and do not manage
          assets, but accompany the client in financial planning.
        </p>

        <p style={{ marginTop: "0.75rem" }}>
          <a href="https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.legislativo:1998-02-24;58!vig="
            target="_blank" rel="noopener noreferrer">TUF (Normattiva)</a>{" · "}
          <a href="https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32014L0065"
            target="_blank" rel="noopener noreferrer">MiFID II Directive</a>{" · "}
          <a href="https://www.organismocf.it" target="_blank" rel="noopener noreferrer">OCF – Register</a>
        </p>

        <h2>Differences from “Traditional” Advice</h2>
        <p>
          MiFID II imposes specific requirements on anyone presenting themselves as an independent adviser.
          According to article 24, if an intermediary claims to provide independent advice it must:
        </p>
        <ul>
          <li>
            <strong>analyse a wide range of financial instruments</strong>: it must evaluate a sufficient number and variety of products of different types and origin, without limiting itself to instruments issued by itself or by entities with which it has close ties. The FCA Handbook (United Kingdom) reports the same obligation: the intermediary must evaluate products sufficiently diversified to meet the client's objectives and cannot limit itself to those of its own group.
          </li>
          <li>
            <strong>renounce inducements and commissions</strong>: the independent adviser cannot accept or retain commissions, monetary incentives or non-monetary benefits from third parties in relation to the service provided; he can receive only small benefits that improve the quality of the service and do not compromise independence. The remuneration must therefore come solely from the client, typically in the form of a fee.
          </li>
        </ul>
        <p>
          “Non-independent” or “restrictive advice” allows the intermediary to receive incentives from product issuers and to limit analysis to a restricted universe of instruments. For this reason advisers tied to banks or sales networks are often remunerated based on the commissions collected on the products placed, generating potential conflicts of interest.
        </p>
        <p style={{ marginTop: "0.75rem" }}>
          <a href="https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32014L0065#d1e2797-1-1"
            target="_blank" rel="noopener noreferrer">MiFID II – Article 24</a>{" · "}
          <a href="https://www.handbook.fca.org.uk/handbook/COBS/6.2A" target="_blank" rel="noopener noreferrer">
            FCA Handbook – COBS 6.2A (inducements & independent advice)
          </a>
        </p>

        <h2>Fundamental Principles</h2>
        <p>
          In addition to the obligations above, MiFID II establishes that intermediaries must{" "}
          <strong>always act in the client’s best interest</strong>. In transposing the directive,
          CONSOB reminds that the centrality of the client pervades the entire regulatory framework: intermediaries must serve the best interests of clients, with a high level of fairness and transparency. This principle translates for independent advisers into:
        </p>
        <ul>
          <li>
            <strong>Impartiality</strong>: the absence of commercial links with banks, insurers or SGRs (asset managers) allows them to formulate recommendations based solely on the client’s needs.
          </li>
          <li>
            <strong>Breadth of analysis</strong>: evaluation of different instruments (stocks, bonds, funds, ETFs, insurance policies, etc.) to build tailor-made solutions.
          </li>
          <li>
            <strong>Cost transparency</strong>: the fee-only remuneration is agreed in advance; there are no hidden costs related to rebates or inducements.
          </li>
        </ul>
        <p style={{ marginTop: "0.75rem" }}>
          <a href="https://www.consob.it/web/area-pubblica/mifid2" target="_blank" rel="noopener noreferrer">
            CONSOB – MiFID II (client centrality)
          </a>
        </p>

        <h2>Benefits for Investors</h2>
        <p>Choosing an independent financial adviser entails several practical advantages:</p>
        <ul>
          <li>
            <strong>No conflicts of interest</strong>: the adviser works exclusively for the client and does not receive commissions from product providers, reducing the risk of recommendations motivated by incentives.
          </li>
          <li>
            <strong>Broader and more personalized choice</strong>: the obligation to analyze a sufficient number of different instruments ensures that the strategy takes into account the entire market, not just the offering of one bank or producer.
          </li>
          <li>
            <strong>Holistic planning</strong>: independent advice does not just select individual products, but considers the total assets (liquidity, investments, pensions, taxes, insurance coverage) and the client’s life horizon.
          </li>
          <li>
            <strong>Greater transparency and control</strong>: the client knows in advance the cost of the service and can monitor the portfolio’s performance without fear that the adviser earns from the transactions carried out.
          </li>
        </ul>

        <h2>Conclusions</h2>
        <p>
          <strong>Independent financial advice</strong> represents a relationship model that places the saver at the center, as required by European and Italian regulations. MiFID II rules state that anyone claiming to be independent must examine a wide range of instruments and refuse incentives from third parties. This protects the investor from possible conflicts of interest and fosters more objective, transparent and tailor-made advice. However, it is important to check that the professional or company is effectively registered in the OCF under the sections for{" "}
          <strong>independent financial advisers</strong> or {" "}
          <strong>financial advisory companies</strong>.
        </p>
      </div>
    ),
  },

  // Article 5: Deep Learning and Asset Allocation: A Guide for Financial Consultants
  {
    slug: "deep-learning-and-asset-allocation-a-guide-for-financial-consultants",
    title: "Deep Learning and Asset Allocation: A Guide for Financial Consultants",
    metaDescription:
      "What deep learning is, how it applies to multi-asset tactical allocation, strengths relative to traditional ML, risks, governance and institutional references (ECB, PRA/BoE).",
    tags: ["ai", "deep learning", "quant", "allocation", "education"],
    readTime: "9 min",
    author: "Editorial Team",
    datePublished: "2025-08-22",
    dateUpdated: "2025-08-22",
    coverImage: DLIntroCover,
    canonical: `${DOMAIN}/en/kb/deep-learning-and-asset-allocation-a-guide-for-financial-consultants`,
    content: (
      <div>
        <h1>Deep Learning and Asset Allocation: A Guide for Tactical Allocation</h1>
        <YouTubeEmbed
          videoId="8VLgtKfG21s"
          title="Deep Learning for ETF portfolio allocation: can it beat the S&P 500?"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />


        <h2>Introduction</h2>
        <p>
          The modern financial system is undergoing a profound transformation. In a context where data flows, news and market signals are increasingly complex, technology becomes an indispensable tool for managing capital. Artificial intelligence (AI) in particular has increased the ability to process information, recognize patterns and predict scenarios, but also brings new risks and challenges. The European Central Bank (ECB) emphasizes that the emergence of generative AI tools constitutes a technological leap with potentially significant impacts on the financial system: AI brings benefits and risks, and its spread involves issues related to data, model development and implementation; concentrated adoption could amplify operational risk, market concentration and imitative behavior, possibly requiring targeted regulatory initiatives.
        </p>
        <p>
          For financial consultants it is therefore fundamental to understand both the opportunities and the limits of these technologies and to be able to explain them to clients. In this article we retrace the concepts of Deep Learning (DL) and illustrate its application in a proprietary tactical allocation model, highlighting its strengths relative to traditional Machine Learning (ML) methods and the governance requirements.
        </p>

        <h2>What is Deep Learning</h2>
        <p>
          Deep Learning is a branch of Machine Learning based on deep neural networks which, thanks to numerous layers of non-linear processing, manage to capture complex relationships in data. Unlike traditional ML algorithms (such as linear regressions, decision trees or SVM) which require intense feature engineering and often struggle to model temporal structures, neural networks can process sequences and large amounts of variables directly. A recent academic survey shows that DL allows optimizing a portfolio directly or replicating an index with fewer assets; AI produces better estimates of return and risk and solves optimization problems with complex constraints. However, the same source notes that AI-based models are often black boxes: the lack of transparency creates interpretability challenges for investors and regulators.
         </p>
        <p>
          In the field of time series forecasting, DL algorithms show superior performance compared to classical statistical models. For example, traditional approaches such as ARIMA have been surpassed by Long Short-Term Memory (LSTM) models; other architectures – such as Gated Recurrent Unit (GRU), Seq2Seq models and Convolutional Neural Networks (CNN) – continue to achieve leading results in market forecasting. These advances derive from the ability of neural networks to capture non-linear and dynamic dependencies among financial variables, going beyond Markowitz’s mean-variance theory.
        </p>

        <h2>Regulatory Context and Risk Management</h2>
        <p>
          The introduction of AI-based models requires an adequate model risk management framework. The Bank of England’s Prudential Regulation Authority (PRA) notes that model risk management is increasingly important to mitigate the risks related to AI use: the current UK regulation provides rules only for capital or stress test models, without explicit references to AI and model explainability. The PRA proposes introducing governance principles for all phases of the model lifecycle – identification and classification of models, governance, development, independent validation and mitigation measures – applicable to AI models. In parallel, the ECB notes that widespread AI adoption by a few providers could increase systemic risks: greater concentration, operational fragility and imitative behavior. It is therefore essential for operators to adopt validation, explainability and “kill switch” procedures to stop algorithms that behave abnormally.
        </p>

        <h2>The Deep Learning Model for Tactical Allocation</h2>

        <h3>Data and Preparation</h3>
        <p>
          The proprietary model illustrated in this article aims to achieve capital appreciation through multi-asset tactical management. The portfolio considers eight liquid ETFs belonging to different asset classes (gold, utilities, consumer discretionary, energy, consumer staples, technology, US long-term government bonds, healthcare). The data used spans from February 2006 to February 2025. For model training, a walk-forward approach was adopted: the period up to February 2020 is used for learning, while the subsequent years (including the pandemic episode) represent out-of-sample validation to assess the model’s adaptability.
        </p>
        <p>
          The data gathering phase includes aligning asset time series, integrating macroeconomic indicators with realistic lags, and adding technical indicators (RSI, MACD, moving averages, etc.). Numeric variables are benchmarked against the S&amp;P 500 (SPY ETF) to provide a measure of relative performance. Seasonal variables are added to capture market cycles. The data are normalized and transformed into time sequences with 71 features for each asset.
        </p>

        <h3>Architecture and Methodology</h3>
        <ul>
          <li><strong>LSTM layers</strong>: model the temporal dependence of financial returns, capturing long-term patterns.</li>
          <li><strong>Convolutional layers with attention mechanism</strong>: highlight relationships among assets and dynamically weight the most relevant information.</li>
          <li><strong>Dropout and Batch Normalization</strong>: improve generalization and training stability.</li>
          <li><strong>Softmax output layer</strong>: returns a vector of weights (allocation) across the eight asset classes.</li>
        </ul>
        <p>
          A key element is the financial loss function. In addition to classic prediction error minimization, the loss includes components that maximize the Sharpe ratio, penalize portfolio concentration via an entropy term and reward excess return relative to the benchmark. This aligns the learning with metrics that matter to investors. Training is iterative: for each time window the model trains on available data and validates on subsequent data. Using walk-forward reduces overfitting, because the model never sees both past and future simultaneously.
        </p>

        <h3>Training Results</h3>
        <p>
          The model was trained on data from February 2006 to February 2020, deliberately excluding the COVID-19 crisis to evaluate out-of-sample adaptability. Nonetheless, the model showed high resilience, dynamically adjusting allocations during the pandemic period and minimizing drawdowns.
        </p>
        <p>
          The figures below illustrate the model’s behaviour as training epochs vary, highlighting improvements in return delta relative to the benchmark and increased robustness obtained through walk-forward adaptation. These snapshots exemplify the model's ability to learn from evolving market conditions and refine allocations accordingly.
        </p>
        <p>
          The evolution across epochs shows a clear process of “allocation learning”. In the early iterations, the portfolio maintains a more diversified and stable composition, with a contained allocation delta between ETFs: weights vary slowly and the model tends to distribute risk across more assets (sector equity, TLT and GLD). At this stage the cumulative return already exceeds SPY, but the advantage is still moderate: the alpha derives mainly from better diversification and a more orderly exposure to trends.
        </p>

        {/* FIGURE 2 — Evolution of allocations by epoch */}
        <figure>
          <a href={dlAllocEpochs} target="_blank" rel="noopener noreferrer">
            <img
              src={dlAllocEpochs}
              alt="Evolution of allocations by epoch: allocation bands (regime switching)"
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>Figure 2 — Evolution of allocations by epoch (regime switching).</figcaption>
        </figure>

        <p>
          Advancing through epochs, the model “learns” regimes and transitions: the allocation delta increases, clear rotations appear and periods of selective concentration on segments with more favorable momentum or carry (e.g., overweight on technology/consumer discretionary in bull markets; reallocations towards TLT/GLD during stress phases – see the 2008 crisis). These more decisive choices are reflected in progressively steeper equity curves in both training and test, with an increasing gap over the benchmark: the portfolio not only participates in rallies, but avoids drawdowns more promptly, improving the risk/return profile.
        </p>
        <p>
          In the later epochs the dynamic becomes even more evident: the allocation bands show compact blocks (regime switching), indicating greater statistical confidence of the model in selecting the drivers of performance for the period. The increase in cumulative return is the direct consequence of three factors: (i) faster timing in regime transitions, (ii) tactical concentration on winning assets when evidence is strong, (iii) timely de-risking towards safe-haven assets when the context worsens. The result is a more stable alpha: the portfolio tends to gain more in favorable phases and lose less in corrections, consolidating superiority compared to SPY and 60/40.
        </p>

        <p>
          After processing, allocations are filtered to make them implementable: minimum position thresholds are applied, the rebalancing frequency is limited and real costs (26% capital gains tax and 0.3% transaction costs) are incorporated. This level of filtering reduces the average number of reallocations to about 7.7 changes per year.
        </p>

        <h3>Main Results</h3>
        <p>The model’s performance is summarized in the following charts and tables.</p>

        <table>
          <thead>
            <tr>
              <th></th>
              <th>2016</th>
              <th>2017</th>
              <th>2018</th>
              <th>2019</th>
              <th>2020</th>
              <th>2021</th>
              <th>2022</th>
              <th>2023</th>
              <th>2024</th>
              <th>2025</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Portfolio</td>
              <td>14.7%</td>
              <td>36.6%</td>
              <td>29.8%</td>
              <td>41.6%</td>
              <td>45.4%</td>
              <td>56.4%</td>
              <td>-0.8%</td>
              <td>65.8%</td>
              <td>23.4%</td>
              <td>5.6%</td>
            </tr>
            <tr>
              <td>SPY</td>
              <td>12.0%</td>
              <td>21.7%</td>
              <td>-4.6%</td>
              <td>31.2%</td>
              <td>18.3%</td>
              <td>28.7%</td>
              <td>-18.2%</td>
              <td>26.2%</td>
              <td>24.9%</td>
              <td>-0.2%</td>
            </tr>
            <tr>
              <td>60-40</td>
              <td>8.1%</td>
              <td>16.8%</td>
              <td>-2.9%</td>
              <td>24.7%</td>
              <td>21.5%</td>
              <td>14.8%</td>
              <td>-22.8%</td>
              <td>16.9%</td>
              <td>10.9%</td>
              <td>1.9%</td>
            </tr>
          </tbody>
        </table>
            
        {/* FIGURE 1 — Annualized Returns */}
        <figure>
          <a href={dlAnnualized} target="_blank" rel="noopener noreferrer">
            <img
              src={dlAnnualized}
              alt="Annualized Returns 2016–2025: DL portfolio vs SPY and 60/40"
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>Figure 1 — Annualized Returns 2016–2025 (YTD): DL portfolio vs SPY and 60/40.</figcaption>
        </figure>

        <p>
          In the years 2016–2025 (YTD) the “Annualized Returns” chart clearly shows a systematic advantage of the model-generated portfolio over the two market benchmarks (SPY and 60/40). The most relevant aspect is not just the size of positive peaks, but the ability to maintain robust performance across very different market contexts, suggesting a dynamic allocation that gradually adapts to the prevailing regime.
        </p>
        <p>
          In stress periods the model has mitigated losses or even generated positive returns: in 2018 it ended with a strong rally (+29.8%) against a declining market (SPY –4.6%), while in 2022 it limited the damage to –0.8% compared with sharp corrections for both SPY (–18.2%) and 60/40 (–22.8%). This resilience to drawdowns is consistent with an opportunistic rebalancing process, reducing exposure to the most vulnerable segments when volatility concentrates.
        </p>
        <p>
          In expansionary phases the model is able to amplify the upside: the 2019–2021 sequence shows significant extra-returns (up to +56.4% in 2021), and 2023 is another emblematic example with +65.8% vs +26.2% for SPY. Even when the market runs strong, the portfolio maintains a favorable differential in most years. The exception is 2024, where the model remains at excellent levels (+23.4%) though slightly below SPY (+24.9%) and well above 60/40 (+10.9%). The start of 2025 YTD confirms resilience (+5.6% vs –0.2% SPY and +1.9% 60/40).
        </p>
        <p><em>(Note: 2025 is until February; past performance does not guarantee future results.)</em></p>

        {/* FIGURE 3 — Average Portfolio Composition */}
        <figure>
          <a href={dlWeightsAvg} target="_blank" rel="noopener noreferrer">
            <img
              src={dlWeightsAvg}
              alt="Average composition of the DL portfolio by asset/sector"
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
            <figcaption>Figure 3 — Average portfolio composition (average weights per asset/sector).</figcaption>
        </figure>

        <p>
          The average composition of the portfolio highlights the model’s orientation: approximately 46.4% in technology (IYW ETF), 13.2% in gold (GLD), 11.4% in energy (IYE) and 10.8% in utilities (IDU). The other asset classes have smaller weights: consumer discretionary 8.4% (IYC), healthcare 3.5% (XLV), long-term Treasury 6.3% (TLT) and zero exposure to consumer staples (IYK). This distribution reflects a preference for growth sectors but maintains diversification in defensive assets such as gold and government bonds.
        </p>
        

        <h2>Comparison with Other Machine Learning Methodologies</h2>
        <p>
          Traditional ML methods – such as regressions, decision trees or Bayesian networks – are effective when the data dimension is contained or patterns are relatively simple. However, to capture the non-linearity and sequential relationships typical of financial markets, the literature shows that DL models – particularly LSTM, CNN, Seq2Seq and Generative Adversarial Networks – achieve better results: for example, LSTM far outperforms ARIMA in forecasting and techniques such as CNNs, originally developed for images, provide more accurate predictions on financial data than non-neural methods. In addition, DL models can process dozens of technical, macroeconomic and sentiment indicators simultaneously.
        </p>
        <p>
          Our model possesses further distinctive characteristics compared with many ML applications on the market:
        </p>
        <ul>
          <li>
            <strong>Customized financial loss</strong>: while many approaches simply optimize prediction error, our model maximizes the Sharpe ratio and excess return, penalizing lack of diversification. This alignment with investor objectives is hard to achieve with traditional ML techniques.
          </li>
          <li>
            <strong>Walk-forward optimization</strong>: training on sliding windows reduces overfitting compared with static back-tests. Academic literature acknowledges that training on “future data” introduces bias; our approach, training only on data available at the moment, minimizes this risk.
          </li>
          <li>
            <strong>Operational filtering</strong>: integrating real costs and applying rebalancing thresholds ensures that the proposed strategies are implementable and not just theoretical simulations.
          </li>
        </ul>
        <p>
          Nevertheless, DL has limitations to consider. First, it requires a large amount of data and considerable computing resources; moreover, the complexity makes it difficult to explain why the model makes certain decisions, a problem of interpretability already noted by researchers. The adoption of explainable AI techniques and a robust governance framework (as recommended by the PRA) are therefore fundamental to mitigate model risk.
        </p>

        <h2>Conclusion</h2>
        <p>
          The evolution of quantitative finance shows how AI, and in particular DL, can offer significant opportunities to improve investment decisions. Academic evidence shows that DL models outperform traditional methods in forecasting and portfolio allocation, and institutions recognize the transformative potential but warn of concentration risks and the need for effective governance. The model presented here, with its LSTM-CNN-Attention architecture, customized financial loss and operational filtering, represents a concrete example of how DL can be applied efficiently and responsibly to multi-asset portfolio management.
        </p>
        <p>
          For financial consultants, integrating these tools means not only potentially obtaining superior performance, but also equipping themselves with a holistic view of market dynamics. However, in recommending or using AI-based solutions it is indispensable to maintain control and transparency, ensuring that algorithms are well understood, validated and aligned with the client’s needs. Only then can the full opportunities of the future be captured while remaining within an acceptable risk perimeter.
        </p>

        <h2>References</h2>
        <ol>
          <li>
            The rise of artificial intelligence: benefits and risks for financial stability — ECB.
          </li>
          <li>
            Enhancing portfolio management using artificial intelligence: literature review — PMC.
          </li>
          <li>
            (Interpretability) Same, sections on explainability and black box models.
          </li>
          <li>
            (Time-series forecasting with DL) Same, sections on LSTM/GRU/CNN/Seq2Seq.
          </li>
          <li>
            DP5/22 — Artificial Intelligence and Machine Learning | Bank of England (PRA).
          </li>
          <li>
            (Model lifecycle governance) Same — principles proposed by the PRA.
          </li>
        </ol>
      </div>
    ),
  },

  // Article 6: Deep Learning Portfolio Allocation with Global ETFs: Training, Simulations and Stress Tests
  {
    slug: "deep-learning-portfolio-allocation-global-etfs-training-simulations-stress-tests",
    title: "Deep Learning Portfolio Allocation with Global ETFs: Training, Simulations and Stress Tests",
    metaDescription:
      "Global version of the DL allocation model: pipeline, architecture, backtest, two simulation methodologies (historical replay and correlated synthetic returns), bull/Covid/Financial crisis scenarios and conclusions.",
    tags: ["quant", "deep learning", "asset allocation"],
    readTime: "9 min",
    author: "Editorial Team",
    datePublished: "2025-09-05",
    dateUpdated: "2025-09-05",
    coverImage: DLSyntCover,
    canonical: `${DOMAIN}/en/kb/deep-learning-portfolio-allocation-global-etfs-training-simulations-stress-tests`,
    content: (
      <div>
        <h1>Deep Learning Portfolio Allocation with Global ETFs: Training, Simulations and Stress Tests</h1>
        <YouTubeEmbed
          playlistId="PL4Ivcohv28tKP2PntUWhhOhkRC0438kzL"
          title="Deep Learning applied to portfolio optimization (playlist)"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />


        <h2>Model Creation</h2>
        <p>
          The allocation model described here is the global version compared to the one focused on the United States already described in the previous article. So the global version operates on a universe of ETFs covering the United States, Europe and Emerging Markets (sector and broad equity, government/credit, gold/commodities). The goal is the generation of risk-adjusted excess return relative to an equity benchmark (e.g. S&amp;P 500) while maintaining a logic of tactical rotation between assets/regions.
        </p>
        <p>
          Compared with the previous US-only version, the geographical extension introduces:
        </p>
        <ul>
          <li>
            greater cross-sectional optionality (more "levers" to extract alpha in different regimes);
          </li>
          <li>
            geographic and currency diversification (with reporting in EUR, via EURUSD conversion);
          </li>
          <li>
            new interactions between macro/volatility drivers (e.g. non-synchronous shocks between the US, Europe and EM).
          </li>
        </ul>

        <h2>Feature Engineering and Model Architecture</h2>
        <p>
          The pipeline integrates series of prices/volatility (ETFs, benchmark), technical indicators (RSI, MACD, etc), macro variables (growth, inflation, rates—with realistic lags) and seasonal signals. Quantities are harmonized on a single time index, normalized and transformed into multivariate sequences to feed the model.
        </p>
        <p>
          Architecturally, the model combines recurrent components (e.g. LSTM/GRU/RNN) and convolutional/attention components to capture non-linear patterns intra- and cross-asset. It is important to mention that the objective function is not limited to prediction error but includes terms to maximize Sharpe, reward the excess return vs. benchmark and penalize concentration (entropy), aligning training with investor-relevant metrics. This methodological setup follows guidelines introduced in the reference article on DL for asset allocation and the importance of robust out-of-sample training.
        </p>
        <p>
          The model is trained with a walk-forward methodology: time is advanced on historical windows, and in each window the data are divided into train/validation/test (e.g. 70/15/15, with early stopping and hyperparameter selection on the validation). Decisions are validated on the immediately subsequent test portion and then the window moves forward. In this way the model does not “see” the future and the out-of-sample result is genuine; it is a procedure closer to the real sequence and reflects good data science practices recalled in the previous article. The model’s prediction is made daily even though the change frequency on assets is another parameter subject to optimization since higher rebalancing frequency generates higher transaction costs and taxes.
        </p>

        <h2>Model Results</h2>
        <p>
          The cumulative return of the model is shared considering different time ranges. In particular, the last 3 years serve as validation and thus were not considered by the model to update the network weights, nor as testing. For a complete view of the backtest, Figure 1 shows the cumulative returns (portfolio, SPY and 60/40) and allocations over different time horizons. Figure 2 shows the same quantities but restricts the analysis to a recent period (1 year, 6 months and 1 month). Table 1 summarizes the key backtest metrics (CAGR, Sharpe and maximum drawdown) for the portfolio, the SPY benchmark and the filtered portfolio. Please keep the following note in mind before viewing the charts. The time axis is not evenly distributed: the first panel covers almost seventeen years, the second only five and the third two. For this reason, the trajectories appear more stretched in the last column than the others. It is important to keep these scale differences in mind when visually comparing the curves: an apparent change in shape does not necessarily imply a change in the model’s behaviour, but may be due to the different stretching of the x-axis.
        </p>

        <p>
        <figure>
          <a href={fig1} target="_blank" rel="noopener noreferrer">
            <img
              src={fig1}
              alt="Figure 1 — Cumulative returns and allocations: all, 5 and 2 years."
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>Figure 1 — Cumulative returns and allocations of the backtest considering the entire history, 5 and 2 years.</figcaption>
        </figure>
        </p>
        <p>
        <figure>
          <a href={fig2} target="_blank" rel="noopener noreferrer">
            <img
              src={fig2}
              alt="Figure 2 — Returns and allocations of the backtest: last 12, 6 and 1 month."
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>Figure 2 – Returns and allocations of the backtest considering the last 12, 6 and 1 month.</figcaption>
        </figure>
        </p>

        <p>
          <strong>Table 1</strong> – Backtest metrics for portfolio, benchmark and filtered portfolio
        </p>
        <table>
          <thead>
            <tr>
              <th>Instrument</th>
              <th>CAGR all</th>
              <th>Sharpe all</th>
              <th>Max Drawdown all</th>
              <th>CAGR 1Y</th>
              <th>Sharpe 1Y</th>
              <th>Max Drawdown 1Y</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Portfolio</td>
              <td>26.8%</td>
              <td>0.06</td>
              <td>54.8%</td>
              <td>46.0%</td>
              <td>0.13</td>
              <td>11.0%</td>
            </tr>
            <tr>
              <td>Benchmark SPY</td>
              <td>10.5%</td>
              <td>0.03</td>
              <td>60.6%</td>
              <td>22.5%</td>
              <td>0.07</td>
              <td>15.6%</td>
            </tr>
            <tr>
              <td>Filtered portfolio</td>
              <td>27.0%</td>
              <td>0.06</td>
              <td>54.8%</td>
              <td>45.8%</td>
              <td>0.13</td>
              <td>11.0%</td>
            </tr>
          </tbody>
        </table>

        <h2>2. Methodologies for Generating Future Data</h2>
        <p>
          To test the behaviour of the deep learning model, considering the high complexity and limited interpretability of the model (1 million parameters), we sought to verify the model’s allocation as the historical series input varied. For this purpose two different approaches were considered, detailed below.
        </p>

        <h3>2.1 Historical Pattern Replay</h3>
        <p>
          A first methodology simulates future series by replicating the return patterns observed in a predefined historical period.
        </p>
        <p>
          Returns are repeated cyclically until the desired horizon is covered and applied to current price levels to obtain a synthetic trajectory. Macro features are replicated in the same way. This technique preserves exactly the historical correlation and sequence of shocks of the original scenario.
        </p>
        <p>
          <strong>Assumptions and Limitations:</strong> – It is assumed that historical patterns can repeat themselves in the future identically (stationarity). Structural changes in markets are not considered. – Intra-scenario variability is absent: the same block of returns is always repeated, so no new combinations of shocks and recoveries are introduced. Also the starting point (price level) may differ greatly from the original one, generating different trajectories even though the returns are the same.
        </p>
        <p>
          The advantage is that this approach allows testing how the model reacts to a known context (e.g. 2008), maintaining the consistency among returns, volatility and macro features.
        </p>
        <p>
          The disadvantage is that it does not evaluate the model’s ability to face new scenarios or different combinations of events; it could overestimate the model’s effectiveness in unpredictable future contexts.
        </p>

        <h3>2.2 Correlated Synthetic Return Generation</h3>
        <p>
          The second methodology generates future data starting from a multivariate normal distribution that preserves the historical correlation between ETF and benchmark returns, but allows obtaining new estimates.
        </p>
        <p>
          Below is a detailed procedure for those interested in quantitative statistical methodologies. Those not interested can skip to the next results paragraph.
        </p>
        <p>
          The procedure is as follows:
        </p>
        <ol>
          <li>
            <strong>Estimate mean and covariance:</strong> from the historical returns of the ETFs and the benchmark, a vector of means and a covariance matrix are calculated. The VIX and macro features do not enter this covariance. For example, the VIX is simulated as a random walk with variance proportional to the scenario, then limited between 5 and 80.
          </li>
          <li>
            <strong>Scenario parameters:</strong> depending on the scenario (bull, lateral, high_vol, low_vol), drift and volatility factors are applied to the mean and covariance. For example, “bull” multiplies the mean by 1.5; “high_vol” doubles the variance.
          </li>
          <li>
            <strong>Sampling returns:</strong> using Cholesky (or eigen) decomposition, series of correlated Gaussian returns are sampled. The result provides returns for each ETF and the benchmark.
          </li>
          <li>
            <strong>Simulate the VIX:</strong> separately, the VIX is simulated as a random walk with variance proportional to the scenario, then limited between 5 and 80.
          </li>
          <li>
            <strong>Construct prices:</strong> starting from current prices, multiply by <em>e<sup>r</sup></em> to obtain future prices.
          </li>
        </ol>
        <p>
          <strong>Assumptions and Limitations:</strong> The normality of returns is one of the fundamental assumptions. Assuming multivariate returns are Gaussian ignores fat tails and jumps typical of financial markets. In addition the scenario factors for drift and volatility are arbitrary and set by hand; small changes can drastically alter results. Finally, macro variables are independent, remaining historical while the VIX is simulated separately. The model, therefore, can receive macro/VIX signals that are not reflected in simulated prices, generating decisions that are hard to interpret.
        </p>
        <p>
          <strong>Advantages:</strong> allows testing the model’s sensitivity to different levels of drift and volatility, introducing variability and scenarios not present in the historical.
        </p>
        <p>
          <strong>Disadvantages:</strong> can produce unrealistic trajectories (e.g. “financial_crisis” with positive returns) and allocations still very concentrated, because the filter does not allow real rebalancing.
        </p>

        <h2>3. Use and Comparison of the Two Methodologies</h2>
        <p>
          Based on the analyzed results, the two methodologies serve different purposes:
        </p>
        <p>
          <strong>Historical pattern replay:</strong> ideal for assessing the model’s consistency in real contexts already observed. It allows verifying if the algorithm manages to protect the portfolio in a crisis (e.g. 2008) or to follow an upward trend. It is particularly useful to explain to the reader how the model reacts to a known event, with charts showing cumulative returns, delta vs benchmark and allocations.
        </p>
        <p>
          <strong>Correlated synthetic returns:</strong> useful as a stress test to explore the algorithm’s sensitivity to different drift and volatility combinations. However, it does not provide reliable information on the model’s ability to beat the benchmark, because performance depends strongly on chosen parameters. It is recommended to show only the scenarios that add value (e.g. “high_vol” to assess the impact of high volatility) and to clearly explain that this is an exploratory exercise.
        </p>
        <p>
          Below three scenarios (Bull, Covid crash, Financial crisis) are analyzed, limiting to the last column on the right, corresponding to the final month of the simulation and associated metrics. In each case, the portfolio allocation is almost identical in both methods. What changes is the trend of the individual ETFs, and therefore the cumulative return.
        </p>

        <h3>Bull Scenario (2012–2014)</h3>
        <p>
          According to the historical replay methodology, in the last month the strategy remains almost 100% concentrated on a single ETF related to Healthcare, with a previous transition period in which the portfolio was allocated in technology, as seen from the area chart. This seems rather strange, considering we are now used to allocating the portfolio to the technology sector during a bull market. In reality the model moves correctly considering the movement of the underlying ETFs, shifting to the instrument which during the 2012-2014 period would then have a significant bull run (the healthcare ETF, XLV).
        </p>
        <p>
          <figure>
            <a href={fig3} target="_blank" rel="noopener noreferrer">
              <img
                src={fig3}
                alt="Figure 3 — Bull scenario (2012–2014): cumulative returns vs SPY/60-40 and time-varying allocations."
                loading="lazy"
                style={{ maxWidth: "100%", height: "auto" }}
              />
            </a>
            <figcaption>
              Figure 3 — Bull scenario (2012–2014) with historical pattern replay: comparison of the cumulative returns of the portfolio relative to SPY and 60/40 and the corresponding area-chart of allocations. Highlighted is the rotation into Healthcare (XLV).
            </figcaption>
          </figure>
        </p>
        <figure>
          <a href={fig4} target="_blank" rel="noopener noreferrer">
            <img
              src={fig4}
              alt="Figure 4 — Bull scenario: 1M metrics (CAGR, Sharpe, Max Drawdown) portfolio vs SPY."
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 4 — Underlying ETFs returns compared according to the historical simulation vs the current one.
          </figcaption>
        </figure>
        <p>
          The metrics of reference are reported in the following table. The portfolio realizes a 1M CAGR of about 27% with a Sharpe of about 0.11, beating SPY (24% with Sharpe 0.12), but at the cost of a deeper drawdown (−16.6% vs −9.7%). The model outperforms SPY by about 3 points in the month but at the cost of a deeper drawdown.
        </p>

        <table>
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Methodology</th>
              <th>Portfolio CAGR</th>
              <th>SPY CAGR</th>
              <th>Portfolio Sharpe</th>
              <th>SPY Sharpe</th>
              <th>Portfolio MDD</th>
              <th>SPY MDD</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Bull</td>
              <td>pattern replay</td>
              <td>26.9%</td>
              <td>24.1%</td>
              <td>0.11</td>
              <td>0.12</td>
              <td>-16.1%</td>
              <td>-9.7%</td>
            </tr>
          </tbody>
        </table>

        <p>
          In the multivariate simulation methodology the allocation remains similar albeit with some delay, but the synthetic trajectory of ETFs generates a shorter duration (1 year instead of the two years generated previously) and thus explains why the cumulative returns are flatter and less correlated to historical values. It is noted that gold (GLD) in the second simulation approach has larger returns than others, with a healthcare sector index growing instead only later over time.
        </p>
        <figure>
          <a href={fig5} target="_blank" rel="noopener noreferrer">
            <img
              src={fig5}
              alt="Figure 5 — Synthetic Cholesky simulation: cumulative returns and allocations"
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 5 — Synthetic Cholesky simulation: cumulative returns and allocations.
          </figcaption>
        </figure>

        {/* === Figure 6 === */}
        <figure>
          <a href={fig6} target="_blank" rel="noopener noreferrer">
            <img
              src={fig6}
              alt="Figure 6 — Synthetic Cholesky simulation: Cumulative returns of underlying ETFs"
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 6 — Synthetic Cholesky simulation: cumulative returns of underlying ETFs.
          </figcaption>
        </figure>
        <h3>Covid Crash Scenario (February–April 2020)</h3>
        <p>
          We decided to take as an additional point of analysis an unusual transitional moment, therefore the temporary bear market of Covid, simulating it again according to the two simulation approaches previously described.
        </p>
        <p>
          According to the historical replay methodology, the portfolio almost totally eliminates the equity exposure and positions itself in gold. Below we propose again the trends of ETFs that trace what happened in this historical period, with a difference that however has an impact: this new Covid crash starts today, with inertia of historical series, momentum, volatility and drift different from those just before February 2020. For this scenario the model does not react exactly as it did in training, but very similarly, because the sequence and historical series are still different.
        </p>
        <figure>
          <a href={fig7} target="_blank" rel="noopener noreferrer">
            <img
              src={fig7}
              alt="Figure 7 — Covid Crisis Scenario: cumulative returns and allocations with past returns simulation."
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 7 — Covid Crisis Scenario: cumulative returns and allocations with past returns simulation.
          </figcaption>
        </figure>

        {/* === Figure 8 === */}
        <figure>
          <a href={fig8} target="_blank" rel="noopener noreferrer">
            <img
              src={fig8}
              alt="Figure 8 — Covid Crisis Scenario: ETF performance with past returns simulation."
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 8 — Covid Crisis Scenario: ETF performance with past returns simulation.
          </figcaption>
        </figure>
        <p>
          The result is surprising: CAGR 1M ≈ 63%, Sharpe 0.13 and drawdown around 12%, while SPY collapses by 5% with drawdown over 33%. This reflects rotation and maintenance towards safe haven assets, taking advantage of the continuous momentum of gold, albeit after reaching the peak other ETFs could price themselves higher.
        </p>

        <table>
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Methodology</th>
              <th>Portfolio CAGR</th>
              <th>SPY CAGR</th>
              <th>Portfolio Sharpe</th>
              <th>SPY Sharpe</th>
              <th>Portfolio MDD</th>
              <th>SPY MDD</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Covid Crash</td>
              <td>pattern replay</td>
              <td>63.0%</td>
              <td>—4.9</td>
              <td>0.132</td>
              <td>0.01</td>
              <td>-12.5%</td>
              <td>—33.7</td>
            </tr>
          </tbody>
        </table>

        <p>
          The multivariate simulation also presents the same defensive asset allocation, but the synthetic series does not reproduce the crash and rebound, ETFs follow an almost Gaussian process, so even with defensive weights a moderately positive return is obtained. Again there is a difference in duration between the two simulations: while the historical simulation has a duration exactly equal to the Covid period, the multivariate simulation has a preset duration, i.e. one year. Thus this simulation has, in some way, maintained the stable trend of gold and the recovery of other ETFs, and the model in such a trend, although unprecedented, has stayed in gold. It is reiterated again that the output serves more to test the model’s sensitivity than to assess its ability in real contexts.
        </p>
        {/* === Figure 9 === */}
        <figure>
          <a href={fig9} target="_blank" rel="noopener noreferrer">
            <img
              src={fig9}
              alt="Figure 9 — Covid Crisis Scenario: returns and allocations from multivariate (Gaussian) simulation"
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 9 — Covid Crisis Scenario: returns and allocations from multivariate (Gaussian) simulation.
          </figcaption>
        </figure>

        {/* === Figure 10 === */}
        <figure>
          <a href={fig10} target="_blank" rel="noopener noreferrer">
            <img
              src={fig10}
              alt="Figure 10 — Covid Crisis Scenario: ETFs performance from multivariate (Gaussian) simulation"
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 10 — Covid Crisis Scenario: ETFs performance from multivariate (Gaussian) simulation.
          </figcaption>
        </figure>
        <p>
          <strong>Table 2</strong> – 1-year return and risk metrics for the replicated scenarios
        </p>
        <table>
          <thead>
            <tr>
              <th>Scenario </th>
              <th>Portfolio CAGR </th>
              <th>SPY CAGR </th>
              <th>Portfolio Sharpe </th>
              <th>SPY Sharpe </th>
              <th>Portfolio MDD </th>
              <th>SPY MDD </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Bull replay</td>
              <td>26.9%</td>
              <td>24.1%</td>
              <td>0.10</td>
              <td>0.11</td>
              <td>-16.1%</td>
              <td>-9.7%</td>
            </tr>
            <tr>
              <td>Bull synt</td>
              <td>22.6%</td>
              <td>-5.4%</td>
              <td>0.082</td>
              <td>-0.013</td>
              <td>-12.7%</td>
              <td>-26.3%</td>
            </tr>
            <tr>
              <td>Covid replay</td>
              <td>63.0%</td>
              <td>-4.9%</td>
              <td>0.132</td>
              <td>0.01</td>
              <td>-12.5%</td>
              <td>-33.7%</td>
            </tr>
            <tr>
              <td>Covid synt</td>
              <td>30.0%</td>
              <td>30.6%</td>
              <td>0.097</td>
              <td>0.101</td>
              <td>-17.0%</td>
              <td>-9.6%</td>
            </tr>
            <tr>
              <td>Financial replay</td>
              <td>3.9%</td>
              <td>1.7%</td>
              <td>0.018</td>
              <td>0.011</td>
              <td>-21.0%</td>
              <td>-17.6%</td>
            </tr>
            <tr>
              <td>Financial synth</td>
              <td>-12.7%</td>
              <td>−24.4%</td>
              <td>0.04</td>
              <td>−0.03</td>
              <td>-61.2%</td>
              <td>-52.3%</td>
            </tr>
          </tbody>
        </table>

        <h2>3. Conclusions</h2>
        <p>
          From the analysis it emerges that among the two simulation test methodologies, historical replay is the most reliable way to test the strategy, because
          it preserves coherence among returns, volatility and macro signals. These methodologies are useful in any case to test or highlight model overfitting problems, aspects that need to be evaluated before the model is put into production. For this aspect the behavior of the deep neural network proved positive.
        </p>
        <p>
          Based on these simulations, the overall judgment on the model can be considered positive. The model maintains the ability to protect capital in crises (both real and replicated), generating drawdowns lower than SPY; it generates extra return in sideways markets thanks to sector rotation and integration of macro and technical signals that help recognize market regimes. In summary, the global model confirms the ability to provide protection in crises and generate alpha in sideways contexts, but is still improvable to reduce concentration and better exploit sustained rallies.
        </p>

        <p>
          <strong>Want to read other similar articles?</strong>{" "}
          <a href="/kb">Return to the Knowledge Base</a>
        </p>
      </div>
    ),
  },

  

  // Article 10: Investor Psychology: Cognitive Biases and How to Defend Yourself
  {
    slug: "investor-psychology-cognitive-biases-and-how-to-defend-yourself",
    title: "Investor Psychology: Cognitive Biases and How to Defend Yourself",
    metaDescription:
      "The most common biases (confirmation, anchoring, recency), how they influence investment decisions and how to mitigate them with disciplined plans and diversification.",
    tags: ["psychology", "education"],
    readTime: "6 min",
    author: "Editorial Team",
    datePublished: "2025-05-08",
    coverImage: PsyCover,
    canonical: `${DOMAIN}/en/kb/investor-psychology-cognitive-biases-and-how-to-defend-yourself`,
    content: (
      <div>
        <h1>Investor Psychology: Cognitive Biases and How to Defend Yourself</h1>
        <p>
          Investment decisions are not always rational: behavioural finance shows that we are subject to numerous{" "}
          <strong>cognitive biases</strong> that can harm our results. Understanding them is the first step to avoiding them.
        </p>

        <h2>1. Most Common Biases</h2>
        <ul>
          <li>
            <strong>Confirmation bias</strong>: we look for information that confirms our opinions and ignore data to the contrary.
          </li>
          <li>
            <strong>Anchoring</strong>: we fixate on a purchase price or a previous high/low and struggle to sell at a loss or buy on new highs.
          </li>
          <li>
            <strong>Recency bias</strong>: we attach too much importance to recent events, neglecting the long-term history.
          </li>
          <li>
            <strong>Illusion of control</strong>: we believe we have more control over the market than we actually do, leading to overtrading. Academic studies show that excessive turnover reduces net returns for retail investors.
          </li>
        </ul>

        <h2>2. How to Defend Yourself</h2>
        <ol>
          <li>
            Establish <strong>ex-ante rules</strong>: target asset allocation, rebalancing thresholds, rational stop-losses.
          </li>
          <li>
            Adopt a <strong>PAC</strong> to invest gradually and reduce the emotional impact.
          </li>
          <li>
            Keep an <strong>investment diary</strong> to reflect on decisions and associated feelings.
          </li>
          <li>
            Rely on an <strong>independent adviser</strong> to anchor choices to data and documented strategies.
          </li>
        </ol>

        <p>
          Remember: recognizing biases is the basis for avoiding them. For a complete behavioral education program,{" "}
          <a href="/contact">write to me</a>
        </p>
      </div>
    ),
  },

  // Article 11: Currency Hedging: Hedged vs Unhedged ETFs and How to Analyze Them
  {
    slug: "currency-hedging-hedged-vs-unhedged-etfs-and-how-to-analyze-them",
    title: "Currency Hedging: Hedged vs Unhedged ETFs and How to Analyze Them",
    metaDescription:
      "Hedged or unhedged? What currency hedging is in ETFs, pros and cons, and a quantitative analysis with Python (Colab) on S&P 500, MSCI World and 20+ year Treasuries.",
    tags: ["etf", "currencies", "education", "quant"],
    readTime: "10 min",
    author: "Editorial Team",
    datePublished: "2025-08-22",
    dateUpdated: "2025-08-22",
    coverImage: HedgedCover,
    canonical: `${DOMAIN}/en/kb/currency-hedging-hedged-vs-unhedged-etfs-and-how-to-analyze-them`,
    content: (
      <div>
        <h1>Currency Hedging: Hedged vs Unhedged ETFs and How to Analyze Them</h1>

        <h2>DISCLAIMER</h2>
        <p>
          The analyses and opinions presented here are purely for information. They do not constitute investment recommendations. Before adopting any strategy it is essential to consult a financial adviser.
        </p>

        <h2>Introduction and Objectives</h2>
        <p>
          Investing in international markets allows diversifying the portfolio, but exposes the investor to exchange rate risk. For example, a European investor who purchases an ETF on the S&P 500 index quoted in dollars will get a return that depends both on the index’s performance and on the euro/dollar exchange rate. As Vanguard points out, even if a US equity index rises by 10%, the return in pounds (or euros) can be different because the amount to be converted at redemption is influenced by the exchange rate. To manage this variable, there are “hedged” ETFs (with currency hedging) that try to neutralize the effects of currency movements. “Unhedged” ETFs, on the other hand, leave the currency exposure open.
        </p>
        <p>
          The aim of this article is to explain how currency hedging works, what are the advantages and disadvantages of hedged ETFs compared with unhedged ones, and to illustrate, through an updated analysis in Python (available on Google Colab), the differences in returns between the two types in recent years. We will do this while maintaining an informative but rigorous tone, with references to institutional studies and showing a graphic example.
        </p>
        <p>
          <strong>Video deep-dive:</strong> A video has been published that explains the concept practically. You can watch it here:&nbsp;
          <a
            href="https://www.youtube.com/watch?v=x3S1ZbtwnNk"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.youtube.com/watch?v=x3S1ZbtwnNk
          </a>
          .
        </p>

        <h2>Why Do Exchange Rates Move?</h2>
        <p>
          Exchange rate fluctuations depend on numerous macroeconomic factors: interest rate differentials, inflation, political stability, economic performance and global shocks. In general, a currency tends to appreciate if the country offers higher interest rates or greater expected growth, because investors move capital there seeking higher returns; it depreciates, instead, if the economy slows or inflation is higher. These oscillations do not necessarily cancel out over the long term: periods of persistent strength or weakness can last many years, making it difficult to predict the endpoint.
        </p>

        <h2>What is a Hedged ETF and Why Use It</h2>
        <p>
          A hedged ETF and the unhedged version invest in the same basket of securities; the difference is that the hedged ETF uses forward contracts (or other currency derivatives) to lock in the future exchange rate and neutralize the effect of fluctuations. This allows the investor to replicate the performance “in local currency” of the index, without the return depending on the strengthening or weakening of the foreign currency. However, hedging also removes the possibility of benefitting from favorable currency movements.
        </p>
        <p>
          Vanguard explains that it offers currency-hedged share classes mainly for bond funds, since bonds serve to reduce portfolio volatility and currency risk can negate that benefit. On the other hand, the company does not offer hedged share classes for equity funds intended for UK retail investors, because equities are already riskier and the currency’s contribution can be neutral in the long term. Morningstar notes that currency hedging reduces volatility, but over the long term the effect on total return is moderate: from January 2001 to September 2022 the MSCI ACWI ex USA hedged index slightly outperformed (23,968 USD vs. 21,936 USD on 10,000 USD invested) the unhedged index, and only from June 2022 onwards. Moreover, hedged ETFs have higher costs (0.30–0.40% vs {'<0.10%'} for unhedged), can generate capital gain distributions due to the monthly rollover of forward contracts and do not completely eliminate currency risk.
        </p>

        <h2>Pros and Cons Summarized</h2>
        <ul>
          <li>
            <strong>Exchange risk</strong>: reduced thanks to forward contracts (hedged) vs full currency exposure (unhedged), with the possibility of additional gain or loss.
          </li>
          <li>
            <strong>Volatility</strong>: lower in hedged ETFs (they do not suffer currency volatility) vs higher in unhedged, especially in periods of strong currency variation.
          </li>
          <li>
            <strong>Costs</strong>: hedged ETFs have higher fees and hedging costs vs generally lower costs in unhedged.
          </li>
          <li>
            <strong>Long-term return</strong>: on average similar, with differences related to interest rate differentials and costs (hedged sometimes slightly lower or higher depending on periods).
          </li>
          <li>
            <strong>Typical use</strong>: hedged more common on bond funds and for those who want to reduce total risk; unhedged typical on equities for investors willing to accept currency swings.
          </li>
        </ul>

        <h2>Quantitative Analysis Methodology (Python Code)</h2>
        <p>
          To empirically assess the impact of currency hedging, a Python notebook (available on GitHub) was created that downloads historical prices for some ETF pairs and the EUR/USD exchange rate via the yfinance library. The analysis considers three cases:
        </p>
        <ul>
          <li>
            <strong>European S&amp;P 500</strong> – unhedged version (IUSA.AS) and hedged (IUSE.AS).
          </li>
          <li>
            <strong>MSCI World</strong> – unhedged version (IWDA.AS) and hedged (IWDE.AS).
          </li>
          <li>
            <strong>US Treasury 20+ Years</strong> – ETF of long-duration bonds unhedged vs the hedged version (DTLE.L).
          </li>
        </ul>
        <p>The code:</p>
        <ul>
          <li>
            Downloads adjusted close prices for the ETFs from 2019 to today (updated end of August 2025).
          </li>
          <li>
            Calculates a third reference series (“Perfect Hedge”) by multiplying the price of the unhedged ETF by the EUR/USD rate, simulating a theoretical hedge without costs.
          </li>
          <li>
            Calculates cumulative returns and compares them.
          </li>
        </ul>
        <p>
          The notebook produces comparative charts and calculates the annualized spread between the real hedged ETF and the perfectly hedged ETF, highlighting the inefficiency of the real hedge (hedging costs, interest rate differences). The results we report below come from the notebook update in September 2025.
        </p>

        <h2>Results: Hedged vs Unhedged in Recent Years</h2>

        <h3>S&amp;P 500 ETF</h3>
        <p>
          For the S&amp;P 500 index the hedged version offered an even more obvious advantage. Between October 2022 and September 2025 the hedged ETF IUSE.AS went from 1 to about 1.67, versus about 1.46 for the unhedged version IUSA.AS; the perfect hedge would have reached about 1.75. This differential (about 20% in favor of the hedged) is due to the strong depreciation of the dollar versus the euro since Q4 2022: the European investor who hedged the currency avoided the loss tied to the dollar drop. In previous years, when the dollar was strengthening, unhedged ETFs often outperformed; the recent reversal underscores how hedging convenience depends on currency dynamics and should be evaluated case by case.
        </p>

        <figure>
          <a href={fxFig1} target="_blank" rel="noopener noreferrer">
            <img
              src={fxFig1}
              alt="Figure 1 — S&P 500: cumulative returns comparison between unhedged, hedged and perfect hedge."
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 1 — S&amp;P 500: comparison of cumulative returns between unhedged ETF, hedged ETF and theoretical “perfect” hedge.
          </figcaption>
        </figure>

        <h3>MSCI World ETF</h3>
        <p>
          The updated analysis from October 5, 2022 to September 8, 2025 shows that the hedged version IWDE.AS outperformed the unhedged version IWDA.AS. If an initial investment of 1 is normalized to October 5, 2022, at the end of the period the hedged ETF would be around 1.62, while the unhedged version would be around 1.49; the perfect hedge (IWDA.PerfectHedged) would reach about 1.78. The performance difference between hedged and unhedged (≈ 13%) reflects the euro’s strengthening versus the dollar in the period; however, the currency hedge does not fully replicate the ideal hedge, because it incorporates hedging costs and does not cover 100% of the quota denominated in currencies other than the dollar.
        </p>

        <figure>
          <a href={fxFig2} target="_blank" rel="noopener noreferrer">
            <img
              src={fxFig2}
              alt="Figure 2 — MSCI World: cumulative returns comparison between unhedged, hedged and perfect hedge."
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 2 — MSCI World: cumulative returns comparison between the unhedged version, the hedged version and theoretical “perfect” hedge.
          </figcaption>
        </figure>

        <h3>US Treasury 20+ Years Bond ETF</h3>
        <p>
          On the bond front, the hedged ETF on long-duration US Treasuries recorded an average gap of about 1.6% per year relative to the perfect hedge. During the euro appreciation phase (end of 2022) the hedged advantage was temporary, but over longer horizons the unhedged ETF tends to catch up and surpass the hedged counterpart, also thanks to reinvested coupons and the interest rate differential.
        </p>
        <p>
          The following image shows how the cumulative returns of the three series (unhedged, hedged and “perfect hedge”) progressively diverge over time.
        </p>

        <figure>
          <a href={fxFig3} target="_blank" rel="noopener noreferrer">
            <img
              src={fxFig3}
              alt="Figure 3 — Treasury 20+ years: cumulative returns comparison between unhedged, hedged and perfect hedge."
              loading="lazy"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </a>
          <figcaption>
            Figure 3 — US Treasuries 20+ years: comparison of cumulative return trajectories between unhedged ETF, hedged ETF and theoretical hedge.
          </figcaption>
        </figure>

        <h2>Predicting the Exchange Rate: Prophet Experiment</h2>
        <p>
          To assess whether hedging is convenient in coming years, the notebook uses the Prophet library to forecast EUR/USD. Trained on historical data, the model predicts a downward trend in the exchange rate, with a confidence interval (80%) between 1.00 and 1.15 in the next year. The mean absolute error of 6% and the standard deviation of the error of 8 cents indicate considerable uncertainty. Assuming an exchange rate at 1.15, the investor choosing the hedged ETF would obtain a potential advantage of only 2–3 percentage points over the unhedged version; if instead the euro does not strengthen, hedging would entail an unnecessary cost. The prediction therefore provides no certainties, but suggests that currency swings can turn out to be either an ally or a foe.
        </p>

        <h2>Potential and Limits of the Python Model</h2>
        <p>
          The presented analysis is completely reproducible: the notebook uses only open-source libraries (yfinance for downloading data, pandas and numpy for calculations, matplotlib for charts). It is possible to modify the observation period, change the ETFs analyzed or include further indices (e.g. emerging markets) to explore other currency exposures. The method of calculating the “perfect hedge” by multiplying the price of the ETF by the exchange rate can be applied to any instrument, provided data are available.
        </p>
        <p>
          Compared with available market analyses (often based on long-term averages or synthetic indices), this approach allows assessing precisely the effect of currency on specific ETFs, highlighting the real costs of hedging and the trend of the differential across different periods. It is not a complex machine learning algorithm: there is no predictive model of allocations, but a simple statistical calculation. Nevertheless, it is an example of how an investor can use quantitative tools to make more informed decisions.
        </p>

        <h2>Comparison with Other Methodologies</h2>
        <p>
          There are more sophisticated algorithms (for example neural networks or LSTM models) that try to predict exchange rate movements or to optimize a portfolio considering multiple variables simultaneously. These models can capture non-linear patterns and complex interactions, but they have two limitations: they require large amounts of historical data and their forecasts can easily be invalidated by unforeseen economic shocks. In this context, the simplicity of the model used (perfect hedge and direct comparison) can be an advantage: it is transparent, easily verifiable and does not hide implicit assumptions. As Morningstar points out, the effect of hedging oscillates in cycles and over the long term the performance difference narrows, while costs and taxes can erode benefits. Therefore, a sophisticated approach might add marginal benefits relative to a simple periodic assessment.
        </p>

        <h2>Final Considerations</h2>
        <ul>
          <li><strong>Currency hedging reduces volatility:</strong> those who do not want to see their investments influenced by currency movements can opt for hedged ETFs. This can be particularly useful in the short term or for bond investments.</li>
          <li><strong>In the long term, extra return is limited:</strong> historical data and quantitative analyses show that hedged and unhedged tend to have comparable returns; hedged often is slightly disadvantaged due to costs. The potential advantage depends also on interest rate differentials.</li>
          <li><strong>Costs matter:</strong> hedged ETFs have higher fees, potential capital gain distributions and transaction costs related to rolling forward. Over time, these items can erode returns.</li>
          <li><strong>The choice depends on expectations and horizon:</strong> if a strong appreciation of your currency is expected in the short term, hedging can offer protection; if instead it is believed that the foreign currency will remain stable or weaken, leaving the currency exposure can increase returns.</li>
          <li><strong>Currency diversification:</strong> another way to manage currency risk is to invest in ETFs denominated in multiple currencies or in different currencies (e.g. dollar, yen, Swiss franc), creating a multicurrency portfolio. This topic will be explored in future articles.</li>
        </ul>
        <p>
          Ultimately, the decision to choose a hedged or unhedged ETF should be based on your risk profile, beliefs about exchange rate direction and time horizon. However, hedging should not be ruled out due to higher costs alone, as multiple factors need consideration. The quantitative tools presented make it possible to concretely evaluate the effects of hedging, but they do not replace professional judgment. For this reason it is advisable to consult an expert before implementing any strategy. For further explanation please watch the deep-dive video on the YouTube channel.
        </p>

        <h2>Useful Resources and Code</h2>
        <ul>
          <li>
            <strong>Colab Notebook (updated):</strong> HedgedvsUnhedged.ipynb
            {/* Example: replace with your Colab/GitHub link */}
          </li>
          <li>
            <strong>Deep dive video:</strong>{" "}
            <a
              href="https://www.youtube.com/watch?v=x3S1ZbtwnNk"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.youtube.com/watch?v=x3S1ZbtwnNk
            </a>
          </li>
          <li>
            <strong>Morningstar analysis</strong> on the effectiveness of currency-hedged ETFs.
          </li>
          <li>
            <strong>Vanguard insight</strong> on hedged share classes and the advantages/limitations of hedging.
          </li>
          <li>
            <strong>Harvest ETF article</strong> on the characteristics of currency-hedged ETFs.
          </li>
        </ul>
      </div>
    ),
  },

// Requires IM, BM, tex from your math component, e.g.:
// import { InlineMath as IM, BlockMath as BM, tex } from "@/components/Math";

{
  slug: "human-capital-portfolio-optimization",
  title: "Valuing Human Capital and Integrating It into Portfolio Optimization",
  metaDescription:
    "What human capital is, how to value it and how to integrate it into asset allocation: theory, formulas and two practical examples (family with stable income and entrepreneur with volatile income).",
  tags: ["education", "asset allocation", "human capital"],
  readTime: "14 min",
  author: "Editorial Team",
  datePublished: "2025-08-22",
  dateUpdated: "2025-08-22",
  coverImage: HumanCapitalCover,
  canonical: `${DOMAIN}/en/kb/human-capital-portfolio-optimization`,
  content: (
    <div>
      <h1>Valuing Human Capital and Integrating It into Portfolio Optimization</h1>

      <h2>What is human capital and why does it matter?</h2>
      <p>
        In personal finance, <em>human capital</em> is the present value of all future labour income that an individual will earn over a working lifetime&nbsp;[1]. For a young investor, human capital is often the most valuable asset—much larger than accumulated financial wealth—and it is an intangible yet crucial component of a person’s total wealth&nbsp;[2]. According to ISTAT, the average human-capital value of an Italian citizen has been estimated at about €342,000&nbsp;[3], highlighting its material weight. Ignoring human capital in financial planning means overlooking a substantial part of one’s wealth and overall risk profile.
      </p>
      <p>
        From a financial point of view, we can treat human capital as an additional asset within an individual’s total portfolio&nbsp;[4]. Although it is non-tradable (you cannot sell or directly diversify it), it does have a risk/return profile that should be accounted for. Age, job stability and the industry in which one works affect both the value and the risk of human capital. Early in one’s career many years of income lie ahead (high human capital) but savings are limited; as retirement approaches, human capital shrinks while financial wealth grows thanks to years of saving and investing&nbsp;[2]. This implies that younger investors can take more financial risk because they are supported by ample, “insurable” human capital over time, whereas older investors should be more conservative since the labour-dependent share of total wealth is small. As shown by Bodie, Merton and Samuelson (1992), flexibility in work (e.g., the ability to extend a career or do overtime) offers a kind of insurance against financial risk, encouraging younger workers to invest a larger share of their financial wealth in equities than older investors&nbsp;[5].
      </p>

      <p>
        <strong>Figure 1:</strong> Simplified trajectory of human and financial capital over the life cycle. In youth, human capital (yellow line) dominates total wealth, while accumulated financial wealth (orange line) is initially small and grows over time with saving. The red dashed line indicates total wealth (human + financial) in this hypothetical example.
      </p>
      <p>
        As Figure 1 suggests, for a young individual most wealth consists of human capital, which is gradually converted into financial wealth as one saves and invests over a working life. Total wealth tends to rise over time if part of labour income is saved (in our example we assume an increase that keeps the sum H + W roughly constant). In general, the ratio between human and financial capital (H/W) varies along the life cycle: it is very high at the start (new workers with little savings) and approaches zero near retirement&nbsp;[6]. It follows that a young investor with high “wealth in human capital” and little financial wealth should invest relatively more in risky instruments (equities) than a mature investor close to retirement with little remaining human capital&nbsp;[6]. This is the principle behind classic life-cycle funds and age-based advice (high equity weights when young, progressively more bonds with age).
      </p>
      <p>
        Another reason human capital matters: it acts as a risk absorber. If labour income is stable, an investor can take more investment risk, knowing that a reliable cash flow is available to cover expenses (and perhaps to buy additional financial assets at discounted prices in market downturns). Conversely, if labour income is volatile or uncertain, the capacity to withstand financial losses declines. In essence, the risk profile of human capital should guide the composition of the investor’s financial portfolio&nbsp;[7]. Elements such as job security, income volatility and industry cyclicality should be considered carefully within the overall asset allocation&nbsp;[7]. As a general rule, human capital should be “hedged” or compensated by financial capital: if one’s job is risky, the financial portfolio should be more conservative; if human capital is very safe, the financial portfolio can be more aggressive&nbsp;[8][9].
      </p>

      <h2>How to value human capital</h2>
      <p>
        Valuing human capital means calculating the present value of an individual’s expected future earnings. Practically speaking, it is a discounting exercise analogous to valuing a bond or an annuity. Formally:
      </p>
      <p>
        <BM>{tex`H = \sum_{t=1}^{T} \frac{E(\text{Income}_t)}{(1 + r_d)^t}`}</BM>
      </p>
      <p>
        where <IM>{tex`T`}</IM> is the number of remaining working years and <IM>{tex`r_d`}</IM> is an appropriate discount rate. The choice of <IM>{tex`r_d`}</IM> is critical: for very safe earnings (e.g., a tenured public employee) one can use a rate close to government bond yields or the risk-free rate. For more uncertain earnings, add a risk premium, as with the cost of equity for a company. For example, a young employee with a stable salary of €30,000 per year and 27 years to retirement could have human capital around €810,000 simply by multiplying annual income by remaining years&nbsp;[10] (ignoring growth and discounting for simplicity). More rigorously, with the same €30,000 income and a 2% real discount rate, the present value of the next 27 years of salary is roughly €600,000–€700,000 depending on income-growth assumptions (here we assume no real growth, i.e., discounting only). A similar order of magnitude (several hundred thousand euros) applies to many workers, explaining why human capital is often the dominant share of total wealth in the early working years.
      </p>
      <p>
        Note that the value of human capital declines over time: each passing year “uses up” part of one’s future earning capacity (and retirement draws nearer). Adverse events can reduce H abruptly (job loss, disability, death—risks against which insurance is advisable). Conversely, investing in education and experience can raise expected future earnings, thereby increasing human capital. Interest-rate changes also matter: a higher discount rate (e.g., higher market rates or greater perceived job risk) reduces H, whereas lower rates increase it.
      </p>

      <h2>Human capital and asset allocation: theory and models</h2>
      <p>
        Incorporating human capital into asset-allocation models means viewing the investor not only in terms of financial wealth but also in terms of the “implicit position” arising from work. The academic literature on portfolio choice with background risk tackles this problem. A fundamental result (Bodie, Merton and Samuelson, 1992) is that an investor with certain or weakly correlated labour income should tilt the financial portfolio toward risky assets more than an investor without labour income&nbsp;[5][11]. In other words, risk-free human capital acts like an “implicit bond” in the investor’s balance sheet.
      </p>
      <p>
        In a one-period mean–variance setting with one risky asset (equities) and one risk-free asset (bonds), the <strong>optimal equity weight on financial wealth</strong> \(W\) when human capital \(H\) is present is given by the Bodie–Merton–Samuelson formula:
      </p>
      <p>
        <BM>{tex`
          w_{\text{equity}}^{(W)} \;=\;
          \frac{W+H}{W}\cdot\frac{E(R_m)-R_f}{A\,\sigma_m^{2}}
          \;-\;
          \frac{\operatorname{Cov}(R_L,\,R_m)}{A\,W\,\sigma_m^{2}}
        `}</BM>
      </p>
      <p>
        where <IM>{tex`A`}</IM> is risk aversion, <IM>{tex`\sigma_m^{2}`}</IM> the market variance, and <IM>{tex`R_L`}</IM> the <strong>return on human capital</strong> (i.e., percentage changes/shocks to labour income), not the income level in euros. Key implications:
        <br />– If <IM>{tex`R_L`}</IM> is uncorrelated with the market (<IM>{tex`\operatorname{Cov}\approx 0`}</IM>), the <em>leverage</em> factor <IM>{tex`(W+H)/W`}</IM> amplifies the equity share on financial assets because H behaves like a bond.
        <br />– If <IM>{tex`R_L`}</IM> is positively correlated (pro-cyclical income), the subtractive term lowers <IM>{tex`w_{\text{equity}}^{(W)}`}</IM>; if negatively correlated (counter-cyclical income), the term raises it.
        <br />– In one-period mean–variance, the <em>variance</em> of non-tradable income does not enter the optimal weights directly; <strong>only the covariance with the market matters</strong>. Multi-period CRRA life-cycle models add <em>hedging demand</em>, but that is a different (richer) framework.
      </p>
      <p>
        On the other hand, if human capital is risky and correlated with financial markets, it looks more like an “implicit equity” in the individual’s wealth. For example, an entrepreneur whose income is tied to the economic cycle, or a worker with stock-linked compensation, has equity-like human capital. In these cases, theory suggests reducing equity exposure in the financial portfolio to avoid being exposed twice to the same risk&nbsp;[12][13]. The Enron case (salary and pension concentrated in company stock) is a cautionary tale: when the company failed, both jobs and savings were wiped out. Applying diversification to human capital means lowering exposure to financial assets whose returns are positively correlated with <em>labour-income returns</em> <IM>{tex`R_L`}</IM>.
      </p>
      <p>
        A useful way to think about human capital is to ask: “Does my job make me more like a bond or like a stock?”&nbsp;[22] Teachers, civil servants or tenured professors have bond-like human capital; commission-based agents or start-up tech workers have stock-like human capital. In the first case, tilt the financial portfolio toward equities (since the “bond” comes from work); in the second, tilt toward bonds to counterbalance job risk&nbsp;[8][9]. The worst situation is double concentration in the same risk (high correlation), e.g., a petroleum engineer investing all savings in energy stocks.
      </p>
      <p>
        Finally, consider practical constraints. Unconstrained solutions may recommend equity weights &gt;100% (leverage) or &lt;0% (shorting). In practice, retail/HNWI investors rarely use substantial leverage or shorting. Hence the unconstrained optimum is often brought back to 0%–100% bounds. The two numerical examples below illustrate these effects.
      </p>

      <h2>Practical examples: integrating human capital into asset allocation</h2>
      <p>
        To illustrate how to value human capital and incorporate it into portfolio decisions, consider two very different cases:
      </p>
      <p>
        <em>Example 1:</em> A family with two stable incomes (one public-sector job, one permanent private-sector job), long-term horizon, low risk appetite.
      </p>
      <p>
        <em>Example 2:</em> An entrepreneur with volatile income but high growth potential, whose human capital is more like a risky asset (equity-like).
      </p>
      <p>
        For each example we compute human-capital value, the H/W ratio (Human capital / Financial wealth), optimal equity/bond weights in a simple two-asset mix, the impact of practical constraints (no shorting, no leverage) and a sensitivity analysis with respect to key parameters (correlation, discount rate, income volatility).
      </p>

      <h3>Example 1: Dual-income family with stable earnings, long horizon</h3>
      <p>
        Suppose a young couple:
        <br />– He is a public-sector employee with net salary of €30,000 per year;
        <br />– She works for a stable firm (large non-cyclical company) with net salary of €25,000 per year.
      </p>
      <p>
        Both are about 35 and plan to work until 65 (30-year horizon). They hold €200,000 of investable financial wealth, currently 50% bonds and 50% equities (a balanced starting allocation). Risk appetite: moderate (assume a coefficient <IM>{tex`A`}</IM> such that, absent labour income, a 50/50 mix would be considered adequate).
      </p>
      <ol>
        <li>
          <strong>Present value of human capital (H):</strong> Treat both incomes as fairly safe—the public job is virtually guaranteed, the private job is stable with low layoff probability. Use a 2% real discount rate. Current combined income is €55,000 per year. Assuming constant real income, the annuity PV is:
          <br />
          <BM>{tex`H \approx 55{,}000 \times \frac{1 - (1+0.02)^{-30}}{0.02} \;\approx\; 1{,}231{,}800\,\text{€}.`}</BM>
        </li>
        <li>
          <strong>H/W ratio:</strong> H ≈ €1.23m, W = €200k ⇒ H/W ≈ 6.15 (≈86% of total wealth is human capital).
        </li>
        <li>
          <strong>Optimal equity weight (BMS):</strong> Let <IM>{tex`E(R_m)-R_f=5\%`}</IM>, <IM>{tex`\sigma_m=20\%`}</IM> (so <IM>{tex`\sigma_m^2=0.04`}</IM>), and <IM>{tex`A=4`}</IM>. With <IM>{tex`\operatorname{Cov}(R_L,R_m)\approx 0`}</IM> (bond-like incomes):
          <br />
          <BM>{tex`w_{\text{equity}}^{(W)}=\frac{W+H}{W}\frac{0.05}{4\cdot 0.04}\approx 7.15\cdot 0.3125 \approx 2.23 \; (223\%).`}</BM>
          This is the <em>unconstrained</em> optimum &gt;100%. With 0–100% bounds and no leverage, the constrained solution is to move <strong>toward 100% equities on W</strong> (or at least a very high share). Despite sounding aggressive, overall risk remains moderate because most wealth is bond-like via H.
        </li>
        <li>
          <strong>Constraints &amp; intuition:</strong> The gap between the unconstrained target and feasible allocation narrows over time as W grows and H decays; continually high equity on W naturally raises total equity exposure as H/W falls.
        </li>
        <li>
          <strong>Sensitivity:</strong> A modest positive income–market correlation would slightly reduce the equity share; higher discount rates (more job risk) reduce H/W and temper the equity tilt. Pure income <em>variance</em> alone does not change weights in this one-period MV model; only the <em>covariance</em> matters.
        </li>
      </ol>

      <h3>Example 2: Entrepreneur with volatile income and high potential</h3>
      <p>
        A 40-year-old tech entrepreneur earns, on average, ~€80,000 per year with high uncertainty; income is highly correlated with equity markets (<IM>{tex`\rho \approx 0.6\text{–}0.8`}</IM>). Financial wealth W = €300,000 (70% equities / 30% bonds).
      </p>
      <ol>
        <li>
          <strong>Present value of H:</strong> Use a prudent real discount rate <IM>{tex`r_d=8\%`}</IM>, 20-year horizon:
          <br />
          <IM>{tex`H \approx 80{,}000 \times 9.8 \approx 784{,}000\ \text{€} \ (\sim 800k).`}</IM>
        </li>
        <li>
          <strong>H/W ratio:</strong> H/W ≈ 2.67 (human capital ≈72% of total).
        </li>
        <li>
          <strong>Optimal equity weight (BMS):</strong> Estimate <IM>{tex`\beta_L=\operatorname{Cov}(R_L,R_m)/\sigma_m^2`}</IM> from percentage income changes (or a sector proxy). The correction term <IM>{tex`-\beta_L/(A\,W)`}</IM> can push the unconstrained optimum to ≤0. With 0–100% bounds, a <strong>very low equity share (often 0%)</strong> on W is sensible: the job already provides large equity-like exposure.
        </li>
        <li>
          <strong>Constraints &amp; hedging:</strong> Full hedging would short equity (e.g., index futures). Most individuals won’t; thus, hold safe assets (govies, IG credit, cash) and ample liquidity.
        </li>
        <li>
          <strong>Sensitivity:</strong> Lower correlation permits some equity (e.g., 20–40%); if correlation were near 1, keep equities at 0%. Higher idiosyncratic income volatility argues for more safety/liquidity even if covariance is moderate.
        </li>
      </ol>

      <h2>Conclusions</h2>
      <p>
        Human capital is central to building optimal portfolios. In the Bodie–Merton–Samuelson one-period mean–variance setting, integrate it by:
        (i) applying the <strong><IM>{tex`(W+H)/W`}</IM></strong> “leverage” factor when income is weakly correlated/safe;
        (ii) adjusting by <strong><IM>{tex`\operatorname{Cov}(R_L,R_m)`}</IM></strong> (estimated on <em>income returns</em>) to reduce/increase equity when labour income is pro-/counter-cyclical;
        (iii) remembering that income <em>variance</em> per se does not change weights (covariance does). Multi-period CRRA models add intertemporal <em>hedging demand</em>.
      </p>
      <p>
        Practically, this yields portfolios better aligned with real-world risk, avoids double-loading the same factor via job and investments, and improves total diversification. Rebalance as H decays or its risk profile changes.
      </p>

      <h3>References</h3>
      <p>
        Bodie, Z., Merton, R.C., Samuelson, W.F. (1992); Campbell, J.Y., Viceira, L.M. (2002); OECD (2012) <em>Measuring Sustainable Development</em>; CONSOB (2004) <em>Portfolio choices in pension saving</em>; Biagio Del Prete (2022), <em>Divide the portfolio into three, without forgetting human capital</em>; Investopedia (2022), <em>Human Capital’s Impact on Investors</em>.
      </p>
      <p>
        [1] [2] [4] [7] [9] [23] [26] Human Capital&apos;s Impact on Investors<br />
        <a href="https://www.investopedia.com/articles/younginvestors/09/human-capital.asp" target="_blank" rel="noopener noreferrer">
          https://www.investopedia.com/articles/younginvestors/09/human-capital.asp
        </a>
      </p>
      <p>
        [3] [10] Human capital: what it is, how to calculate it and how to protect it (IT)<br />
        <a href="https://www.lucalecchini.com/blog/capitale-umano-definizione/" target="_blank" rel="noopener noreferrer">
          https://www.lucalecchini.com/blog/capitale-umano-definizione/
        </a>
      </p>
      <p>
        [5] ecb.europa.eu<br />
        <a
          href="https://www.ecb.europa.eu/events/pdf/conferences/131017/papers/Session_3_Fagereng.pdf?01933346814bac7658a7aab2d76a4c31"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.ecb.europa.eu/events/pdf/conferences/131017/papers/Session_3_Fagereng.pdf?01933346814bac7658a7aab2d76a4c31
        </a>
      </p>
      <p>
        [6] [16] [17] [18] [19] [20] [21] [24] [25] Lec_4_Human_Capital_Background_Risks_and_Optimal_Portfolio_Decisions20190918104650 (1).pdf<br />
        <code>file://file-R5jmXxCgu7FBv3QdjKTVtU</code>
      </p>
      <p>
        [8] Divide the portfolio into three, without forgetting human capital • Biagio Del Prete (IT)<br />
        <a href="https://www.biagiodelprete.it/dividere-il-portafoglio-in-tre-non-dimenticando-il-capitale-umano/" target="_blank" rel="noopener noreferrer">
          https://www.biagiodelprete.it/dividere-il-portafoglio-in-tre-non-dimenticando-il-capitale-umano/
        </a>
      </p>
      <p>
        [11] Lec_4_Human_Capital_Background_Risks_and_Optimal_Portfolio_Decisions20190918104650.pdf<br />
        <code>file://file-39aiidxoHeZVaqFk52tWD1</code>
      </p>
      <p>
        [12] [13] [14] [15] Microsoft Word - inarcassa_oct2003.doc (IT)<br />
        <a href="https://www.cerp.carloalberto.org/wp-content/uploads/2008/12/le_scelte_di_portafoglio.pdf" target="_blank" rel="noopener noreferrer">
          https://www.cerp.carloalberto.org/wp-content/uploads/2008/12/le_scelte_di_portafoglio.pdf
        </a>
      </p>
      <p>
        [22] Tim Isbell online - Human vs Financial Capital<br />
        <a href="https://www.isbellonline.org/personal-finance/investments/asset-allocation-basics/human-v-financial-capital" target="_blank" rel="noopener noreferrer">
          https://www.isbellonline.org/personal-finance/investments/asset-allocation-basics/human-v-financial-capital
        </a>
      </p>
    </div>
  ),
},


{
  slug: "factor-investing-complete-guide",
  title: "Factor Investing: Concepts, Evidence, and a Practical Guide for Investors",
  metaDescription:
    "What factor investing is, the core factors (value, momentum, low volatility, quality), empirical evidence, single vs multi-factor implementations, costs/risks, and the role of human capital. Full guide with references.",
  tags: ["factor", "education", "etf"],
  readTime: "10 min",
  author: "Editorial Team",
  datePublished: "2025-08-22",
  dateUpdated: "2025-08-22",
  coverImage: FactorCover,
  canonical: `${DOMAIN}/en/kb/factor-investing-complete-guide`,
  content: (
    <div>
      <h1>Factor Investing: Concepts, Evidence, and a Practical Guide for Investors</h1>

      <h2>Introduction</h2>
      <p>
        Factor investing is an investment approach that focuses on systematic characteristics—the so-called
        factors—capable of generating long-term excess returns over the market portfolio. Since the formulation
        of the Capital Asset Pricing Model (CAPM) in the 1960s, researchers have observed valuation anomalies
        that CAPM alone could not explain: stocks with low multiples, reduced volatility, or favorable recent
        price trends delivered returns above those predicted by the model. These anomalies were codified as
        risk factors and, over recent decades, their combination has become increasingly central for both
        institutional and retail portfolios.
      </p>
      <p>
        Robeco’s guide clarifies that investing in factors means focusing on market segments with structural
        properties that historically delivered superior risk-adjusted returns versus the index. Initially
        confined to academic portfolios, factor investing spread among professionals after a 2009 study on the
        Norwegian sovereign wealth fund (NBIM) showed that over 70% of active returns were explained by implicit
        factor exposures. Many investors have since exploited factors deliberately as a third way between passive
        and active management: rules-based, transparent, low-cost strategies that can still generate alpha.
      </p>
      <p>
        The approach does not aim to find a single “winning” stock, but to
        capture these risk premiums in a transparent, systematic way. To reduce
        the phases of underperformance of individual factors it is advisable to
        combine several factors in multi‑factor portfolios.
      </p>

      <h2>Key rewarded factors</h2>
      <p>
        The literature has identified many factors, but only a few meet strict criteria of persistence,
        pervasiveness (across markets), robustness (to construction methods), investability, and intuitive
        explanation. Four are widely regarded as “proven” in equities:
      </p>
      <ul>
        <li>
          <strong>Value</strong> – Stocks with low valuations (e.g., low price-to-book or price-to-earnings)
          tend to outperform high-valuation stocks. Documented since the 1970s across many markets, “cheap”
          stocks have delivered higher returns for a given level of risk.
        </li>
        <li>
          <strong>Momentum</strong> – Stocks with strong recent performance tend, on average, to continue doing
          well over subsequent months. Formalized in the 1990s, momentum is among the most persistent factor premia.
        </li>
        <li>
          <strong>Low Volatility</strong> – Stocks with lower volatility or market beta deliver returns higher
          than their risk would suggest, a CAPM violation confirmed in developed and emerging markets; drawdowns
          are typically smaller.
        </li>
        <li>
          <strong>Quality</strong> – Firms with solid fundamentals (persistent earnings, low leverage, strong
          balance sheets) tend to outperform lower-quality peers; modern definitions also include earnings
          sustainability and governance.
        </li>
      </ul>
      <p>
        Other commonly cited factors include <em>size</em> (small-caps vs large-caps), <em>carry/value</em>, and
        <em> sentiment</em>. Recently, research proposed factors tied to intangibles, such as intellectual capital
        and reputation: Sparkline Capital, for instance, suggests an “intangible value” factor that invests in
        firms with strong intellectual capital and brands, delivering returns with low correlation to traditional
        factors. This reflects the growing importance of intangible assets (brands, IP, human capital) in the
        modern economy.
      </p>
      <h2>Fundamentals and formulas</h2>
      <h3>From CAPM to the Fama–French models</h3>
      <p>
        The <strong>Capital Asset Pricing Model</strong> (CAPM) states that the
        expected return of an asset depends on the risk‑free rate and on its
        sensitivity to the market (beta):
      </p>
      <p style={{ fontFamily: "monospace", paddingLeft: "1rem" }}>
        E[R] = R_f + β\*(E[R_M] – R_f)
      </p>
      <p>
        The model can be viewed as a long position on the market and a short
        position on the risk‑free asset: a stock with β=1.3 is equivalent to
        holding 130 % of the market and financing 30 % with a short position in
        the risk‑free asset.
      </p>
      <p>
        To explain differences in returns not captured by the CAPM, Fama and
        French introduced the <em>size</em> (SMB) and <em>value</em> (HML)
        factors. The three‑factor model expresses the excess return of a
        portfolio as the sum of exposures to the market, the size factor and
        the value factor.
      </p>
      <h2>Empirical evidence</h2>
      <p>
        To quantify factor premia, many studies analyze long/short portfolios that buy high-scoring stocks and
        sell low-scoring ones. Alpha Architect shows that investing in cheap stocks after the dot-com bust
        (2000–2014) produced about a 4.67% annual premium versus the market. Over the same period, value,
        momentum, low volatility, quality, and size all outperformed the market. Yet premia are not constant:
        extended underperformance can test investors’ discipline, who must keep exposure through adverse cycles.
      </p>
      <p>
        A 2024 Russell Investments report confirms factor performance varies quarter to quarter: in Q3-2024,
        Low Volatility outperformed the global index by +0.8% to +2.3%, while Momentum and Growth underperformed
        (–0.7% to –1.8%) and Value delivered mixed results. This highlights the importance of combining multiple
        factors to mitigate cyclicality.
      </p>

      <h2>From theory to practice: building factor portfolios</h2>
      <p>Robeco distinguishes two main implementation paths:</p>
      <ul>
        <li>
          <strong>“Pure” single-factor portfolios</strong>: maximize exposure to a specific premium (e.g., a
          value or momentum index). They are simple and transparent but can endure long droughts when the factor
          faces a negative cycle.
        </li>
        <li>
          <strong>Integrated multi-factor portfolios</strong>: combine factors within one basket. Integration can
          be (a) <em>integrated</em>, scoring each stock on multiple factors simultaneously and selecting only if
          it ranks well across them, or (b) <em>mixed/sleeved</em>, building separate sleeves and aggregating later.
          Research (AQR/Alpha Architect) finds integrated designs under tracking-error constraints can generate
          higher alpha than siloed approaches; other work notes sleeves can be competitive when the number of
          holdings is limited. The choice depends on objectives, turnover costs, and tracking-error capacity.
        </li>
      </ul>

      <h2>Factors beyond equities</h2>
      <p>
        While most associate factor investing with equities, premia exist in other asset classes: bonds (credit
        spread, term premium), FX (carry), and commodities (backwardation vs contango). For instance, FX carry
        buys high-rate currencies and sells low-rate ones; in corporate bonds, value (spreads) and momentum also
        appear. Cross-asset diversification raises the odds that at least one factor sleeve is in a favorable cycle.
      </p>

      <h2>Costs and risks</h2>
      <p>
        Factor strategies are not free of costs or risks. Smart-beta ETFs or funds can charge higher fees versus
        broad beta and may incur higher turnover, with trading costs and tax implications. Product selection is
        crucial (fees, transparent methodology), and premia can fade if a factor becomes overcrowded or data are
        not maintained. No strategy is drawdown-proof: value lagged for much of the 2010s; momentum can suffer in
        sideways markets or sharp reversals.
      </p>

      <h2>Human capital as a risk factor</h2>
      <p>
        Andrew Ang’s work emphasizes a neglected dimension: human capital also exhibits factor exposures. Labor
        income represents a large share of overall wealth and can be viewed as a non-tradable asset with bond-like
        or stock-like traits. If your income is <em>bond-like</em> (stable, low cyclicality), you can assume more
        financial risk and keep a higher equity weight even near retirement; if it is <em>stock-like</em> (entrepreneur
        or cyclical sector), it is prudent to hold more bonds or defensive assets to offset human-capital risk.
      </p>
      <p>
        Recent research quantifies these links. Blanchett and Straehl (2017) estimate average human capital to be
        ~30% stock-like, with wide sector differences: public-sector workers have near-zero market beta, while
        mining shows ~0.53. These gaps affect optimal equity weights: public employees may hold up to 76% in stocks,
        whereas mining workers should cap at ~40%. In Sweden, a 20% increase in wage volatility reduces equity
        allocation by roughly 20%. Human capital also relates to size and value factors by industry, so cyclical
        workers should tilt to defensive factors (low vol, quality) and avoid buying their own sector’s risk.
      </p>
      <p>
        Irrational Capital, with J.P. Morgan, developed the Human Capital Factor® index (culture, morale, engagement).
        Reports show HCI beat the benchmark every sample year (including 2024), with superior risk/return, lower
        volatility, and smaller drawdowns—evidence that incorporating human-capital quality can generate durable alpha
        and underscores the role of intangibles.
      </p>

      <h2>Human asset evaluation and personal human capital</h2>
      <p>
        Assess whether your job is <em>stock-like</em> or <em>bond-like</em> and how it evolves over your life cycle;
        this is key to designing a balanced portfolio and choosing factor tilts. A young saver with stable income can
        lean more into value and momentum; an entrepreneur with volatile income should prefer low volatility, quality,
        or multi-factor designs with lower market risk.
      </p>

      <h2>Investor takeaways</h2>
      <ul>
        <li>
          <strong>Diversify across factors and asset classes.</strong> Blend value, momentum, low volatility, quality,
          and size; extend factors to bonds, FX, and commodities.
        </li>
        <li>
          <strong>Evaluate human capital.</strong> Gauge income stability and correlation with market factors; if your
          job is cyclical, reduce exposure to the same sector and increase defensive factor tilts.
        </li>
        <li>
          <strong>Pick efficient products.</strong> Check methodology, universe, rebalance frequency, and fees. Consider
          currency-hedged share classes where appropriate.
        </li>
        <li>
          <strong>Stay disciplined.</strong> Factor premia are cyclical; long horizons and consistency are essential.
        </li>
        <li>
          <strong>Consider integrated designs.</strong> Integrated multi-factor can improve risk/return, but balance
          complexity and concentration with your resources and goals.
        </li>
      </ul>

      <h2>Conclusions</h2>
      <p>
        Factor investing advances beyond the simple active/passive dichotomy. By harnessing documented premia—value,
        momentum, low volatility, quality, size, and intangibles—investors can enhance risk-adjusted returns and build
        more robust portfolios. Yet factors are cyclical and demand method and discipline. Incorporating human capital
        into asset-allocation decisions helps avoid concentrated risks and tailor factor exposure to personal conditions.
      </p>

      <h3>References</h3>
      <ul>
        <li>
          [1] What is Factor Investing? –{" "}
          <a href="https://www.nepc.com/a-guide-to-factor-investing/" target="_blank" rel="noopener noreferrer">
            https://www.nepc.com/a-guide-to-factor-investing/
          </a>
        </li>
        <li>
          [2] [3] Intangible Value: A Sixth Factor –{" "}
          <a
            href="https://www.sparklinecapital.com/post/intangible-value-a-sixth-factor"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.sparklinecapital.com/post/intangible-value-a-sixth-factor
          </a>
        </li>
        <li>
          [4] [5] Factor Investing: Evidence Based Insights –{" "}
          <a
            href="https://alphaarchitect.com/factor-investing-and-evidence-based-investing/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://alphaarchitect.com/factor-investing-and-evidence-based-investing/
          </a>
        </li>
        <li>
          [6] Equity Factor Report – Q3 2024 | Russell Investments –{" "}
          <a
            href="https://russellinvestments.com/content/ri/nn/en-gb/insights/russell-research/2024/10/equity-factor-report---q3-2024-value-and-low-volatility-strategi.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            link
          </a>
        </li>
        <li>
          [7] [8] Should Investors Combine or Separate Their Factor Exposures? –{" "}
          <a
            href="https://alphaarchitect.com/should-investors-combine-or-separate-their-factor-exposures/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://alphaarchitect.com/should-investors-combine-or-separate-their-factor-exposures/
          </a>
        </li>
        <li>
          [9] Do Currency-Hedged ETFs Have Merit for the Long Term? | Morningstar –{" "}
          <a
            href="https://www.morningstar.com/funds/do-currency-hedged-etfs-have-merit-long-term"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.morningstar.com/funds/do-currency-hedged-etfs-have-merit-long-term
          </a>
        </li>
        <li>
          [10] Andrew Ang – Slides (local) –{" "}
          <a href="http://localhost:8451/file:///home/oai/share/51630_20151119_113426_ANDREW_SLIDESBOOK.pdf" target="_blank" rel="noopener noreferrer">
            localhost
          </a>
        </li>
        <li>
          [11] [12] [13] Factor Human Capital Risk into Your Financial Plans –{" "}
          <a
            href="https://cogentsw.com/financial-planning/factor-human-capital-risk-into-your-financial-plans/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://cogentsw.com/financial-planning/factor-human-capital-risk-into-your-financial-plans/
          </a>
        </li>
        <li>
          [14] [15] [16] J.P. MORGAN RESEARCH | Irrational Capital –{" "}
          <a href="https://www.irrational.capital/jp-morgan-research" target="_blank" rel="noopener noreferrer">
            https://www.irrational.capital/jp-morgan-research
          </a>
        </li>
      </ul>
    </div>
  ),
},

{
  slug: "what-are-alpha-and-beta-how-to-calculate-them",
  title: "What Are Alpha and Beta in Investing (and How to Calculate Them)",
  metaDescription:
    "Alpha and Beta explained clearly yet rigorously: definitions, CAPM formulas, how to compute and interpret them with practical examples on sector ETFs (Tech vs Utilities).",
  tags: ["education", "finance", "etf", "risk management"],
  readTime: "12 min",
  author: "Editorial Team",
  datePublished: "2025-08-22",
  dateUpdated: "2025-08-22",
  coverImage: AlphaBetaCover,
  canonical: `${DOMAIN}/kb/what-are-alpha-and-beta-how-to-calculate-them`,
  content: (
    <div>
      <h1>What Are Alpha and Beta in Investing (and How to Calculate Them)</h1>

      <p>
        In the investing world, we often hear about <strong>Alpha</strong> and{" "}
        <strong>Beta</strong>—two foundational indicators to evaluate performance and risk for portfolios,
        funds, and ETFs. These concepts come from the standard reference model, the{" "}
        <em>Capital Asset Pricing Model</em> (CAPM), and are used by both retail investors and professionals
        to understand how an investment behaves relative to the market. In this article—written in an
        accessible style but with technical depth—we’ll see exactly what Alpha and Beta indicate, how to
        compute them quantitatively, and provide practical examples using real data from U.S. sector ETFs to
        illustrate their use. The goal is to help investors (especially passive ETF investors) better grasp
        these concepts and extract useful insights for their decisions.
      </p>
        <YouTubeEmbed
          videoId="RJAH1QgOlZA"
          title="Alpha vs Beta in investing: how to read risk and return"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />


      <h2>Alpha and Beta at a glance: why they matter</h2>
      <p>
        Alpha and Beta are two sides of the same coin. In short:
      </p>
      <ul>
        <li>
          <strong>Beta</strong> measures the volatility and systematic risk of an investment relative to the
          reference market (e.g., the S&amp;P 500). It tells us how much the security or fund follows market moves.
        </li>
        <li>
          <strong>Alpha</strong> measures an investment’s excess return relative to what would be expected based
          on its Beta. It indicates whether the return earned is above (or below) what is justified by the market
          risk taken.
        </li>
      </ul>
      <p>
        In other words, Beta tells us how correlated and risky an investment is relative to the market, while
        Alpha tells us how much it returns in excess of the market, net of the risk taken. These indicators are
        crucial for evaluating funds and ETFs: for example, a passive fund replicating an index will have a Beta
        close to 1 and Alpha around zero (it faithfully tracks the market), whereas an active manager will seek
        positive Alpha (to beat the market) possibly assuming a certain Beta (market exposure).
      </p>

      <h2>Beta: what it is and how it works</h2>
      <p>
        <strong>Beta</strong> ({String.raw`\beta`}) is a coefficient that defines the measure of systematic risk
        of a financial asset or portfolio. Technically, the Beta of an investment{" "}
        <IM>{String.raw`i`}</IM> relative to a market index <IM>{String.raw`m`}</IM> is computed as:
      </p>
      <p>
        <BM>{String.raw`\beta_i \;=\; \dfrac{\operatorname{Cov}(R_i,\,R_m)}{\operatorname{Var}(R_m)}`}</BM>
      </p>
      <p>
        where <IM>{String.raw`R_i`}</IM> are the returns of investment <IM>{String.raw`i`}</IM> and{" "}
        <IM>{String.raw`R_m`}</IM> are the returns of the market (benchmark index). Put simply,{" "}
        <IM>{String.raw`\beta`}</IM> expresses how much the security’s moves follow (or amplify, or dampen) the
        moves of the reference market. The practical computation of Beta requires:
      </p>
      <ol>
        <li>
          <em>Historical return data</em>—e.g., collect monthly returns of a security or ETF and the corresponding
          market index for a given period (say, 3 years).
        </li>
        <li>
          <em>Covariance and variance</em>—compute the covariance between the security’s returns and the market’s
          returns, and the variance of the market’s returns.
        </li>
        <li>
          <em>Covariance/variance ratio</em>—dividing covariance by market variance yields Beta. This is
          mathematically equivalent to the slope of a linear regression of the security’s returns (dependent
          variable) on the market’s returns (independent variable).
        </li>
      </ol>

      <h3>Interpreting Beta</h3>
      <ul>
        <li>
          <IM>{String.raw`\beta = 1`}</IM>: the investment moves, on average, in line with the market. If the
          market gains +1%, the security ~+1%; if it drops −1%, the security ~−1%. A Beta of 1 signals volatility
          similar to the benchmark.
        </li>
        <li>
          <IM>{String.raw`\beta > 1`}</IM>: the investment is more volatile than the market. It tends to amplify
          moves (e.g., <IM>{String.raw`\beta = 1{,}2`}</IM>).
        </li>
        <li>
          <IM>{String.raw`0 < \beta < 1`}</IM>: the investment is less volatile than the market (same direction,
          attenuated).
        </li>
        <li>
          <IM>{String.raw`\beta \approx 0`}</IM>: the investment shows no correlation with the market (independent
          moves).
        </li>
        <li>
          <IM>{String.raw`\beta < 0`}</IM>: the investment moves in the opposite direction to the market (negative
          correlation).
        </li>
      </ul>
      <p>
        Beta thus measures <em>systematic risk</em>: the risk tied to market-wide moves that cannot be eliminated via
        diversification. By definition, the market (benchmark) has{" "}
        <IM>{String.raw`\beta = 1`}</IM>. Knowing a security’s or fund’s Beta helps assess how exposed that product
        is to market swings.
      </p>
      <p>
        <em>Conceptual example</em>: imagine a fund with{" "}
        <IM>{String.raw`\beta = 1{,}5`}</IM>. If the market gains +10% in a year, we expect (on average) the fund to
        gain ~+15% thanks to its high beta. If, however, it earns only +12% or, conversely, +18%, the difference
        relative to what was expected is Alpha—which we cover now.
      </p>

      <h2>Alpha: what it is and what it shows</h2>
      <p>
        <strong>Alpha</strong> ({String.raw`\alpha`}) represents an investment’s ability to generate{" "}
        <em>additional return</em> independent of the market. In practical terms, Alpha is the extra (positive or
        negative) return that a manager or security earns relative to its benchmark after accounting for market risk
        (Beta).
      </p>
      <p>
        In an actively managed fund, Alpha measures the value added (or destroyed) by the manager: an{" "}
        <IM>{String.raw`\alpha > 0`}</IM> indicates the fund beat the market; an{" "}
        <IM>{String.raw`\alpha < 0`}</IM> indicates underperformance. For passive (indexed) ETFs, one expects{" "}
        <IM>{String.raw`\alpha \approx 0`}</IM>.
      </p>
      <p>
        Methodologically, Alpha is the intercept in a linear regression of the investment’s returns on the market’s
        returns. Using CAPM (in excess over the risk-free rate), the baseline relation is:
      </p>
      <p>
        <BM>{String.raw`R_{i} - R_{f} \;=\; \alpha \;+\; \beta\,\big(R_{m} - R_{f}\big) \;+\; \varepsilon`}</BM>
      </p>
      <p>
        Rearranging yields <em>Jensen’s Alpha</em> for a portfolio{" "}
        <IM>{String.raw`p`}</IM>:
      </p>
      <p>
        <BM>{String.raw`\alpha_{p} \;=\; (R_{p} - R_{f}) \;-\; \beta_{p}\,\big(R_{m} - R_{f}\big)`}</BM>
      </p>

      <h3>Interpreting Alpha</h3>
      <ul>
        <li>
          <IM>{String.raw`\alpha = 0`}</IM>: return equal to what is expected given Beta (neither out- nor
          under-performance).
        </li>
        <li>
          <IM>{String.raw`\alpha > 0`}</IM>: return above what CAPM predicts (market-independent value added).
        </li>
        <li>
          <IM>{String.raw`\alpha < 0`}</IM>: return below what is due for the risk taken (underperformance).
        </li>
      </ul>
      <p>
        Together with Beta, Alpha is among the primary indicators for risk-adjusted performance (alongside standard
        deviation, Sharpe, R², Sortino/Information, etc.).
      </p>

      <h2>Passive vs active management: the role of Alpha and Beta</h2>
      <p>
        In <strong>passive</strong> ETF investing, the core concept is{" "}
        <em>market Beta</em>: buying an index ETF means buying Beta, i.e., the market’s performance (minus costs).
        Expected Alpha is near zero. In <strong>active management</strong>, the goal is to produce positive Alpha via
        security selection, sector tilts, and market timing. However, consistently beating the market is difficult;
        over long horizons only a minority of active funds deliver persistent positive Alpha after costs.
      </p>

      <h2>Practical examples: computing Alpha and Beta for sector ETFs</h2>
      <p>
        Consider two U.S. sector ETFs: <em>Technology</em> (e.g., XLK) and{" "}
        <em>Utilities</em> (e.g., XLU), with the broad S&amp;P 500 as benchmark. These sectors have very different
        risk/return profiles: tech is more volatile with strong returns over the last decade; utilities are more
        defensive.
      </p>

      <h3>(Conceptual) figure</h3>
      <figure>
        <a href={AlphaBetaPlot} target="_blank" rel="noopener noreferrer">
          <img
            src={AlphaBetaPlot}
            alt="Scatterplot of portfolio returns vs market with CAPM regression line: slope = Beta, intercept = Alpha."
            loading="lazy"
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </a>
        <figcaption>
          Scatterplot of portfolio returns versus the market with the CAPM regression line. The slope of the line
          represents <strong>Beta</strong>, and the intercept represents <strong>Alpha</strong>.
        </figcaption>
      </figure>

      <h3>Technology sector Beta vs S&amp;P 500</h3>
      <p>
        The Technology sector ETF (ticker: XLK) in recent years has exhibited volatility well above the market.
        Calculating Beta on a historical basis (for example using monthly returns over the last 5–10 years), one
        finds that the Beta of XLK relative to the S&amp;P 500 is slightly above 1, indicating amplified sensitivity.
        Indeed, the standard deviation of XLK’s returns is roughly 27% per year, versus ~19% for the S&amp;P 500[1],
        with a high correlation to the market (given the equity nature, correlation near 1 is not far off). This leads
        the Tech sector Beta to be around 1.1–1.2 (about 10–20% more volatile than the market). A Beta &gt;1 was
        predictable: tech companies tend to be more subject to economic cycles and fluctuations (think of the declines
        during the dot-com bust or the rapid climbs in recent years), so they have higher systematic risk.
        For a concrete calculation, we use a 10-year horizon. Between 2015 and 2025, the XLK ETF recorded a
        compounded average annual return of about +23.3%, compared with ~+15.3% per year for the S&amp;P 500[2]. Assuming
        an average risk-free rate negligible in those years (it was very low, around 0–2%), we can estimate Beta and
        Alpha:
        <ul>
          <li>
            <strong>Beta (XLK)</strong> – suppose β = 1.2 over 10 years (consistent with the higher observed
            volatility of 27% vs 19% and high correlation).
          </li>
          <li>
            <strong>Expected return according to CAPM</strong> – Given β=1.2, if the market returned +15.3%, the
            “expected” return for XLK would be: R_f + 1.2 × (15.3% − R_f). Even assuming R_f ≈ 0%, this yields an
            expected ≈ 18.4%.
          </li>
          <li>
            <strong>Actual return of XLK</strong> – ~23.3% per year.
          </li>
        </ul>
        Alpha (XLK) = actual return − expected return = +23.3% − 18.4% ≈ +4.9% per year. This positive Alpha
        indicates that the technology sector outperformed expectations based on its risk. Even allowing for some
        risk-free rate, the result changes little (with R_f&gt;0% the calculated alpha would be slightly lower but
        still strongly positive). In other words, the tech ETF achieved a significant extra return beyond what is
        explainable by simply “being more volatile”: there was an additional gain, due to the extraordinary growth of
        tech stocks in that period.
      </p>

      <h3>Utilities sector Beta vs S&amp;P 500</h3>
      <p>
        We now turn to the Utilities sector ETF (ticker: XLU), which includes electricity, gas, water companies, etc.
        Utilities are considered defensive: relatively stable demand, high dividends, lower sensitivity to economic
        cycles. We therefore expect a Beta below 1. Indeed, historically the Beta of XLU hovers around 0.7 relative
        to the S&amp;P 500[3]. This means that if the market rises or falls by 10%, the utilities sector on average
        rises/falls by about 7%. Data confirm lower volatility: for example, the daily standard deviation of XLU is
        around 16–17%, significantly below that of the S&amp;P 500 (~19%)[4], with moderate correlation.
        Looking again at the 10 years 2015–2025, XLU achieved a compounded average annual return of about +10.9%,
        compared with +15.3% for the S&amp;P 500[5][6]. We estimate:
        <ul>
          <li>
            <strong>Beta (XLU)</strong> – about β = 0.7 (as observed historically).
          </li>
          <li>
            <strong>Expected return according to CAPM</strong> – With β=0.7 and the market at +15.3% (taking the risk-free
            rate as negligible), CAPM would give an expected return ≈ 0% + 0.7 × 15.3% = 10.7% per year. (If we
            assumed R_f = 2%, it would be 2% + 0.7 × (15.3% − 2%) = 2% + 0.7 × 13.3% = 11.3%. The idea changes
            little.)
          </li>
          <li>
            <strong>Actual return of XLU</strong> – ~10.9% per year.
          </li>
        </ul>
        Alpha (XLU) ≈ 10.9% − 10.7% = +0.2% per year (practically ~0%). With R_f = 2% in the example, we would have
        10.9% − 11.3% = −0.4%, i.e., a small slightly negative alpha. We can conclude that the utilities sector
        returned roughly what one would expect from its Beta—slightly below expectations if we include a risk-free
        rate, but still within a narrow margin. This makes sense: utilities had lower risk and accordingly generated
        lower returns than the market, as expected. There was no significant out- or under-performance independent of
        the market, which is reflected in an Alpha around zero (slightly negative if considering high dividends and
        other characteristics that basic CAPM might not capture). In summary, the utilities sector neither “beat” the
        market nor disappointed beyond what is attributable to lower risk: it delivered the expected return for a
        defensive investment.
      </p>

      <h3>Comparison and considerations</h3>
      <p>
        From the example, it is clear that Beta and Alpha together paint the full picture: XLK (Tech) had Beta &gt; 1
        and produced a substantial positive Alpha (it outperformed beyond market risk), while XLU (Utilities) had Beta
        &lt; 1 and produced Alpha ~0 or slightly negative (it returned less than the market, but proportionally to
        the lower risk taken). For a passive ETF investor, these indicators help understand the nature of one’s
        portfolio: if one holds many “aggressive” sectors with high Betas, one can expect more volatility and potentially
        extra return in bull markets (but risk of underperformance in bear markets); defensive sectors will provide
        stability (low Betas) but will unlikely beat the market in prolonged rallies. It is important to note that
        Alpha, being a historical measure, is not guaranteed in the future: the fact that tech had ~5% per year of
        Alpha over the last 10 years does not mean it will maintain that in the next 10 (it could decline if the
        sector slows or if other sectors catch up). Similarly, a past negative Alpha does not condemn a sector
        forever—but often reflects structural dynamics (e.g., difficulties in beating the market for actively managed
        funds, or mature sectors that grow less than average). Finally, recall that Alpha and Beta are calculated
        relative to a specific benchmark: choosing the right benchmark is crucial. In our example, we used the S&amp;P
        500 for both sector ETFs; this is fine for a general comparison, but sometimes to truly assess a manager’s
        skill it is better to use a more targeted benchmark. For example, a technology sector fund should be compared
        to a tech index (its Alpha versus S&amp;P 500 also reflects sector effects); however, from an asset allocator’s
        perspective, comparing to the broad market helps understand how the sector contributes to the portfolio’s
        overall performance. The key is consistency: Beta and Alpha make sense only relative to the same reference
        index.
      </p>

      <h2>Conclusion</h2>
      <p>
        Alpha and Beta are two key financial parameters: Beta quantifies an investment’s exposure to market risk,
        while Alpha measures its ability to generate extra return relative to the market. For passive ETF investors,
        Beta and Alpha help understand how to combine various products to build the desired risk/return profile—
        knowing that most of the return will come from market Beta, while generating consistent Alpha is difficult and
        rare. A deep understanding of these concepts helps better evaluate investment performance: for example,
        distinguishing whether a fund is beating the market because of Beta (perhaps because it is riskier) or because
        of true Alpha (manager skill, stock selection, etc.). In a field where people often search for “miraculous
        active strategies,” Alpha and Beta refocus attention on the fundamentals: how much of the result is explained
        by market movements, and how much is true value added? The vast majority of returns can be traced back to
        Beta (i.e., market factors); genuine Alpha is rare and valuable—hence the name “alpha,” the first letter of
        the Greek alphabet, as if indicating something special. It’s no coincidence that the financial jargon speaks of
        “alpha hunting.” But as investors, one must be aware that this hunt entails costs and risks, and that often a
        good strategy is to secure market Beta efficiently (for example with low-cost ETFs) and not chase elusive
        Alpha at all costs.
      </p>
      <p>
        In conclusion, Alpha and Beta are conceptual tools that help us navigate the waters of financial markets:
        knowing them and being able to calculate them gives us an edge in understanding why our investments are doing
        well or poorly, and in making informed decisions for the future. Whether you choose the simplicity of a
        passive portfolio or venture into active strategies in search of Alpha, keeping an eye on these indicators
        will help you stay anchored to risk-return fundamentals.
      </p>

      <h3>Helpful sources</h3>
      <ul>
        <li>
          Investopedia — <em>Alpha and Beta for Beginners</em>
        </li>
        <li>
          Moneyfarm — <em>Beta and Alpha: Meaning in Finance and Investing</em>
        </li>
        <li>
          Corporate Finance Institute — <em>Alpha – How to Calculate and Use Alpha</em>
        </li>
        <li>
          PortfoliosLab — <em>ETF Comparison Tool</em> (historical data XLK, XLU vs SPY):{" "}
          <a
            href="https://portfolioslab.com/tools/stock-comparison/XLK/SPY"
            target="_blank"
            rel="noopener noreferrer"
          >
            XLK vs SPY
          </a>{" "}
          •{" "}
          <a
            href="https://portfolioslab.com/tools/stock-comparison/XLU/SPY"
            target="_blank"
            rel="noopener noreferrer"
          >
            XLU vs SPY
          </a>
        </li>
        <li>
          Institute of Business &amp; Finance —{" "}
          <em>S&amp;P 500 Sectors: Beta, Correlation, R² &amp; Weightings</em>:{" "}
          <a
            href="https://icfs.com/financial-knowledge-center/sp-500-sectors-beta-correlation-r2-weightings"
            target="_blank"
            rel="noopener noreferrer"
          >
            icfs.com
          </a>
        </li>
      </ul>
    </div>
  ),
}





  
];

// ─────────────────────────────────────────────────────────────────────────────
// ARTICLE: F.I.R.E. Analysis — the mathematics of financial independence
// ─────────────────────────────────────────────────────────────────────────────

const fireArticleEN = {
  slug: "fire-analysis-mathematics-financial-independence",
  title: "F.I.R.E. Analysis: the mathematics of financial independence",
  metaDescription:
    "Quantitative guide to the FIRE movement: 4% rule, FIRE Number, Lean/Fat/Barista/Coast FIRE, Trinity Study, CAPE-SWR, Block Bootstrap Monte Carlo and Sequence of Returns Risk. Formulas, assumptions and model limitations.",
  tags: ["fire", "planning", "quant", "education"],
  readTime: "18 min",
  author: "AlphaFrame Editorial Team",
  datePublished: "2026-04-11",
  dateUpdated: "2026-04-11",
  coverImage: IndFinCover,
  canonical: `${DOMAIN}/en/kb/fire-analysis-mathematics-financial-independence`,
  content: (
    <div>
      <h1>F.I.R.E. Analysis: the mathematics of financial independence</h1>

      <p>
        The <strong>F.I.R.E.</strong> (Financial Independence, Retire Early) movement has transformed
        personal financial planning into a rigorous quantitative discipline. Born in English-speaking
        online communities in the early 2010s — with academic roots dating back at least to 1994 —
        the core idea is straightforward: accumulate enough wealth to sustain your lifestyle
        indefinitely by periodically withdrawing from investment returns. This article examines the
        mathematical models underlying FIRE, their assumptions, their limitations, and how to apply
        them in practice using the{" "}
        <a href="https://alphaweb-93f02.web.app/en/tools" target="_blank" rel="noopener noreferrer">
          AlphaFrame F.I.R.E. Calculator
        </a>.
      </p>

      {/* ───────────────────────── 1. ORIGINS ───────────────────────── */}
      <h2>1. Origins: from the 4% rule to the FIRE movement</h2>
      <p>
        The empirical foundation of FIRE is the seminal work of <strong>William Bengen (1994)</strong>,
        published in the <em>Journal of Financial Planning</em>. Analysing historical returns of
        American stock-bond portfolios from 1926 to 1992 over 30-year horizons, Bengen concluded
        that a withdrawal rate of <strong>4% per annum</strong> of the initial portfolio value —
        adjusted annually for inflation — had never depleted a portfolio in any historical 30-year
        period. This result became known as the <em>4% rule</em>.
      </p>
      <p>
        In <strong>1998</strong>, Cooley, Hubbard and Walz — professors at Trinity University —
        published the study that became famous as the <em>Trinity Study</em>. They extended
        Bengen's analysis by introducing the concept of <em>portfolio success rate</em>: the
        percentage of historical periods in which the portfolio was not depleted. The Trinity
        Study has been updated multiple times (2009, 2011, 2021) incorporating more recent data.
      </p>
      <p>
        In the 2010s, researchers such as <strong>Wade Pfau</strong> (2011) and{" "}
        <strong>Javier Estrada</strong> (2020) refined the model to account for market valuation
        (via Shiller's CAPE) and horizons exceeding 30 years — relevant for those retiring at
        35–45. In parallel, <strong>Karsten Jeske</strong> (known as <em>Early Retirement Now</em>,
        ERN) developed from 2018 a Block Bootstrap simulation methodology that represents the
        current state of the art for Sequence of Returns Risk analysis.
      </p>

      {/* ───────────────────────── 2. FIRE NUMBER ───────────────────────── */}
      <h2>2. The FIRE Number: how much do I need?</h2>
      <p>
        The starting point of any FIRE analysis is computing the target wealth, called the{" "}
        <strong>FIRE Number</strong>. The formula is immediate:
      </p>
      <BM>{tex`FN = \frac{C}{SWR}`}</BM>
      <p>
        where <IM>{tex`C`}</IM> is the annual expenditure sustainable in retirement (in real terms,
        i.e. at constant purchasing power) and <IM>{tex`SWR`}</IM> is the Safe Withdrawal Rate.
        With <IM>{tex`SWR = 4\%`}</IM>:
      </p>
      <BM>{tex`FN = \frac{C}{0.04} = 25 \times C`}</BM>
      <p>
        The "25× multiplier" has become the FIRE rule of thumb: you need a portfolio equal to
        25 times your annual expenses. With <IM>{tex`SWR = 3.5\%`}</IM> the multiplier rises to
        approximately 28.6 — more conservative and appropriate for horizons exceeding 40 years.
      </p>
      <p>
        <strong>Critical assumption:</strong> the FIRE Number assumes the portfolio is fully
        invested in financial assets with a positive expected real return. It excludes
        owner-occupied property, labour income, state pension, or other illiquid assets. Ignoring
        these sources makes the FIRE Number conservative — which may be desirable.
      </p>

      {/* ───────────────────────── 3. TRINITY ───────────────────────── */}
      <h2>3. The Trinity Study: historical success rates</h2>
      <p>
        Cooley, Hubbard and Walz (1998, updated 2021) systematised the concept of{" "}
        <strong>portfolio success rate</strong>: across all historical periods of length{" "}
        <IM>{tex`T`}</IM> years available, what percentage did not exhaust the portfolio?
        Their dataset covers the American stock and bond markets from 1926 to the present
        (S&P 500 + long-term US government bonds).
      </p>
      <p>
        Key results for a 50/50 stock/bond portfolio with inflation-indexed withdrawals show
        success rates of approximately 95–98% for 30-year horizons with{" "}
        <IM>{tex`SWR \leq 4\%`}</IM>. For 40-year horizons results are more uncertain and
        heavily dependent on allocation: a 100% equity portfolio performs better over long
        horizons (Trinity Study 2021).
      </p>
      <p>
        <strong>Limitations of the Trinity Study:</strong>
      </p>
      <ul>
        <li>US-only dataset: does not capture international diversification or underperforming
          markets such as Japan in the 1990s.</li>
        <li>Overlapping historical periods are not statistically independent: the success rate
          is not a true frequentist probability.</li>
        <li>Uses historical US inflation, not directly applicable to EUR-denominated portfolios.</li>
        <li>No modelling of taxes, transaction costs, or variable spending over time.</li>
      </ul>

      {/* ───────────────────────── 4. CAPE-SWR ───────────────────────── */}
      <h2>4. CAPE-based SWR: market valuation and withdrawal rates</h2>
      <p>
        Wade Pfau (2011) showed that the safe withdrawal rate is not a constant: it depends on
        market valuation at the time of retirement. Pfau estimated a linear model using
        Shiller's <strong>CAPE</strong> (Cyclically Adjusted Price-to-Earnings, ten-year moving
        average of real earnings) as a predictor:
      </p>
      <BM>{tex`SWR_t \;\approx\; \alpha + \beta\,\cdot\, \frac{1}{CAPE_t}`}</BM>
      <p>
        The intuition is direct: expensive markets at retirement (high CAPE) imply lower expected
        future returns, hence a lower SWR to ensure sustainability. With CAPE around 30 (S&amp;P 500
        level in 2024), Pfau's model suggests{" "}
        <IM>{tex`SWR \approx 3.0\%\text{–}3.5\%`}</IM>.
      </p>
      <p>
        <strong>Model assumption:</strong> the CAPE–SWR relationship is estimated on US historical
        data. In a European or global context, CAPE has different predictive power. The model also
        assumes the portfolio is periodically rebalanced and that allocation remains stable over time.
      </p>

      {/* ───────────────────────── 5. LONG HORIZONS ───────────────────────── */}
      <h2>5. Long horizons: Estrada's contribution (2020)</h2>
      <p>
        Javier Estrada (2020, <em>Journal of Financial Planning</em>) extended the FIRE analysis
        to 40-, 50- and 60-year horizons using an international dataset of 21 countries (1900–2019).
        Key findings:
      </p>
      <ul>
        <li>For <strong>40-year horizons</strong>, a 3.5% SWR delivers success rates above 90%
          in the vast majority of markets studied.</li>
        <li>For <strong>50+ year horizons</strong>, 3.5% remains reasonable but cross-country
          dispersion is high: markets like Japan in the 1990s show much lower success rates.</li>
        <li>International diversification significantly reduces depletion risk compared to
          single-country portfolios.</li>
      </ul>
      <p>
        The AlphaFrame calculator uses <strong>SWR = 3.5% as the default</strong> for long
        retirement horizons, consistent with Estrada's and Pfau's literature.
      </p>

      {/* ───────────────────────── 6. FIVE TYPES ───────────────────────── */}
      <h2>6. The five FIRE variants: mathematical definitions</h2>
      <p>
        The term "FIRE" encompasses several distinct goals, all derived from the base formula but
        with different multipliers or conditions on spending. In all cases, expenditure{" "}
        <IM>{tex`C`}</IM> is relative to the individual's own lifestyle — not an absolute threshold.
      </p>

      <h3>6.1 Regular FIRE</h3>
      <p>The base case: maintain current spending exactly in retirement.</p>
      <BM>{tex`FN_{\text{regular}} = \frac{C}{SWR}`}</BM>

      <h3>6.2 Lean FIRE</h3>
      <p>Frugal lifestyle in retirement, expenses reduced 30% from current levels:</p>
      <BM>{tex`FN_{\text{lean}} = \frac{0.70 \cdot C}{SWR}`}</BM>
      <p>
        Lean FIRE requires less wealth but implies real trade-offs. It suits those with already
        low expenses or who plan to optimise drastically (e.g. relocating to low cost-of-living
        areas, eliminating subscriptions, lifestyle redesign).
      </p>

      <h3>6.3 Fat FIRE</h3>
      <p>Premium lifestyle, expenses increased 50% from current levels:</p>
      <BM>{tex`FN_{\text{fat}} = \frac{1.50 \cdot C}{SWR}`}</BM>
      <p>
        Fat FIRE includes a significant buffer for discretionary spending, travel, giving or
        emergencies. It requires 2.14× the wealth of Lean FIRE, for the same SWR.
      </p>

      <h3>6.4 Barista FIRE</h3>
      <p>
        Semi-retirement: the individual continues working part-time with annual income{" "}
        <IM>{tex`I_{pt}`}</IM>, funding only the gap from the portfolio:
      </p>
      <BM>{tex`FN_{\text{barista}} = \frac{\max(0,\; C - I_{pt})}{SWR}`}</BM>
      <p>
        If <IM>{tex`I_{pt} \geq C`}</IM>, the FIRE Number is zero: part-time income fully covers
        expenses. Barista FIRE is particularly attractive for those wanting to exit full-time work
        without exiting work entirely, while preserving benefits such as health coverage,
        intellectual stimulation and social connection.
      </p>

      <h3>6.5 Coast FIRE</h3>
      <p>
        The most mathematically elegant variant: to what wealth{" "}
        <IM>{tex`P_t`}</IM> must I arrive <em>today</em> (at age <IM>{tex`t`}</IM>) so that,
        without any further contributions, compound growth alone brings the portfolio to the
        FIRE Number by target age <IM>{tex`T`}</IM>?
      </p>
      <BM>{tex`CoastFN = \frac{FN_{\text{regular}}}{(1+r)^{T-t}}`}</BM>
      <p>
        where <IM>{tex`r`}</IM> is the expected real annual return and{" "}
        <IM>{tex`T - t`}</IM> is the years remaining to target retirement. Once Coast FN is
        reached, one can stop saving (or dramatically reduce contributions) and let compounding
        do the work.
      </p>
      <p>
        <strong>Example:</strong> with <IM>{tex`C = \$36{,}000`}</IM>,{" "}
        <IM>{tex`SWR = 3.5\%`}</IM>, <IM>{tex`r = 5\%`}</IM>, current age 30, target age 55:
      </p>
      <BM>{tex`FN = \frac{36{,}000}{0.035} = \$1{,}028{,}571`}</BM>
      <BM>{tex`CoastFN = \frac{1{,}028{,}571}{(1.05)^{25}} = \frac{1{,}028{,}571}{3.386} \approx \$303{,}780`}</BM>

      {/* ───────────────────────── 7. YEARS TO FIRE ───────────────────────── */}
      <h2>7. The years-to-FIRE formula</h2>
      <p>
        Given current wealth <IM>{tex`P`}</IM>, monthly contribution <IM>{tex`A`}</IM> and
        monthly real return <IM>{tex`r = (1+r_a)^{1/12} - 1`}</IM>, the future value of the
        portfolio after <IM>{tex`n`}</IM> months is:
      </p>
      <BM>{tex`FV_n = P\,(1+r)^n \;+\; A\,\frac{(1+r)^n - 1}{r}`}</BM>
      <p>
        Setting <IM>{tex`FV_n = FN`}</IM> and solving for <IM>{tex`n`}</IM>:
      </p>
      <BM>{tex`(1+r)^n \;=\; \frac{FN + A/r}{P + A/r}`}</BM>
      <BM>{tex`n \;=\; \frac{\displaystyle\ln\!\left(\frac{FN + A/r}{P + A/r}\right)}{\ln(1+r)}`}</BM>
      <p>
        Years to FIRE are therefore <IM>{tex`n/12`}</IM>. This is the exact closed-form
        solution for any combination of initial wealth and periodic contribution. The formula
        reduces to two limiting cases:
      </p>
      <ul>
        <li>
          <strong>A = 0 (no contributions, compounding only):</strong>{" "}
          <IM>{tex`n = \ln(FN/P) / \ln(1+r)`}</IM>
        </li>
        <li>
          <strong>r = 0 (zero return):</strong>{" "}
          <IM>{tex`n = (FN - P) / A`}</IM> months
        </li>
      </ul>
      <p>
        <strong>Note:</strong> the formula assumes monthly contributions that are constant in
        real terms. In practice contributions grow with career progression — this simplification
        is conservative (overestimates time needed).
      </p>

      {/* ───────────────────────── 8. SAVINGS RATE ───────────────────────── */}
      <h2>8. Savings rate and acceleration toward FIRE</h2>
      <p>
        The <strong>savings rate</strong> is the single most powerful variable in determining
        speed to FIRE. Defining net income as <IM>{tex`Y = C + S`}</IM> (expenses + monthly savings):
      </p>
      <BM>{tex`SR = \frac{S}{Y} = \frac{Y - C}{Y}`}</BM>
      <p>
        The impact of SR is twofold: a higher SR increases the monthly contribution{" "}
        <IM>{tex`A = S`}</IM> <strong>and simultaneously reduces expenses</strong>{" "}
        <IM>{tex`C`}</IM>, lowering the FIRE Number. Increasing SR from 20% to 50% can
        reduce time to FIRE from over 40 years to approximately 15–17 years (at constant
        real return), as demonstrated in the foundational studies of the{" "}
        <em>Mr. Money Mustache</em> blog (Adeney, 2012).
      </p>

      {/* ───────────────────────── 9. SORR ───────────────────────── */}
      <h2>9. Sequence of Returns Risk (SORR)</h2>
      <p>
        <strong>Sequence of Returns Risk</strong> is the phenomenon whereby the same average
        return produces radically different outcomes depending on the temporal order of returns.
        During the <em>withdrawal</em> (decumulation) phase, a sequence of negative returns in
        the early years of retirement is devastating: assets are sold at low prices to fund
        spending, permanently reducing the base that will benefit from the subsequent recovery.
      </p>
      <p>
        Formally, let <IM>{tex`W_t`}</IM> be wealth at year <IM>{tex`t`}</IM>:
      </p>
      <BM>{tex`W_{t+1} = W_t \cdot (1 + r_{t+1}) - C`}</BM>
      <p>
        If <IM>{tex`r_1 = -30\%`}</IM> (catastrophic year 1) followed by{" "}
        <IM>{tex`r = +8\%/\text{year}`}</IM> for 29 years, the portfolio behaves dramatically
        worse than the same scenario with reversed returns (first +8%, then −30%). The annual
        average is identical; the final outcome is completely different.
      </p>
      <p>SORR is the primary reason why:</p>
      <ul>
        <li>The 4% SWR is lower than the historical average real equity return (7–8% real).</li>
        <li>Maintaining a bond or cash allocation during the withdrawal phase is recommended.</li>
        <li>"Guardrail" and "bucket" strategies have been developed to mitigate its impact.</li>
      </ul>

      {/* ───────────────────────── 10. MONTE CARLO ───────────────────────── */}
      <h2>10. Monte Carlo simulation and Block Bootstrap</h2>
      <p>
        Monte Carlo simulations quantify the distribution of possible outcomes, overcoming the
        limitations of deterministic analysis. The AlphaFrame calculator implements the{" "}
        <strong>Block Bootstrap</strong> method developed and popularised by Karsten Jeske
        (ERN, 2018–2024).
      </p>
      <h3>10.1 Naive Monte Carlo (and why we don't use it)</h3>
      <p>
        The simplest approach is to sample returns from a normal distribution:
      </p>
      <BM>{tex`r_t \sim \mathcal{N}(\mu,\, \sigma^2)`}</BM>
      <p>
        The problem is that real equity returns <strong>are not Gaussian</strong>: they exhibit
        fat tails, negative skewness and short-term autocorrelation. A normal distribution
        significantly underestimates the probability of years like 2008 (−40%) or 2022 (−18%),
        which are critical for SORR modelling.
      </p>
      <h3>10.2 Block Bootstrap</h3>
      <p>
        Block Bootstrap solves these problems by sampling contiguous blocks of actual historical
        returns rather than synthetic ones. For each simulation:
      </p>
      <ol>
        <li>The historical series is divided into blocks of length <IM>{tex`L`}</IM>
          (in our case <IM>{tex`L = 3`}</IM> years).</li>
        <li>Blocks are sampled with replacement until the desired horizon is covered.</li>
        <li>The return sequence is applied to the portfolio, including monthly contributions.</li>
      </ol>
      <BM>{tex`W_{t+1} = W_t \cdot (1 + r_{t+1}^{*}) + A`}</BM>
      <p>
        where <IM>{tex`r_{t+1}^{*}`}</IM> is the bootstrapped return. The 3-year block length
        ensures that correlated crises (e.g. 2000–2002, 2008–2009) appear jointly in simulations,
        preserving the temporal autocorrelation structure.
      </p>
      <p>
        The dataset embedded in the AlphaFrame calculator comprises annual real returns of the
        MSCI World from <strong>1970 to 2024</strong> (55 observations), including: oil crisis
        (1973–1974), Black Monday (1987), dot-com crash (2000–2002), Great Recession (2008–2009),
        and bear market 2022. With <IM>{tex`N = 2{,}000`}</IM> simulations and 3-year blocks,
        the method captures both average volatility and the fat tails of global equity return
        distributions.
      </p>

      {/* ───────────────────────── 11. ASSUMPTIONS ───────────────────────── */}
      <h2>11. Model assumptions and limitations</h2>
      <p>
        Every FIRE model rests on a set of assumptions that are essential to understand before
        making important decisions:
      </p>
      <ul>
        <li>
          <strong>Constant real return</strong> — The deterministic model uses a fixed annual
          real return. Monte Carlo relaxes this assumption but uses historical data that may
          not replicate in the future (the "this time is different" problem).
        </li>
        <li>
          <strong>Constant real spending</strong> — In reality expenses change with age (declining
          in later years, increasing for healthcare). The model does not capture this dynamic.
        </li>
        <li>
          <strong>No tax modelling</strong> — In Italy capital gains on ETFs are taxed at 26%
          (with some exceptions). A nominal SWR of 3.5% becomes approximately 2.6% net of taxes —
          requiring a higher FIRE Number or higher gross SWR to compensate. In the UK,
          ISA wrappers allow tax-free growth; in the US, Roth accounts serve a similar role.
        </li>
        <li>
          <strong>No alternative income</strong> — State pension, rental income or part-time work
          can significantly reduce the FIRE Number. Ignoring them is conservative but not always
          realistic.
        </li>
        <li>
          <strong>Limited historical dataset</strong> — 55 years of MSCI World data are
          insufficient to precisely estimate fat tail behaviour. Block Bootstrap mitigates the
          issue but does not eliminate it.
        </li>
        <li>
          <strong>Uncertain horizon</strong> — Life expectancy cannot be known in advance. For a
          retirement at 40, a 60+ year horizon is realistic; the classic FIRE model (30 years)
          may be insufficient.
        </li>
      </ul>

      {/* ───────────────────────── 12. HOW TO USE ───────────────────────── */}
      <h2>12. How to use the AlphaFrame F.I.R.E. Calculator</h2>
      <p>
        The{" "}
        <a href="https://alphaweb-93f02.web.app/en/tools" target="_blank" rel="noopener noreferrer">
          F.I.R.E. Calculator
        </a>{" "}
        implements all the models discussed in this article. Follow these steps:
      </p>
      <ol>
        <li>
          <strong>Choose your currency</strong> — EUR, USD or GBP. The default inflation adjusts
          automatically (2% for EUR, 2.5% for USD/GBP).
        </li>
        <li>
          <strong>Enter your age and current wealth</strong> — Target age determines the
          horizon for the Coast FIRE Number.
        </li>
        <li>
          <strong>Estimate your expenses</strong> — Use direct entry or the category wizard
          (housing, food, transport, etc.) which automatically computes the annual total.
        </li>
        <li>
          <strong>Select your FIRE type</strong> — Regular, Lean, Fat, Barista or Coast.
          For Barista, enter expected monthly part-time income.
        </li>
        <li>
          <strong>Advanced parameters</strong> — Adjust SWR (default 3.5%), expected real
          return (default 5%) and inflation if you have different estimates.
        </li>
        <li>
          <strong>Run the Monte Carlo</strong> — The Block Bootstrap generates 2,000 possible
          paths. The chart shows the 5th, 25th, 50th, 75th and 95th percentiles of wealth
          over time.
        </li>
      </ol>
      <p>
        Results display FIRE Number, estimated years to FIRE, projected FIRE age and implied
        savings rate, alongside a comparative table of all five FIRE variants.
      </p>

      {/* ───────────────────────── 13. BIBLIOGRAPHY ───────────────────────── */}
      <h2>13. References</h2>
      <ul>
        <li>Bengen, W. P. (1994). "Determining Withdrawal Rates Using Historical Data."
          <em> Journal of Financial Planning</em>, 7(4), 171–180.</li>
        <li>Cooley, P. L., Hubbard, C. M., &amp; Walz, D. T. (1998). "Retirement Savings:
          Choosing a Withdrawal Rate That Is Sustainable."
          <em> AAII Journal</em>, 20(2), 16–21. [Trinity Study]</li>
        <li>Cooley, P. L., Hubbard, C. M., &amp; Walz, D. T. (2011). "Portfolio Success Rates:
          Where to Draw the Line."
          <em> Journal of Financial Planning</em>, 24(4), 48–60.</li>
        <li>Pfau, W. D. (2011). "Safe Savings Rates: A New Approach to Retirement Planning
          Over the Life Cycle."
          <em> Journal of Financial Planning</em>, 24(5), 42–50.</li>
        <li>Estrada, J. (2020). "Managing to Target: Dynamic Asset Allocation for Goal-Based
          Investing." <em>Journal of Financial Planning</em>, 33(2), 38–50. See also:
          Estrada, J. (2018). "Replacing the Failure Rate: A New Success Metric for
          Retirement Portfolios." <em>Journal of Wealth Management</em>.</li>
        <li>Karsten, K. (ERN). (2018–2024). "Safe Withdrawal Rate Series."
          <em> Early Retirement Now Blog</em>.{" "}
          <a href="https://earlyretirementnow.com/safe-withdrawal-rate-series/" target="_blank" rel="noopener noreferrer">
            earlyretirementnow.com
          </a></li>
        <li>Shiller, R. J. (1981). "Do Stock Prices Move Too Much to Be Justified by
          Subsequent Changes in Dividends?" <em>American Economic Review</em>, 71(3), 421–436.
          [CAPE ratio]</li>
        <li>Adeney, P. (Mr. Money Mustache). (2012). "The Shockingly Simple Math Behind
          Early Retirement." <em>mrmoneymustache.com</em>.</li>
      </ul>

      {/* ───────────────────────── 14. DISCLAIMER ───────────────────────── */}
      <h2>Disclaimer and warnings</h2>
      <p>
        <strong>This article and the associated calculator are for educational and illustrative
        purposes only.</strong> The models presented are based on historical data and assumptions
        that may not replicate in the future. Past performance is not a guarantee of future results.
        The FIRE Number computed does not constitute an investment recommendation or personalised
        financial, legal or tax advice.
      </p>
      <p>
        Applying FIRE models to one's own financial situation requires the evaluation of individual
        variables — time horizon, risk tolerance, tax position, pension entitlements, family
        obligations — that lie beyond the scope of any automated simulation. Retirement planning
        is a complex subject; before making material decisions it is advisable to consult a
        qualified independent financial adviser.
      </p>
      <p>
        AlphaFrame Analytics accepts no liability for decisions made on the basis of the
        calculator's outputs. Monte Carlo simulations are probabilistic tools: even a 95th
        percentile outcome may prove insufficient in historically unprecedented scenarios.
      </p>
    </div>
  ),
};

// ─────────────────────────────────────────────────────────────────────────────
// ARTICLE: Compound interest and Italian taxation
// ─────────────────────────────────────────────────────────────────────────────

const compoundArticleEN = {
  slug: "compound-interest-italian-taxation-guide",
  title: "Compound Interest and Italian Taxation: The Complete Guide with Real Numbers",
  metaDescription:
    "Complete guide to compound interest in the Italian financial context: mathematical formula, Deposit Account vs BTP vs ETF comparison, 26% and 12.5% tax rates, administered regime, and numerical simulations over 5–20 year horizons.",
  tags: ["education", "finance", "etf", "quant"],
  readTime: "12 min",
  author: "AlphaFrame Editorial",
  datePublished: "2026-04-16",
  dateUpdated: "2026-09-29",
  coverImage: IndFinCover,
  canonical: `${DOMAIN}/en/kb/compound-interest-italian-taxation-guide`,
  content: (
    <div>
      <h1>Compound Interest and Italian Taxation: The Complete Guide with Real Numbers</h1>

      <p>
        Compound interest is the most silent force in personal finance. Albert Einstein —
        according to an apocryphal but effective quote — reportedly called it "the eighth
        wonder of the world." Regardless of the attribution, the concept is real and
        measurable: every euro of return reinvested generates further return, creating
        exponential growth over time. This guide analyses compound interest in the specific
        Italian context, where differentiated taxation across financial instruments
        significantly changes the equation. You can explore all calculations interactively
        with the{" "}
        <a href="https://alphaweb-93f02.web.app/en/tools" target="_blank" rel="noopener noreferrer">
          AlphaFrame Goal Planner
        </a>.
      </p>

      <h2>1. The Mathematics of Compound Interest</h2>
      <p>
        The future value formula for a capital with periodic contributions is the foundation of
        every financial planning analysis. For an initial capital{" "}
        <BM>{tex`P`}</BM>, monthly contribution{" "}
        <BM>{tex`A`}</BM>, monthly rate{" "}
        <BM>{tex`r = r_{\text{annual}}/12`}</BM> and a horizon of{" "}
        <BM>{tex`n`}</BM> months, the gross final value is:
      </p>
      <BM>{tex`FV = P \cdot (1+r)^n + A \cdot \frac{(1+r)^n - 1}{r}`}</BM>
      <p>
        The first term is the compounding of the initial capital; the second is the future
        value of the contribution series (ordinary annuity). This formula assumes a constant
        rate — a simplification that in practice must be adjusted for the fact that returns
        vary year to year.
      </p>
      <p>
        The monthly rate <IM>{tex`r`}</IM> is derived from the gross annual return{" "}
        <IM>{tex`r_{\text{gross}}`}</IM> net of annual costs{" "}
        <IM>{tex`c`}</IM>:
      </p>
      <BM>{tex`r = \frac{r_{\text{gross}} - c}{12 \cdot 100}`}</BM>
      <p>
        The separation between gross return and cost is critical: a fund with 7% gross return
        and 1.5% costs is not equivalent to a fund at 5.5% gross — the difference compounds
        over time producing significant gaps on long horizons.
      </p>

      <h2>2. The Italian Administered Regime and Taxation</h2>
      <p>
        Taxation of financial instruments in Italy is governed by the{" "}
        <strong>Testo Unico delle Imposte sui Redditi (TUIR)</strong> and distinguishes two
        major categories of financial income: <em>capital income</em> (interest, dividends)
        and <em>diverse income</em> (capital gains from disposal). Under the administered
        regime — the typical regime for retail investors using a bank as intermediary —
        taxes are applied and paid automatically by the bank, without the need for self-assessment
        on these incomes.
      </p>
      <p>
        The currently applicable rates are:
      </p>
      <ul>
        <li>
          <strong>26%</strong> on proceeds from harmonised ETFs, equities, mutual funds and
          deposit accounts: the standard rate for most financial instruments.
        </li>
        <li>
          <strong>12.5%</strong> on proceeds from Italian government bonds (BTP, BOT, CTZ,
          CCT) and equivalent EU/OECD whitelist sovereign bonds: the preferential rate
          reflects the public debt financing function.
        </li>
      </ul>
      <p>
        One important peculiarity of the administered regime: <strong>losses from diverse
        income cannot offset gains from capital income</strong>. Losses realised on ETFs
        (diverse income) cannot offset gains from dividends or coupons (capital income). This
        fiscal asymmetry is one reason why asset allocation planning must account for the
        tax structure of instruments.
      </p>
      <p>
        For the Goal Planner, taxation is applied in a simplified way that follows when the tax
        is actually due: for the deposit account, 26% is withheld on interest as it accrues; for
        the fixed-rate BTP, 12.5% is withheld on coupons when paid (bought at par and held to
        maturity); for the accumulating ETF, 26% applies only to the gain at sale
        (<IM>{tex`FV_{\text{gross}} - \text{total invested}`}</IM>), since there are no
        intermediate distributions and the tax is effectively deferred.
      </p>

      <h2>3. Three Scenarios Compared: Real Numbers</h2>
      <p>
        Let's compare the three most accessible instruments for Italian investors over a
        10-year horizon, with an initial capital of €10,000 and a monthly contribution of €300.
      </p>

      <h3>Fixed-term deposit account</h3>
      <p>
        Fixed-term deposits currently offer gross returns in the 3.0–4.5% range. Costs are
        limited: on time deposits stamp duty is 0.20% a year; on demand deposit accounts the fixed
        €34.20/year duty applies above €5,000 average balance (about 0.10% on €34,000, the value
        used as the calculator's default assumption). Taxation is 26% on interest, withheld as it
        accrues. Key feature:{" "}
        <strong>no market risk</strong> — nominal capital is guaranteed within FITD limits
        (€100,000 per depositor per bank).
      </p>

      <h3>BTP and fixed-rate Italian government bonds</h3>
      <p>
        Italian government bonds (BTPs) offer yields to maturity around 3.5–4.5% gross
        (October 2025) with the preferential 12.5% tax rate. Compared to deposit accounts,
        the tax advantage is significant: on a €10,000 capital gain the difference between
        26% and 12.5% is €1,350 less in taxes. The risk is <em>interest rate risk</em>:
        if rates rise after purchase, the BTP's market value falls. For buy-and-hold investors,
        this risk does not materialise. Costs include brokerage commissions and securities
        stamp duty (0.20% annually of the position value).
      </p>

      <h3>Global equity ETF</h3>
      <p>
        ETFs tracking global indices such as MSCI World or FTSE All-World have produced
        historical gross nominal returns around 9–10% annually over the past 50 years
        (source: MSCI). Net of inflation (2.0–2.5%), the real return was approximately
        6–7%. TERs on index ETFs have fallen dramatically in the last decade: the most
        efficient ETFs stand at 0.07–0.20%. Adding stamp duty (0.20%) and annualised broker
        commissions (0.05–0.15%), total cost of ownership reaches 0.30–0.55%.
      </p>
      <p>
        Numerical example over 10 years, capital €10,000, monthly PAC €300 (total invested €46,000;
        deposit and BTP with tax withheld every year, ETF with tax at sale):
      </p>
      <ul>
        <li>Deposit account 3.0% gross, costs 0.10%: net final approximately <strong>€52,500</strong></li>
        <li>BTP 4.2% gross, costs 0.25%: net final approximately <strong>€57,100</strong></li>
        <li>ETF 7.0% gross, costs 0.40%: net final approximately <strong>€64,000</strong></li>
      </ul>
      <p>
        The difference between BTP and ETF in this scenario is approximately €7,000 over
        10 years, generated by the combination of a higher assumed gross return and compounding
        of the differential. The comparison uses constant returns, however: it does not show the
        risk, which is much higher for an equity ETF and can mean losses lasting years.
      </p>

      <h2>4. The Cost of Costs: Compounded Drag</h2>
      <p>
        Costs are not linear: they multiply on compounded returns and create a{" "}
        <em>compounded drag</em>. Formally, each year the capital grows by{" "}
        <IM>{tex`(1 + r_{\text{gross}})`}</IM> but must also absorb the cost{" "}
        <IM>{tex`c`}</IM>, reducing the effective accumulation rate to{" "}
        <IM>{tex`(1 + r_{\text{gross}} - c)`}</IM>.
      </p>
      <p>
        Over 20 years, the difference between an ETF with TER 0.20% and one with TER 1.50%
        on a capital of €50,000 without further contributions (7% return, monthly compounding) is
        approximately <strong>€44,000</strong>. TER is not the only cost to consider: tracking error (deviation
        from the benchmark), transaction commissions and bid-ask spread all contribute to the
        Total Cost of Ownership (TCO).
      </p>
      <BM>{tex`\text{Cost drag} = FV(r_{\text{gross}}) - FV(r_{\text{gross}} - c)`}</BM>
      <p>
        Where <IM>{tex`FV(r)`}</IM> is the future value computed at rate <IM>{tex`r`}</IM>.
        The drag grows with the square of time for long horizons, making cost minimisation one
        of the most important factors in long-term planning.
      </p>

      <h2>5. Taxation and Time Horizon: Interaction</h2>
      <p>
        A rarely discussed property of realisation-based taxation is its{" "}
        <strong>time leverage effect</strong>. Since tax is paid only at final sale, all
        intermediate gains compound tax-free during the horizon. This creates an implicit
        advantage over instruments where taxation occurs annually (such as deposit accounts,
        where tax on interest is applied each year).
      </p>
      <p>
        Comparison over 20 years between annual and realisation-based taxation, same gross
        return 5%, same initial capital €20,000:
      </p>
      <ul>
        <li>Annual taxation at 26%: effective net rate = 5% × (1 − 0.26) = <strong>3.70%</strong>/year</li>
        <li>Realisation-based taxation: 5% gross compounds for 20 years, tax paid only at end</li>
      </ul>
      <p>
        The difference between the two approaches on €20,000 over 20 years is approximately
        €3,100 in favour of deferred taxation — even at the same 26% rate. This partly explains
        the preference for <em>accumulating</em> ETFs (which reinvest dividends without
        distributing them) over <em>distributing</em> ETFs, which generate dividends taxed
        annually.
      </p>

      <h2>6. Planning Concrete Goals: Home, University, Emergency</h2>
      <p>
        Compound interest is not just an abstract concept: it is the tool for planning concrete
        financial goals with a defined date and amount.
      </p>
      <h3>House down payment (7 years, target €60,000)</h3>
      <p>
        To accumulate €60,000 net in 7 years starting from €5,000 initial capital, the required
        monthly contribution varies significantly by scenario:
      </p>
      <ul>
        <li>Deposit account 3.0%: approximately <strong>€598/month</strong></li>
        <li>BTP 4.2%: approximately <strong>€565/month</strong></li>
        <li>ETF 7.0%: approximately <strong>€524/month</strong></li>
      </ul>
      <p>
        Under constant-return assumptions, the €74/month difference between deposit and ETF over
        84 months equals about €6,200 in additional contributions to reach the same goal — about
        10 deposit instalments. Over a 7-year horizon, however, an equity ETF may be at a loss
        right at maturity: the comparison does not measure this risk.
      </p>
      <h3>University fund for a child (15 years, target €50,000)</h3>
      <p>
        Over a 15-year horizon the compounding effect is far more pronounced. With initial
        capital €3,000 and net target €50,000:
      </p>
      <ul>
        <li>Deposit account: approximately <strong>€217/month</strong></li>
        <li>BTP: approximately <strong>€191/month</strong></li>
        <li>ETF: approximately <strong>€158/month</strong></li>
      </ul>
      <p>
        The €59/month difference between deposit and ETF over 180 months amounts to about €10,600
        fewer contributions with the ETF for the same final outcome, again with constant returns
        and without accounting for the different level of risk.
      </p>

      <h2>7. Inflation and Real Return</h2>
      <p>
        All the numbers presented above are <em>nominal</em>: they do not account for the
        loss of purchasing power caused by inflation. The real return is approximated by:
      </p>
      <BM>{tex`r_{\text{real}} \approx r_{\text{nominal}} - \pi`}</BM>
      <p>
        Where <IM>{tex`\pi`}</IM> is the expected inflation rate (2.0–2.5% for the euro area
        in the medium term according to ECB projections). With a global ETF at 7% gross nominal
        and 2.5% inflation, the expected real return is approximately 4.5% before costs.
      </p>
      <p>
        The practical implication is that setting a nominal target today (e.g. €60,000 for a
        house in 7 years) means accepting that the real value of that capital will be
        approximately 84% of €60,000 in today's euros
        (<IM>{tex`60{,}000 / (1.025)^7 \approx 50{,}500`}</IM>). To plan in real terms,
        the nominal target should be uplifted by expected inflation: €60,000 × (1.025)⁷ ≈{" "}
        <strong>€71,300</strong>.
      </p>

      <h2>8. How to Use the Goal Planner</h2>
      <p>
        The <a href="https://alphaweb-93f02.web.app/en/tools" target="_blank" rel="noopener noreferrer">
          AlphaFrame Goal Planner
        </a> applies the mathematics described in this article interactively:
      </p>
      <ol>
        <li>
          <strong>Choose a preset</strong> (Home, Education, Wedding, Car, Emergency) or enter
          a custom amount and horizon.
        </li>
        <li>
          <strong>Select the mode:</strong>
          <ul>
            <li>"Given my contribution" → see what you accumulate in each scenario</li>
            <li>"Given my target" → see the required monthly contribution per scenario</li>
          </ul>
        </li>
        <li>
          <strong>Advanced settings</strong>: adjust the assumed gross return and costs for each
          scenario; enable inflation adjustment to see the real value of the final capital.
        </li>
        <li>
          <strong>Read the table</strong>: the "Taxes paid" row shows the fiscal cost per
          scenario. The comparison box summarises the difference in monthly contribution (or final
          capital) between the best and worst scenario under the assumptions set, noting that the
          comparison does not measure risk.
        </li>
      </ol>
      <p>
        Note: the calculator uses monthly compound rate to simulate monthly contributions (PAC),
        applies tax every year on deposit interest and BTP coupons and, for the accumulating ETF,
        only on the gain at sale (administered regime); it does not model distributing ETFs. For an
        analysis including stochastic simulations over a retirement horizon, use the{" "}
        <a href="https://alphaweb-93f02.web.app/en/tools" target="_blank" rel="noopener noreferrer">
          FIRE Calculator
        </a>.
      </p>

      <h2>9. Model Limitations and Assumptions</h2>
      <ul>
        <li>
          <strong>Constant return:</strong> the model uses a fixed rate per scenario. In
          reality, market returns vary year to year (volatility). For stochastic scenarios,
          the FIRE Calculator with Block Bootstrap is more appropriate.
        </li>
        <li>
          <strong>Simplified taxation:</strong> annual tax on interest and coupons for the deposit
          and the BTP (bought at par and held to maturity), tax on the total gain at sale for the
          ETF. For partial sales, BTPs bought above or below par or distributing ETFs, the fiscal
          logic is more complex.
        </li>
        <li>
          <strong>Fixed costs:</strong> annual costs are assumed constant. In reality, ETF TERs
          can change over time (trending lower); brokerage commissions depend on the chosen
          platform.
        </li>
        <li>
          <strong>Inflation:</strong> the real value shown is an approximation. Actual inflation
          depends on personal spending basket, not the general IPCA/CPI index.
        </li>
        <li>
          <strong>Risk:</strong> ETFs and BTPs carry market risks not captured by the
          deterministic model. A stock market crash in the liquidation year can significantly
          reduce the final value versus the projection.
        </li>
      </ul>

      <h2>Bibliography</h2>
      <ol>
        <li>
          Brealey, R., Myers, S., Allen, F. (2020). <em>Principles of Corporate Finance</em>,
          13th ed. McGraw-Hill. — Chapter on the time value of money.
        </li>
        <li>
          Agenzia delle Entrate (2024). <em>Capital income and diverse income of a financial
          nature: guide to taxation</em>. Circolare n. 165/E.
        </li>
        <li>
          ESMA (2022). <em>Costs and Performance of EU Retail Investment Products</em>.
          European Securities and Markets Authority.
        </li>
        <li>
          Vanguard Research (2023). <em>Putting a Value on Your Value: Quantifying Vanguard
          Advisor's Alpha</em>. Vanguard Group.
        </li>
        <li>
          Bogle, J.C. (2010). <em>Common Sense on Mutual Funds</em>. John Wiley & Sons.
        </li>
        <li>
          MSCI (2024). <em>MSCI World Index Performance Analytics 1970–2023</em>. MSCI Inc.
        </li>
      </ol>

      <h2>Legal Disclaimer</h2>
      <p>
        The contents of this article are for exclusively educational and informational purposes.
        The returns and calculations presented are based on simplified assumptions and historical
        data that do not guarantee future results. The taxation described reflects Italian
        regulations in force at the time of publication and may be subject to legislative changes.
      </p>
      <p>
        This article does not constitute personalised financial advice, an investment
        recommendation, or a solicitation to buy or sell financial instruments within the
        meaning of D.Lgs. 58/1998 (TUF) and MiFID II Directive. Numerical simulations are
        hypothetical scenarios prepared for illustrative purposes only.
      </p>
      <p>
        Before making significant investment decisions, it is advisable to consult a qualified
        independent financial adviser who can consider your individual tax situation, financial
        position and personal objectives. AlphaFrame Analytics accepts no liability for
        decisions made on the basis of this article or the calculator's outputs.
      </p>
    </div>
  ),
};

// ─────────────────────────────────────────────────────────────────────────────
// NEW KB ARTICLES — batch: Derivatives cluster (pillar + 3 spokes) + Metrics + Market Update
// (Series index page intentionally NOT created: parked.)
// ─────────────────────────────────────────────────────────────────────────────
const KB_DISC = {
  fontSize: "0.9rem",
  color: "#475569",
  borderLeft: "3px solid #cbd5e1",
  padding: "0.5rem 0 0.5rem 0.85rem",
  margin: "0 0 1.25rem",
};
const KB_FIG = { margin: "1.5rem 0" };
const KB_IMG = { width: "100%", height: "auto", borderRadius: "10px", border: "1px solid #e2e8f0" };
const KB_CAP = { fontSize: "0.88rem", color: "#475569", marginTop: "0.5rem", lineHeight: 1.5 };

const newKbArticlesEN = [
  {
    slug: "derivati-spiegati-semplice",
    title: "Derivatives: what they are and how they work (complete guide)",
    metaDescription:
      "What financial derivatives are, how forwards, futures, options and swaps work, the difference between regulated and OTC markets, and the three purposes: hedging, speculation, arbitrage. Educational guide with video.",
    tags: ["derivatives", "futures", "options", "education", "markets"],
    readTime: "11 min",
    author: "Redazione",
    datePublished: "2026-06-25",
    dateUpdated: "2026-10-15",
    coverImage: CoverDerivatiPillar,
    canonical: `${DOMAIN}/en/kb/derivati-spiegati-semplice`,
    content: (
      <div>
        <h1>Derivatives: what they are and how they work (complete guide)</h1>

        <p style={KB_DISC}>
          Educational and informational content only. Not personalized advice, a recommendation, or a
          solicitation. Derivatives are complex instruments and can lead to losses exceeding the
          capital invested.
        </p>

        <h2>Introduction: what "derivative" means</h2>
        <p>
          A derivative is a contract whose value <strong>derives</strong> from the movement of
          something else, the <em>underlying</em>: a stock, an index, a commodity, an interest rate, a
          currency. You don't buy the asset itself: you buy (or sell) an agreement about how its price
          will behave by a given date. This is what every derivative — forward, future, option, swap —
          has in common, beyond the technical differences.
        </p>
        <p>
          Why do they exist? Originally for a single reason: to <strong>transfer risk</strong> from
          someone who doesn't want it to someone willing to take it on. The farmer who locks in today
          the price of next season's wheat is protected against a fall; the party on the other side
          takes on that risk. From this core come the three uses we'll cover: hedging, speculation,
          arbitrage.
        </p>

        <YouTubeEmbed
          videoId="L-JmzID5D0U"
          title="Derivatives made simple: hedging, speculation, arbitrage"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <YouTubeEmbed
          playlistId="PL4Ivcohv28tLV6Sd958sbo8rER0-onx5I"
          title="Derivatives — the complete series (playlist)"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />
        <p>
          Every published episode, each with a companion note (key concepts, worked example,
          risks), is on the <a href="/en/series/derivati">Derivatives series page</a>.
        </p>

        <h2>The four building blocks</h2>
        <ul>
          <li>
            <strong>Forward</strong> — a private agreement to buy/sell the underlying at a price and
            date fixed today. Flexible but "bespoke": you pay for it in counterparty risk. How its price
            is set → see <a href="/en/series/derivati#ep-5">episode 5, the cost of carry</a>.
          </li>
          <li>
            <strong>Future</strong> — the <em>standardized</em>, exchange-traded forward, with a
            clearing house that steps in and removes the risk of the counterparty failing to pay. →
            see <a href="/en/kb/derivati-futures-come-funzionano">Futures — Part 1: how they work</a>.
          </li>
          <li>
            <strong>Option</strong> — gives the <em>right</em>, not the obligation, to buy (call) or
            sell (put) the underlying at a preset price. The buyer pays a premium; the seller collects
            the premium but takes on the obligation. → see{" "}
            <a href="/en/series/derivati#ep-8">episodes 8, 9 and 10</a> of the series.
          </li>
          <li>
            <strong>Swap</strong> — an exchange of cash flows over time (e.g. fixed vs floating rate,
            or two currencies). It's the building block under much of corporate and bank finance. → see{" "}
            <a href="/en/series/derivati#ep-7">episode 7</a>.
          </li>
        </ul>

        <h2>Where they trade: regulated markets vs OTC</h2>
        <ul>
          <li>
            <strong>Regulated market (exchange):</strong> standardized contracts, transparent prices,
            a clearing house guaranteeing positions. This is the world of futures and many options.
          </li>
          <li>
            <strong>OTC (over-the-counter):</strong> bilateral, bespoke contracts negotiated
            off-exchange. Maximum flexibility, but the risk of the counterparty defaulting is on you —
            the lesson of Lehman Brothers (2008) was born exactly here. Covered in{" "}
            <a href="/en/series/derivati#ep-1">episode 1</a> of the series.
          </li>
        </ul>

        <h2>What they're actually for: the three purposes</h2>
        <ol>
          <li>
            <strong>Hedging</strong> — reducing a risk you already have. Example: a European exporter
            who'll receive dollars in six months can lock in the exchange rate today with a forward,
            removing currency uncertainty. (Same mechanism behind "hedged" ETFs → see{" "}
            <a href="/en/kb/currency-hedging-hedged-vs-unhedged-etfs-and-how-to-analyze-them">
              Currency hedging: hedged vs unhedged ETFs
            </a>
            .)
          </li>
          <li>
            <strong>Speculation</strong> — taking on risk to seek a profit from an expected move.
            Leverage amplifies gains <em>and</em> losses: this is where derivatives become dangerous
            if used without understanding them.
          </li>
          <li>
            <strong>Arbitrage</strong> — exploiting price misalignments across markets for a
            theoretically risk-free profit. In practice the windows are tiny and close fast.
          </li>
        </ol>

        <h2>Derivatives for the retail saver: certificates and covered warrants</h2>
        <p>
          On the retail market, derivatives arrive "packaged": <strong>certificates</strong> and{" "}
          <strong>covered warrants</strong> are securitized instruments issued by banks, with very
          different risk, tax and efficiency profiles. Understand them before touching them. → see{" "}
          <a href="/en/kb/certificati-covered-warrant">
            Certificates and covered warrants: what they are and how they're taxed
          </a>
          .
        </p>

        <h2>Mistakes and risks to know</h2>
        <ul>
          <li>
            <strong>Leverage</strong> = a small move in the underlying becomes a large move in your
            capital, in both directions.
          </li>
          <li>
            <strong>Counterparty risk</strong> (especially OTC) = the contract is worth as much as
            whoever sold it to you.
          </li>
          <li>
            <strong>Complexity</strong> = a misunderstood derivative is a misunderstood risk.
            Educational rule: if you can't explain its payoff, you're not ready to use it.
          </li>
        </ul>

        <h2>Conclusion</h2>
        <p>
          Derivatives are neither good nor bad: they're risk-transfer tools. The same future that
          protects a company can ruin a leveraged speculator. The difference is understanding.
        </p>

        <h3>Continue the derivatives series</h3>
        <ul>
          <li>
            <a href="/en/series/derivati">The full video series</a>, with a note for every episode
          </li>
          <li>
            <a href="/en/kb/derivati-concetti-chiave">The eight key steps of the series</a>, from
            futures to Black-Scholes
          </li>
          <li>
            <a href="/en/kb/derivati-futures-come-funzionano">Futures — Part 1: how they work</a>{" "}
            (margins, mark-to-market, leverage)
          </li>
          <li>
            <a href="/en/kb/derivati-futures-coperture-hedge-ratio">
              Futures — Part 2: hedging properly
            </a>{" "}
            (hedge ratio and basis risk)
          </li>
          <li>
            <a href="/en/kb/certificati-covered-warrant">Certificates and covered warrants</a>
          </li>
        </ul>

        <h2>Sources</h2>
        <p>
          Borsa Italiana — Glossary · CONSOB — derivatives education · J. Hull, <em>Options, Futures
          and Other Derivatives</em> · BIS — OTC market statistics.
        </p>

        <p style={KB_DISC}>
          This information is for educational purposes only. It is not a recommendation or
          personalized advice. Past performance does not guarantee future results.
        </p>
      </div>
    ),
  },

  {
    slug: "derivati-futures-come-funzionano",
    title: "Futures — Part 1: how they work (margins, mark-to-market, delivery)",
    metaDescription:
      "How a future works: standardization, margin as a performance bond, daily mark-to-market, leverage (real example) and where the price comes from (cash-and-carry). Part 1 of 2.",
    tags: ["derivatives", "futures", "margin", "leverage", "education"],
    readTime: "10 min",
    author: "Redazione",
    datePublished: "2026-06-25",
    dateUpdated: "2026-06-25",
    coverImage: CoverFutures1,
    canonical: `${DOMAIN}/en/kb/derivati-futures-come-funzionano`,
    content: (
      <div>
        <h1>Futures — Part 1: how they work (margins, mark-to-market, delivery)</h1>

        <p style={KB_DISC}>
          Educational content. Futures are leveraged instruments: they can cause losses exceeding the
          capital posted. Not a recommendation.
        </p>

        <p>
          ⟵ Start from the <a href="/en/kb/derivati-spiegati-semplice">derivatives pillar</a> · Go to{" "}
          <a href="/en/kb/derivati-futures-coperture-hedge-ratio">Part 2: hedging with futures</a> ⟶
        </p>

        <h2>Not an investment, a tool</h2>
        <p>
          A derivative isn't a "primary" investment: it's a <strong>tool</strong> for three purposes —
          exposure, risk transfer, leverage. The future is the most linear of these tools, and
          understanding its mechanics is the prerequisite for everything else.
        </p>

        <YouTubeEmbed
          videoId="oR3rjG_maCI"
          title="Futures mechanics: margins, mark-to-market, delivery"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <h2>Standardization = liquidity + operational efficiency</h2>
        <p>
          A future is a <strong>standardized</strong> forward: contract size, delivery (or cash
          settlement) and expiry are fixed. That uniformity is what makes it tradable on organized
          exchanges (CME, CBOE, CBT) with <strong>scale and liquidity</strong>. For large portfolios
          it's a huge advantage: to adjust a fund's equity exposure, instead of moving thousands of
          individual stock positions, you use a future — <strong>same exposure, minimal transaction
          costs, same-day settlement</strong>, without touching long-term holdings. It's "portfolio
          engineering": you tune duration or beta without dismantling anything.
        </p>

        <h2>Mutual obligation (and why mark-to-market is needed)</h2>
        <p>
          The key difference of futures versus options is one word: <strong>obligation</strong>. In a
          future <em>both</em> parties are bound — the buyer to take delivery/settle, the seller to
          deliver/settle. This mutual bond makes <strong>default risk</strong> central, and it's
          managed with <strong>daily mark-to-market</strong>: gains and losses are realized and
          transferred between parties <em>every day</em>. If the market moves against you, you post
          funds immediately; there's no debt quietly accumulating until expiry.
        </p>

        <h2>Margin as a "performance bond", and leverage</h2>
        <p>
          Opening a position, you don't post the full contract value but an{" "}
          <strong>initial margin</strong>: collateral, typically <strong>2-10%</strong> of value.
          That's where leverage is born. Example: a <strong>$200,000</strong> contract with{" "}
          <strong>$6,000</strong> margin → a <strong>1%</strong> move in the underlying (±$2,000) is
          about <strong>±17-18% on margin</strong>; the leverage factor can exceed <strong>35:1</strong>.
          Enormous capital efficiency, on one condition: daily monitoring of cash flows. The moment
          you fail a <strong>margin call</strong>, the position is force-liquidated, no questions
          asked.
        </p>

        <h2>Where the price comes from: the cost of carry</h2>
        <p>A future's price isn't arbitrary: it's the <strong>cost of carrying</strong> the asset to expiry.</p>
        <p><code>F = S + interest (opportunity cost) − income (dividends/coupons)</code></p>
        <p>
          If the cost of carry is positive, the future trades <em>above</em> spot (a situation called{" "}
          <strong>contango</strong>). Keeping the price aligned is <strong>cash-and-carry
          arbitrage</strong>: if the future is too expensive, a trader buys the asset spot, sells the
          future and finances the purchase at the risk-free rate, locking in a sure profit until
          pressure pushes prices back to equilibrium. This mechanism, active every second, prevents
          "free" profit and makes the market efficient.
        </p>

        <h2>Cost of carry by asset class</h2>
        <p>The general formula holds, but it changes with the type of underlying:</p>
        <ul>
          <li>
            <strong>Equity indices:</strong> <code>F = index + interest − expected dividends</code> —
            the basket's dividends reduce the cost of carry and can push the future <em>below</em>
            spot.
          </li>
          <li>
            <strong>Government bonds:</strong> the <em>cheapest-to-deliver</em> logic kicks in — among
            deliverable bonds, the seller picks the most convenient one, and that drives the future's
            price.
          </li>
          <li>
            <strong>Currencies:</strong> the carry is the <em>interest-rate differential</em> between
            the two currencies (covered interest-rate parity): the FX future prices in the rate
            advantage or disadvantage.
          </li>
          <li>
            <strong>Commodities:</strong> on top of interest you add storage costs and any{" "}
            <em>convenience yield</em> from holding the physical good.
          </li>
        </ul>

        <h2>The margin call, in practice</h2>
        <p>
          Back to the example: a $200,000 contract, $6,000 initial margin. The broker also sets a{" "}
          <strong>maintenance margin</strong> (say $4,500). If a few bad sessions erode the margin
          below that threshold — say to $4,200 — a <strong>margin call</strong> fires: you must bring
          the account back to the initial margin (post about $1,800), <em>immediately</em>. If you
          don't, the position is force-closed at the market price, crystallizing the loss. That's why
          leverage demands <strong>reserve liquidity</strong>, not just a correct thesis: you can be
          right over the long run and still be liquidated in the short.
        </p>

        <h2>In short</h2>
        <p>
          Standardization (liquidity + efficiency), mutual obligation (managed via mark-to-market),
          margin as collateral that creates <strong>leverage</strong> (and a margin call that
          liquidates), price = cost of carry. Now that you know <em>how</em> it works, in{" "}
          <a href="/en/kb/derivati-futures-coperture-hedge-ratio">Part 2</a> we look at the use that
          matters: <strong>hedging a risk</strong> — and why no hedge is perfect.
        </p>

        <h2>Sources</h2>
        <p>
          J. Hull, <em>Options, Futures and Other Derivatives</em> · CME Group — education · Borsa
          Italiana — glossary.
        </p>

        <p style={KB_DISC}>
          Educational content, not a recommendation. Leverage can cause losses exceeding capital.
        </p>
      </div>
    ),
  },

  {
    slug: "derivati-futures-coperture-hedge-ratio",
    title: "Futures — Part 2: hedging properly (hedge ratio and basis risk)",
    metaDescription:
      "Hedging with futures: basis and convergence, the minimum-variance hedge ratio, cross-hedging, synthetic control of beta and duration, and the choice between stack and strip hedge. Part 2 of 2.",
    tags: ["derivatives", "futures", "hedging", "risk", "education"],
    readTime: "11 min",
    author: "Redazione",
    datePublished: "2026-06-25",
    dateUpdated: "2026-06-25",
    coverImage: CoverFutures2,
    canonical: `${DOMAIN}/en/kb/derivati-futures-coperture-hedge-ratio`,
    content: (
      <div>
        <h1>Futures — Part 2: hedging properly (hedge ratio and basis risk)</h1>

        <p style={KB_DISC}>
          Educational content. Hedging reduces one risk but introduces others (basis, leverage). Not a
          recommendation.
        </p>

        <p>
          ⟵ <a href="/en/kb/derivati-futures-come-funzionano">Part 1: how futures work</a> · Back to
          the <a href="/en/kb/derivati-spiegati-semplice">pillar</a>
        </p>

        <h2>Hedging transforms risk, it doesn't erase it</h2>
        <p>
          In <a href="/en/kb/derivati-futures-come-funzionano">Part 1</a> we saw <em>how</em> a future
          works. Here, the use that matters: <strong>hedging</strong>. When you hedge an exposure with
          an opposite futures position, you <strong>lock in</strong> a future selling price equal to{" "}
          <em>the initial futures price + the ending basis</em>. In other words: you don't zero the
          risk, you <strong>transform</strong> it from full price risk (unmanageable) into{" "}
          <strong>basis risk</strong> (small and manageable).
        </p>

        <YouTubeEmbed
          videoId="d4CZDJQc0pY"
          title="Hedging properly with futures: hedge ratio and basis risk"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <h2>Basis and convergence</h2>
        <p>
          The <strong>basis</strong> is the difference between spot and futures price:{" "}
          <code>basis = S − F</code>. It's not random: it's the <strong>cost of carry</strong> (Part
          1). The key principle for a hedger is <strong>convergence</strong>: as expiry approaches the
          basis narrows to zero — at expiry <code>S = F</code>. Meanwhile, though, the basis
          fluctuates, and that's the residual risk. Rigorously, <em>hedging is a bet on the
          basis</em>. This is <strong>basis risk</strong>: why no hedge is perfect.
        </p>

        <h2>The minimum-variance hedge ratio: how many contracts</h2>
        <p>
          A "one-to-one" hedge assumes your asset and the future move identically (correlation = 1):
          almost never true. The <strong>minimum-variance hedge ratio</strong> corrects for this:
        </p>
        <p><code>h = ρ · (σ_S / σ_F)</code> — (position opposite to the exposure)</p>
        <p>
          where <code>ρ</code> is the correlation between changes in your asset (S) and the future
          (F). It is, in effect, the <strong>beta</strong> of your exposure to the future.{" "}
          <strong>Example:</strong> asset less volatile than the future (σ_S/σ_F = 0.8) and
          correlation 0.9 → <code>h = 0.72</code>: you hedge <strong>72%</strong> of the notional, not
          100%. When <code>ρ &lt; 1</code> a residual risk remains (the unhedged variance) equal to{" "}
          <code>σ_S² · (1 − ρ²)</code>: the hedge has <em>minimized</em>, not erased, risk.
        </p>

        <h2>Cross-hedging: when the exact future doesn't exist</h2>
        <p>
          Often there's no future on your specific asset: you hedge with a{" "}
          <strong>correlated but different-class</strong> underlying — e.g. a corporate bond portfolio
          hedged with government bond futures. It works as far as the correlation is high and stable;
          that's exactly where basis risk lives.
        </p>

        <h2>Controlling beta and duration without touching the portfolio</h2>
        <p>
          The same logic enables <strong>synthetic</strong> risk control. For equities, the hedge
          ratio is adjusted for the portfolio's <strong>beta</strong> to the index future: you compute
          the exact number of contracts to move risk from the current profile to the desired one —
          without selling a single stock. For fixed income you think in <strong>duration</strong>: to
          cut duration to a target, you compute how many bond futures shorten rate sensitivity.
        </p>

        <h2>The operational choice: stack vs strip hedge</h2>
        <ul>
          <li>
            <strong>Stack hedge</strong> — put the whole position on the most <strong>deferred</strong>{" "}
            contract and hold it. Simple; exposes you <em>only</em> to basis risk at termination. But
            the deferred contract is often less liquid.
          </li>
          <li>
            <strong>Strip hedge</strong> — start with the <strong>near</strong> contract and then{" "}
            <strong>roll</strong> (close the near, open the next), repeating to the horizon. Tighter,
            but exposes you to <strong>rollover risk</strong>: the final price depends on the{" "}
            <em>calendar spread</em> at the roll.
          </li>
        </ul>
        <p>
          There's no right answer: you choose between the <strong>certainty of a known basis
          risk</strong> (stack) and the <strong>risk of unknown future rates on the roll cost</strong>{" "}
          (strip). It's a judgment on your scenario.
        </p>

        <h2>In short</h2>
        <p>
          Hedging = transforming risk into <strong>basis risk</strong>; the basis converges to zero at
          expiry; the <strong>minimum-variance hedge ratio</strong> (<code>ρ·σ_S/σ_F</code>) tells you{" "}
          <em>how much</em> to hedge; <strong>cross-hedging</strong> is for when the exact future
          doesn't exist; beta and duration are controlled <strong>synthetically</strong>; stack vs
          strip is the operational choice. A hedge doesn't eliminate risk: it <strong>reduces and
          manages</strong> it. It all starts at the{" "}
          <a href="/en/kb/derivati-spiegati-semplice">derivatives pillar</a>.
        </p>

        <h2>Sources</h2>
        <p>
          J. Hull, <em>Options, Futures and Other Derivatives</em> (minimum-variance hedge ratio,
          basis) · CME Group — education · Borsa Italiana — glossary.
        </p>

        <p style={KB_DISC}>
          Educational content, not a recommendation. Past performance does not guarantee future
          results.
        </p>
      </div>
    ),
  },

  {
    slug: "certificati-covered-warrant",
    title: "Certificates and covered warrants: what they are and how they work",
    metaDescription:
      "What certificates are (and why they're bonds with issuer risk), the tax efficiency of offsetting capital losses, and covered warrants explained through the delta and theta greeks. Educational guide with video.",
    tags: ["derivatives", "certificates", "covered warrants", "tax", "education"],
    readTime: "12 min",
    author: "Redazione",
    datePublished: "2026-06-25",
    dateUpdated: "2026-06-25",
    coverImage: CoverCertificati,
    canonical: `${DOMAIN}/en/kb/certificati-covered-warrant`,
    content: (
      <div>
        <h1>Certificates and covered warrants: what they are and how they work</h1>

        <p style={KB_DISC}>
          Educational content. The numeric examples (including the AXA case) come from industry
          technical documentation and illustrate a mechanism: they are not recommendations or
          forecasts. Certificates and covered warrants are complex instruments.
        </p>

        <h2>Two tools for two different problems</h2>
        <p>
          Facing a portfolio with losing positions, or the fear of a correction, the sophisticated
          investor opens the toolbox. Inside are two often-confused families:{" "}
          <strong>certificates</strong>, <em>precision</em> tools to build a tailored outcome, and{" "}
          <strong>covered warrants</strong>, <em>power</em> tools for leveraged exposure and hedging.
          It isn't about which is better — it's about which is right for the job.
        </p>

        <YouTubeEmbed
          videoId="ML_oEr8mYho"
          title="Certificates vs Covered Warrants: tax efficiency, advanced hedging"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <h2>Certificates: first of all, they are bonds</h2>
        <p>
          The key thing to grasp isn't the return, it's the <strong>legal nature</strong>: a
          certificate is a <strong>senior unsecured bond</strong> of the issuer. So the first risk
          isn't the market — it's <strong>issuer risk</strong>: if the issuing bank fails, your
          investment is at risk regardless of the underlying.
        </p>
        <p>
          <strong>The market has shifted.</strong> Between 2010 and 2021 (ACEPI data){" "}
          <em>Express</em> certificates — almost <strong>80%</strong> of issuance in 2013 — collapsed
          to <strong>36%</strong> by 2021, while <em>Cash Collect</em> went from near-zero to about{" "}
          <strong>half</strong> the market. Investors swapped an Express "jackpot" for a Cash Collect
          "regular paycheck": <strong>certainty became the new gold</strong>.
        </p>

        <h2>The hidden weapon: tax efficiency</h2>
        <p>
          Certificate proceeds (coupons <em>and</em> capital gains) are classified as{" "}
          <strong>"redditi diversi"</strong> (miscellaneous income). The rate is 26% like elsewhere,
          but — unlike stock dividends and bond coupons, which are <em>capital income</em> — this
          category can <strong>offset prior capital losses</strong> (the "tax basket") from stocks,
          ETFs, derivatives, other certificates. For an investor sitting on losses, a certificate
          generating gains becomes a <strong>tactical tool</strong> to recover them by year-end.
        </p>

        <h2>Why market fear "helps" the issuer</h2>
        <p>
          A certificate's price is built from the options inside it, and their cost depends on{" "}
          <strong>volatility</strong>. When volatility spikes — VIX peaks — option cost rises, and for
          whoever <em>sells</em> options to the investor (the issuer) that's an opportunity: they can
          offer <strong>richer coupons or deeper protection barriers</strong>. A moment of panic can
          be the best moment to issue certain products. (To handle dividend uncertainty,{" "}
          <em>decrement indices</em> emerged: a fixed synthetic dividend subtracted daily, so pricing
          stays predictable.)
        </p>

        <h2>The AXA case: restructuring a losing position</h2>
        <p>
          ACEPI documentation offers a concrete example. An investor holds AXA shares at{" "}
          <strong>−9%</strong>: to break even the stock must rise nearly 10%. The <em>recovery</em>{" "}
          strategy: sell the share (crystallizing the loss, which can then be offset) and buy a{" "}
          <strong>Cash Collect Memory</strong> on AXA — price €91.16, underlying at €15.02,{" "}
          <strong>barrier at €10.33</strong> (a cushion of over <strong>31%</strong>),{" "}
          <strong>5.85%</strong> premium with memory effect. The key scenario: if at maturity AXA is
          flat <em>or even down but above the barrier</em>, the certificate repays 100 plus all
          coupons → a <strong>41.78%</strong> return on a stock that didn't move.
        </p>
        <p>
          <strong>The trade-off is everything:</strong> you give up dividends and <em>any</em> upside
          beyond the predetermined return. You swap <strong>unlimited but uncertain upside</strong>{" "}
          for a <strong>defined return with high probability</strong>. That's the point of a
          certificate: structuring a specific outcome.
        </p>

        <h2>Covered warrants: securitized options, pure leverage</h2>
        <p>
          Unlike classic warrants, <strong>covered warrants</strong> are created by financial
          institutions on already-listed assets: in essence <strong>securitized options</strong> for
          everyone — a <em>call</em> gives the right to buy, a <em>put</em> to sell. Two "controls"
          keep you from crashing:
        </p>
        <ul>
          <li>
            <strong>Delta = the speedometer.</strong> How much the warrant moves as the underlying
            moves. Delta 0.5: the stock rises €1, the call rises €0.50.
          </li>
          <li>
            <strong>Theta = the fuel.</strong> How much value the warrant <strong>burns each
            day</strong> just from time passing. You can be right on direction, but if the move is too
            slow theta eats the gain.
          </li>
        </ul>
        <p>
          Two second-order greeks complete the picture: <strong>gamma</strong> (acceleration, maximal{" "}
          <em>at-the-money</em>) and <strong>vega</strong> (sensitivity to volatility). Watch
          leverage: the <em>gearing</em> is raw; the real measure is{" "}
          <strong>elasticity = gearing × delta</strong>.
        </p>

        <h2>Using them defensively: the protective put</h2>
        <ul>
          <li>
            <strong>Static</strong> — the "one-size policy": on a €650,000 portfolio, ~1,000 warrants
            for 1:1 coverage at maturity. Simple, but imprecise over the holding period.
          </li>
          <li>
            <strong>Dynamic (delta hedging)</strong> — coverage sized by delta (e.g. delta 0.4 →
            ~2,500 warrants): more precise, but needs constant monitoring.
          </li>
        </ul>

        <h2>In short</h2>
        <ul>
          <li>
            <strong>Certificates</strong> = precision: recovery, income, tax efficiency. They build a{" "}
            <em>specific payoff</em>.
          </li>
          <li>
            <strong>Covered warrants</strong> = power: leverage and hedging, but must be understood to
            avoid harm.
          </li>
        </ul>
        <p>
          Start from the basics in the{" "}
          <a href="/en/kb/derivati-spiegati-semplice">derivatives pillar</a>, or see how hedging
          enters ETFs in{" "}
          <a href="/en/kb/currency-hedging-hedged-vs-unhedged-etfs-and-how-to-analyze-them">
            hedged vs unhedged
          </a>
          .
        </p>

        <h2>Sources</h2>
        <p>
          ACEPI — technical presentation and case study · Goldman Sachs — covered warrants handbook ·
          Borsa Italiana — glossary.
        </p>

        <p style={KB_DISC}>
          Illustrative examples for educational purposes, not recommendations. Issuer risk and
          leverage can cause losses, possibly total. Past performance does not guarantee future
          results.
        </p>
      </div>
    ),
  },

  {
    slug: "metriche-di-portafoglio",
    title: "How to measure an investment: return, risk and portfolio metrics",
    metaDescription:
      "CAGR, drawdown, Sharpe, Sortino, Calmar, capture ratio, alpha and beta, honest backtesting and net returns: each metric with formula, meaning and a worked example. A quantitative guide for investors.",
    tags: ["metrics", "risk", "sharpe", "backtesting", "quant", "education"],
    readTime: "15 min",
    author: "Redazione",
    datePublished: "2026-06-25",
    dateUpdated: "2026-06-25",
    coverImage: CoverMetriche,
    canonical: `${DOMAIN}/en/kb/metriche-di-portafoglio`,
    content: (
      <div>
        <h1>How to measure an investment: return, risk and portfolio metrics</h1>

        <p style={KB_DISC}>
          Educational and methodological content. The formulas and examples explain <em>how to
          measure</em> an investment, not which to choose. Past performance does not guarantee future
          results.
        </p>

        {/* TODO: when available, embed the "Portfolio Financial Metrics" (EN) / "Metriche Finanziarie di Portfolio Management" (IT) playlist with <YouTubeEmbed playlistId="..." /> */}

        <h2>Why metrics, and why these</h2>
        <p>
          A return alone says almost nothing: +30% earned by risking half your capital is not the same
          +30% earned with mild swings. Measuring well means separating <strong>return</strong>,{" "}
          <strong>risk</strong>, and the <strong>quality of the process</strong> behind them. These
          are also the metrics we use in the monthly updates and the model monitor.
        </p>

        <h2>1. CAGR vs arithmetic mean — the return that actually counts</h2>
        <p>The arithmetic mean of returns <strong>misleads</strong>. The <strong>CAGR</strong> (compound growth rate) is the only one that reflects real wealth:</p>
        <p><code>CAGR = (V_end / V_start)^(1/n) − 1</code></p>
        <p>
          <strong>Example:</strong> +50% one year, −50% the next. Arithmetic mean = 0%. But 100 → 150
          → 75: you <strong>lost 25%</strong>. CAGR is <code>(0.75)^(1/2) − 1 ≈ −13.4%</code>. The gap
          is <em>volatility drag</em>.
        </p>

        <h2>2. Drawdown, semivariance, time to recovery — how bad it can get</h2>
        <ul>
          <li><strong>Maximum drawdown (MDD):</strong> the worst peak-to-trough decline. 100 to 60 → MDD = <strong>−40%</strong>.</li>
          <li><strong>Semivariance / downside deviation:</strong> measures only deviations <strong>below</strong> a threshold: <code>σ_down = √( mean( min(r − MAR, 0)² ) )</code>.</li>
          <li><strong>Time to recovery (TTR):</strong> a −50% needs <strong>+100%</strong> to recover; at 7% annual compounding that's about <strong>10 years</strong>. Loss is asymmetric.</li>
        </ul>

        <h2>3. Sharpe, Sortino, Calmar — risk-adjusted return</h2>
        <ul>
          <li><strong>Sharpe</strong> = <code>(R_p − R_f) / σ_p</code>. With 8% return, 2% risk-free, 12% volatility → Sharpe = <strong>0.5</strong>.</li>
          <li><strong>Sortino</strong> = like Sharpe, but the denominator uses only <em>downside deviation</em>: <code>(R_p − R_f) / σ_down</code>.</li>
          <li><strong>Calmar</strong> = <code>CAGR / |MDD|</code>. With 12% CAGR and −40% MDD → Calmar = <strong>0.3</strong>.</li>
          <li><strong>Pitfall:</strong> Sharpe assumes ~normal returns; with fat tails and skew it can mislead. Three lenses, not one.</li>
        </ul>

        <h2>4. Capture ratio, alpha and beta — skill or just market exposure?</h2>
        <p>The CAPM decomposes return: <code>R_p = R_f + β·(R_m − R_f) + α</code>.</p>
        <ul>
          <li><strong>Beta (β)</strong> = sensitivity to the market: <code>β = Cov(R_p, R_m) / Var(R_m)</code>. β = 1.2 → you move 20% more than the market.</li>
          <li><strong>Alpha (α)</strong> = the return <em>not</em> explained by the market. The (claimed) skill.</li>
          <li><strong>Capture ratio:</strong> a good strategy has <em>up-capture</em> &gt; 100% and <em>down-capture</em> &lt; 100%.</li>
          <li>
            <strong>Meaning:</strong> don't pay "alpha" fees for what is just <strong>beta</strong>.
            More in <a href="/en/kb/what-are-alpha-and-beta-how-to-calculate-them">Alpha and Beta</a>.
          </li>
        </ul>

        <h2>5. Honest backtesting — out-of-sample, walk-forward, capacity</h2>
        <ul>
          <li><strong>Out-of-sample:</strong> test on data not used to build the strategy.</li>
          <li><strong>Walk-forward:</strong> re-fit and test "forward" on rolling windows.</li>
          <li><strong>Multiple testing / deflated Sharpe:</strong> try 100 strategies and some will look great <em>by luck</em>; the Sharpe must be "deflated" (López de Prado; Harvey-Liu-Zhu).</li>
          <li><strong>Capacity:</strong> a strategy that works on €100k may break at €100M. A backtest without costs and capacity is a drawing, not a proof.</li>
        </ul>

        <h2>6. Tax, costs and capacity — the real enemy of net returns</h2>
        <p>
          Costs (TER, spread, commissions) and tax (26% on capital gains in Italy){" "}
          <strong>compound against you</strong> year after year. High <strong>turnover</strong>{" "}
          realizes gains and pulls tax forward. Always measure <strong>net</strong>.
        </p>

        <h2>In short</h2>
        <p>
          Return (CAGR), risk (MDD, semivariance, TTR), risk-adjusted return (Sharpe, Sortino,
          Calmar), skill vs market (alpha/beta, capture), process honesty and net return. Try the
          concepts with the{" "}
          <a href="/en/kb/etf-portfolio-simulator-methodological-guide">ETF Portfolio Simulator</a>{" "}
          or go straight to the{" "}
          <a href={withUtm("/en/tools", { source: "site", medium: "article", campaign: "metriche-portafoglio" })}>
            free tools
          </a>
          .
        </p>

        <h2>Sources</h2>
        <p>
          López de Prado (2018), <em>Advances in Financial Machine Learning</em> · Harvey, Liu, Zhu
          (2016) · Sharpe (1966) · Sortino &amp; van der Meer (1991) · C. Bacon, <em>Practical
          Portfolio Performance Measurement</em> · SPIVA reports (S&amp;P).
        </p>

        <p style={KB_DISC}>
          Educational content, not advice or a recommendation. Past performance and backtests do not
          guarantee future results.
        </p>
      </div>
    ),
  },

  {
    slug: "analisi-mercati-mensile",
    title: "What we look at in markets each month: the macro analysis map",
    metaDescription:
      "Macro, the rates regime, geographic and sector rotations, risk: what AlphaFrame's monthly markets update contains, with the metrics explained and real examples.",
    tags: ["markets", "macro", "asset allocation", "rates", "education"],
    readTime: "10 min",
    author: "Redazione",
    datePublished: "2026-06-25",
    dateUpdated: "2026-06-25",
    coverImage: CoverMarketUpdate,
    canonical: `${DOMAIN}/en/kb/analisi-mercati-mensile`,
    content: (
      <div>
        <h1>What we look at in markets each month: the macro analysis map</h1>

        <p style={KB_DISC}>
          Educational content. The monthly analysis reads what has <em>already</em> happened and the{" "}
          <em>current</em> regime; it contains no forecasts or recommendations. Data come from public
          sources (FRED, ECB, Eurostat). Past performance does not guarantee future results.
        </p>

        <h2>Not "where the market is going", but "where we are"</h2>
        <p>
          Every month we publish a markets update. It doesn't promise to guess the future: it captures
          the <strong>present</strong> in a structured way, because most mistakes come from reading a
          single data point (equities rising) while ignoring what other markets signal underneath.
          Here we explain the <strong>five lenses</strong> we use.
        </p>

        <YouTubeEmbed
          playlistId="PL4Ivcohv28tL5aLn9l0mJsq7_f-n8euNk"
          title="Monthly Markets Updates — AlphaFrame"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <h2>The five lenses</h2>
        <p>
          <strong>1. Macro compared — growth, inflation, real rates.</strong> The point isn't the
          isolated figure but the <em>comparison</em>. Real example (May 2026): the US with real GDP
          ~+2.6% and core inflation ~2.7% while Fed Funds stay higher (~3.6%) → <strong>positive real
          rates without recession</strong>. Europe instead: growth ~+1% but inflation ~+3% with ECB
          deposits at 2% → the textbook calls this <strong>creeping stagflation</strong>.{" "}
          <em>(Real rate ≈ nominal rate − inflation.)</em>
        </p>
        <p>
          <strong>2. The regime and the yield curve.</strong> The spread between the 10-year and
          2-year yield is the barometer. An <strong>inversion</strong> (10y &lt; 2y) historically
          precedes recession; a return to parity (example May 2026: spread ~0.00%) signals a{" "}
          <strong>regime shift</strong>: extending duration is no longer rewarded.
        </p>
        <p>
          <strong>3. Geographic rotations.</strong> We compare indices by region (1-year performance).
          Example: S&amp;P 500 ~+24.7% vs MSCI Europe ~+5.8%. But the number must be{" "}
          <strong>contextualized</strong> (currency, sector composition).
        </p>
        <p>
          <strong>4. Sector rotations.</strong> Who leads and who lags, and at what risk price —
          looking at sector <strong>Sharpe</strong> and the level of the <strong>VIX</strong>. A
          sector Sharpe that "defies statistics" is a sign of concentration, not just strength.
        </p>
        <p>
          <strong>5. Risk implications.</strong> The "so what?": with a flat curve, active duration
          management loses power as a source of alpha, and attention shifts to credit selection or{" "}
          <em>barbell</em> strategies.
        </p>

        <h2>The metrics, one line each</h2>
        <ul>
          <li><strong>Real rate</strong> = nominal − inflation: how much monetary policy really bites.</li>
          <li><strong>Curve spread (10y−2y)</strong> = cycle tension: inversion → recession fear; flat → regime shift.</li>
          <li><strong>Sharpe</strong> = excess return per unit of volatility.</li>
          <li><strong>VIX</strong> = expected volatility: the "fear thermometer".</li>
          <li><strong>Currency-adjusted performance</strong> = an honest comparison across regions with different currencies.</li>
        </ul>

        <h2>A note on method</h2>
        <p>
          We always read <strong>past/present</strong>, never as a certain forecast; we use public
          data (FRED, ECB, Eurostat, ISTAT) and compare it rather than isolate it. It's the same rigor
          we apply to models — see the{" "}
          <a href="/en/kb/metriche-di-portafoglio">portfolio metrics</a>.
        </p>

        <h2>The report's outputs: real examples, commented</h2>
        <p>
          Below are slides from a real edition of the monthly report (the <strong>T8</strong> video
          series, sample month: April 2026). They are illustrative examples of the <em>kind</em> of
          analysis we share, not recommendations: indices and sectors are shown with descriptive
          names, not as buy suggestions.
        </p>

        <figure style={KB_FIG}>
          <img
            src={MktMacro}
            alt="Eurozone macro context: real GDP, HICP inflation and ECB deposit rate with their trends"
            style={KB_IMG}
          />
          <figcaption style={KB_CAP}>
            <strong>1 · Macro compared (Eurozone).</strong> The three top cards fix the month's key
            numbers — real GDP, HICP inflation, ECB rate — each with the <em>change</em> versus the
            prior period; the mini-charts below show the trend. The value isn't the single figure but
            the <strong>combined direction</strong>: weak growth with inflation still above target is
            the picture we read as stagflation-type pressure. Source: FRED / Eurostat.
          </figcaption>
        </figure>

        <figure style={KB_FIG}>
          <img
            src={MktGeo}
            alt="Geographic markets: 12-month cumulative performance (base 100) of S&P 500, MSCI World, MSCI Europe and Emerging Markets"
            style={KB_IMG}
          />
          <figcaption style={KB_CAP}>
            <strong>2 · Geographic rotations.</strong> Four major regions normalized to base 100 over
            the last year (S&amp;P 500, MSCI World, MSCI Europe, Emerging Markets). You read at a
            glance who leads and who lags, and above all the <strong>gap</strong>: here Emerging
            Markets break away and Europe trails the global market. Always contextualize with
            currency — part of the gap is an FX effect, not just the strength of the underlyings.
          </figcaption>
        </figure>

        <figure style={KB_FIG}>
          <img
            src={MktSectors}
            alt="US sectors: 1-month returns (bars) and 1-year returns (diamonds) for the main sectors"
            style={KB_IMG}
          />
          <figcaption style={KB_CAP}>
            <strong>3 · Sector rotations.</strong> US sectors ranked by return: bars are the last
            month, diamonds the last year. The interesting read is the <strong>divergence</strong>
            between the two horizons — a sector strong over the year but weak over the month signals a
            rotation underway. Extreme concentration in one area (here technology, far ahead over the
            year) is a sign of risk, not just strength.
          </figcaption>
        </figure>

        <figure style={KB_FIG}>
          <img
            src={MktBonds}
            alt="Euro fixed income: multi-horizon performance of Eurozone government bonds, corporates and US Treasuries"
            style={KB_IMG}
          />
          <figcaption style={KB_CAP}>
            <strong>4 · The fixed-income side.</strong> Multi-horizon performance (1M/3M/6M/1Y) of
            Eurozone government bonds, euro corporates and US Treasuries 7-10y. It's the lens on{" "}
            <strong>rates</strong>: when duration "bites", the sign can flip between nearby horizons.
            The Eurozone vs US Treasury comparison also shows the effect of the rate differential and
            the currency — the same theme as{" "}
            <a href="/en/kb/currency-hedging-hedged-vs-unhedged-etfs-and-how-to-analyze-them">
              currency hedging
            </a>
            .
          </figcaption>
        </figure>

        <p>
          None of these charts says "buy": they say <em>where we are</em> and <em>how risk is
          distributed</em>. Turning that snapshot into decisions requires the individual investor's
          context — and that's a separate step from the analysis.
        </p>

        <h2>Sources</h2>
        <p>FRED (St. Louis Fed) · ECB · Eurostat · ISTAT.</p>

        <p style={KB_DISC}>
          Educational content, not advice or a recommendation. Past performance does not guarantee
          future results.
        </p>
      </div>
    ),
  },

  {
    slug: "etf-race-1000-euro-2006",
    title: "$1,000 in 2006: 20 years of market in one ETF race",
    metaDescription:
      "$1,000 invested in 2006 across 13 ETFs, dividends reinvested, no rebalancing: where does it land in 20 years? The nominal podium, and the surprise once you adjust for inflation.",
    tags: ["etf", "returns", "inflation", "long term", "education"],
    readTime: "9 min",
    author: "Redazione",
    datePublished: "2026-06-26",
    dateUpdated: "2026-06-26",
    coverImage: CoverEtfRace,
    canonical: `${DOMAIN}/en/kb/etf-race-1000-euro-2006`,
    content: (
      <div>
        <h1>$1,000 in 2006: 20 years of market in one ETF race</h1>

        <p style={KB_DISC}>
          Educational content. A historical experiment on past data, not a recommendation or a
          forecast. Past performance is not a guarantee of future results.
        </p>

        <h2>The experiment, in one line</h2>
        <p>
          January 2006: <strong>$1,000</strong> on the same day into <strong>13 ETFs</strong> — 8 US
          sectors (Tech, Healthcare, Energy, Industrials, Consumer, Real Estate, Telecom, Utilities),
          Europe, Emerging Markets, Gold, 20+ year Treasury, TIPS. <strong>Dividends reinvested, no
          rebalancing, no timing.</strong> Just monthly closing prices — 245 data points — turned into
          an 80-second animated race.
        </p>

        <YouTubeEmbed
          videoId="OziDehedHWc"
          title="$1,000 in 2006: 20 years of market in 80 seconds"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <h2>The podium (in nominal terms)</h2>
        <div style={{ overflowX: "auto" }}>
          <table className="kb-table">
            <thead><tr><th></th><th>ETF</th><th>Final value</th><th>CAGR</th></tr></thead>
            <tbody>
              <tr><td>🥇</td><td>Technology (US)</td><td><strong>$21,544</strong></td><td>16.27%</td></tr>
              <tr><td>🥈</td><td>Gold</td><td>$7,856</td><td>10.65%</td></tr>
              <tr><td>🥉</td><td>Industrials (US)</td><td>$6,935</td><td>9.98%</td></tr>
              <tr><td>14th</td><td>TIPS</td><td>$1,960</td><td>3.36%</td></tr>
              <tr><td>15th</td><td>20y+ Treasury (TLT)</td><td>$1,741</td><td>2.76%</td></tr>
            </tbody>
          </table>
        </div>
        <p><strong>Three things the race shows better than any table:</strong></p>
        <ol>
          <li><strong>Tech's lead isn't linear:</strong> it stabilizes around 2010 and becomes uncatchable. Staying anchored to classical "defensive" sectors meant giving up a 3-4× multiple.</li>
          <li><strong>Gold beats 11 of 12 equity ETFs.</strong> The "dead asset" label collapses against $7,856 from $1,000.</li>
          <li><strong>The "safe haven" is the slowest boat:</strong> long Treasuries and TIPS barely more than doubled in 20 years — in <em>nominal</em> terms.</li>
        </ol>

        <h2>The twist: the same numbers, adjusted for inflation</h2>
        <p>
          US inflation from 2006 to 2026 was <strong>+66.8%</strong> (a 2006 dollar is worth about 60
          cents today). Deflating, <strong>every ETF loses about 40%</strong> of its apparent value.
          The <em>real</em> podium:
        </p>
        <div style={{ overflowX: "auto" }}>
          <table className="kb-table">
            <thead><tr><th></th><th>ETF</th><th>Real value</th><th>Real CAGR</th></tr></thead>
            <tbody>
              <tr><td>🥇</td><td>Technology (US)</td><td><strong>$12,668</strong></td><td>13.30%</td></tr>
              <tr><td>🥈</td><td>Gold</td><td>$4,413</td><td>7.58%</td></tr>
              <tr><td>🥉</td><td>Industrials (US)</td><td>$4,131</td><td>7.23%</td></tr>
              <tr><td>…</td><td>20y+ Treasury (TLT)</td><td>~$1,057</td><td>+0.27%</td></tr>
              <tr><td>…</td><td>TIPS</td><td>~$1,177</td><td>+0.81%</td></tr>
            </tbody>
          </table>
        </div>
        <p>
          The figure that sticks: <strong>TLT and TIPS, the "safe havens", are essentially flat over
          20 real years.</strong> They didn't protect, they merely <em>kept</em> value. Inflation is
          the tide that moves all boats — and it must always be measured.
        </p>

        <h2>What to take away</h2>
        <ul>
          <li><strong>Always measure in real terms.</strong> A double-digit nominal return can hide a modest real one: only a <strong>positive real CAGR</strong> builds wealth.</li>
          <li><strong>"Safe haven" is a nominal label.</strong> In real terms, the safe harbor can be nearly flat.</li>
          <li><strong>Dividends matter:</strong> this is a <em>total return</em> experiment. Without them, the numbers change.</li>
          <li><strong>Concentration ≠ guarantee:</strong> Tech won <em>this</em> 20-year window. Past performance is not a guarantee.</li>
        </ul>

        <h2>Methodology</h2>
        <p>
          Total return (dividends reinvested), no rebalancing, monthly prices, declared 2006→2026
          period, nominal <strong>and</strong> real comparison (US CPI deflator). The same discipline
          we apply to models: dated data, explicit assumptions, no start-date cherry-picking.
        </p>

        <h2>Continue</h2>
        <ul>
          <li>Rerun the experiment on your parameters with the <a href="/en/kb/etf-portfolio-simulator-methodological-guide">ETF Portfolio Simulator</a>.</li>
          <li>Why "real" beats "nominal": <a href="/en/kb/metriche-di-portafoglio">how to measure an investment</a>.</li>
          <li>All data races: <a href="https://youtube.com/playlist?list=PLFU2zdYFrFvk" target="_blank" rel="noopener noreferrer">Data Races playlist</a>.</li>
        </ul>

        <p style={KB_DISC}>
          Educational experiment on historical data. Not advice or a recommendation. Past performance
          does not guarantee future results.
        </p>
      </div>
    ),
  },

  {
    slug: "magnifici-7-concentrazione-mercato",
    title: "The Magnificent 7 and market concentration",
    metaDescription:
      "The market-cap race of the seven tech giants and what it means for index investors: how diversified are you really when you buy 'the market'?",
    tags: ["stocks", "indices", "concentration", "risk", "education"],
    readTime: "8 min",
    author: "Redazione",
    datePublished: "2026-06-26",
    dateUpdated: "2026-06-26",
    coverImage: FactorCover,
    canonical: `${DOMAIN}/en/kb/magnifici-7-concentrazione-mercato`,
    content: (
      <div>
        <h1>The Magnificent 7 and market concentration</h1>

        <p style={KB_DISC}>
          Educational content. No name is a recommendation. Past performance does not guarantee future
          results.
        </p>

        <h2>The race of the giants</h2>
        <p>
          Apple, Microsoft, Alphabet, Amazon, Nvidia, Meta, Tesla: the <strong>"Magnificent 7"</strong>.
          The market-cap race of these seven names tells one of the most important — and least
          understood — stories for investors today: <strong>concentration</strong>.
        </p>
        <p><em>(Dedicated video coming soon.)</em> In the meantime, watch the full series:</p>

        <YouTubeEmbed
          playlistId="PLFU2zdYFrFvk"
          title="Data Races — AlphaFrame"
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        <h2>Why it matters, even if you don't own those stocks</h2>
        <p>
          Most "passive" investors think they're buying <em>the market</em> with an index ETF. But
          when 7 stocks grow to a huge share of the index, "the market" effectively becomes <strong>a
          concentrated bet on 7 companies in the same sector</strong>. That isn't wrong per se — but
          it's worth <strong>knowing what you own</strong>:
        </p>
        <ul>
          <li><em>Apparent</em> diversification (hundreds of stocks) can hide very high <em>effective</em> concentration at the top.</li>
          <li>The index's return in recent years was largely driven by those few names: it's <strong>concentrated beta</strong>, not a broad balanced exposure.</li>
          <li>When leadership reverses, the same concentration that pushed it up amplifies the way down.</li>
        </ul>

        <h2>The link to alpha, beta and risk</h2>
        <p>
          Buying the index today largely means buying the performance of those 7 stocks — i.e.{" "}
          <strong>beta</strong> to a narrow cluster. Understanding how much of your "market" is
          concentrated is the first step to decide whether that's enough. More in{" "}
          <a href="/en/kb/what-are-alpha-and-beta-how-to-calculate-them">Alpha and Beta</a> and in{" "}
          <a href="/en/kb/metriche-di-portafoglio">how to measure an investment</a>.
        </p>

        <h2>Methodology</h2>
        <p>
          Market capitalization over time (market data source), reshuffling bar race. Period declared
          in the video; no projections.
        </p>

        <p style={KB_DISC}>
          Educational content, not a recommendation. Past performance does not guarantee future
          results.
        </p>
      </div>
    ),
  },
  {
    slug: "risk-tolerance-what-a-questionnaire-really-measures",
    title: "Risk tolerance: what a questionnaire really measures (and what it doesn't)",
    metaDescription:
      "Why banks and advisers make you fill in a questionnaire before investing, what it measures (tolerance, capacity, horizon), why declared tolerance differs from lived tolerance, and how to try the logic with Risk Lab, a free educational exercise.",
    tags: ["risk", "risk tolerance", "drawdown", "education", "tools"],
    readTime: "9 min",
    author: "Editorial team",
    datePublished: "2026-07-12",
    dateUpdated: "2026-07-12",
    coverImage: CoverRiskLab,
    canonical: `${DOMAIN}/kb/risk-tolerance-what-a-questionnaire-really-measures`,
    content: (
      <div>
        <h1>Risk tolerance: what a questionnaire really measures (and what it doesn't)</h1>

        <p style={KB_DISC}>
          Purely educational content. It is not personalised advice, a recommendation or a
          suitability assessment. The Risk Lab mentioned here is a teaching exercise: it collects and
          stores no data and produces no recommendations.
        </p>

        <h2>Why you're asked to fill in a questionnaire</h2>
        <p>
          Anyone who has opened a brokerage account has met it: before you invest, banks and advisers
          must ask about goals, horizon, experience and how you react to losses. It isn't decorative
          bureaucracy: European investment-services rules (the directive known as MiFID II) require
          intermediaries to <strong>assess the suitability</strong> of what they propose for the
          person in front of them. The underlying idea is simple and serious: an investment can be
          excellent in the abstract and wrong <em>for a specific person</em>, if it forces them to
          sell at the worst moment.
        </p>

        <h2>The three dimensions (and the one questionnaires measure worst)</h2>
        <p>A sound profiling process looks at three different things:</p>
        <ul>
          <li>
            <strong>Risk tolerance</strong> (willingness): how much loss you can{" "}
            <em>psychologically</em> bear without abandoning the plan. A personal trait, not a
            balance-sheet item.
          </li>
          <li>
            <strong>Risk capacity</strong> (ability): how much loss your <em>finances</em> can absorb
            without compromising essential goals — it depends on income, reserves, debts, expenses.
          </li>
          <li>
            <strong>Time horizon</strong>: how long you can leave the capital untouched. It's what
            turns a temporary fall into a permanent problem, or the other way round.
          </li>
        </ul>
        <p>
          The weak spot is the first one: tolerance is <strong>declared</strong> in calm markets, in
          front of a form, and <strong>lived</strong> in falling markets, in front of a red balance.
          The two often disagree.
        </p>

        <h2>The number linking declared and lived: drawdown</h2>
        <p>
          The most concrete measure of "how much it hurts" is the <strong>maximum drawdown</strong>{" "}
          (MDD): the distance between the portfolio's highest point and the following trough. It has
          two properties every investor should know. First, it is <em>asymmetric</em>: after −20% you
          need +25% to break even; after −50% you need <strong>+100%</strong>. Second, it has a
          duration: what matters is not only how far you fall, but how many years you stay below the
          previous peak. Other metrics are covered in{" "}
          <a href="/en/kb/metriche-di-portafoglio">how to measure an investment</a>.
        </p>

        <h2>Declared vs lived: three dates to remember</h2>
        <ul>
          <li>
            <strong>2008</strong>: global equities lost roughly a third within the year; from the 2007
            peak to the 2009 trough, more than half. Those who had declared "I can bear −15%"
            discovered what seeing double that feels like.
          </li>
          <li>
            <strong>March 2020</strong>: about −30% in a month, followed by a very fast recovery.
            Anyone looking only at year-end data never saw it — anyone invested certainly lived it.
          </li>
          <li>
            <strong>2022</strong>: the year that surprised the cautious. Bonds fell too (about −17%
            for the euro aggregate): the traditional shelter didn't shelter, and defensive portfolios
            suffered as much as balanced ones, or more.
          </li>
        </ul>

        <h2>Try the logic from the inside: Risk Lab</h2>
        <p>
          To make all this tangible we built{" "}
          <a href="/en/tools?tool=risk">
            <strong>Risk Lab</strong>
          </a>
          , a free educational exercise that replicates the <em>logic</em> of profiling: seven
          questions produce a score and a <strong>teaching profile-type</strong> (from Defensive to
          Aggressive, each with an illustrative equity/bond mix); the eighth question sets your
          tolerance threshold; the exercise then compares that threshold with{" "}
          <strong>25 years of stylised history</strong> of the mix: maximum drawdown, years to reclaim
          the peak, final value of €10,000.
        </p>
        <p>
          The interesting part is when the two numbers clash: if the mix's history exceeded the
          declared threshold, the exercise says so — with the figures — and shows, for comparison,
          the profile-type that stayed within the threshold, and its cost in foregone growth. With
          the most cautious thresholds (−5%, −15%) you'll discover a lesson worth the whole
          exercise: over those 25 years <strong>no</strong> illustrative mix, not even the most
          defensive, stayed within the threshold — mostly because of 2022. Absolute safety is not on
          the market's menu.
        </p>

        <h2>What Risk Lab is NOT (important)</h2>
        <ul>
          <li>
            <strong>Not a suitability questionnaire</strong>: it does not consider your overall
            wealth, your real goals or your capacity to bear losses.
          </li>
          <li>
            <strong>No recommendations</strong>: profile-types and mixes are teaching categories on
            stylised series, not investment proposals.
          </li>
          <li>
            <strong>Nothing is stored</strong>: answers and results stay in your browser.
          </li>
          <li>
            Data are <strong>annual closes</strong>: real intra-year falls are deeper than shown
            (March 2020 doesn't appear at all) — the exercise says so explicitly.
          </li>
        </ul>
        <p>
          Real decisions require a full assessment with an authorised provider: the real
          questionnaire, with all three dimensions.
        </p>

        <p style={KB_DISC}>
          Educational content, not a recommendation or advice. Past returns are not indicative of
          future ones. The series mentioned are stylised, gross of costs, taxes and inflation.
        </p>
      </div>
    ),
  },
  {
    slug: "derivati-concetti-chiave",
    title: "Derivatives: the eight key steps of the series, from futures to Black-Scholes",
    metaDescription:
      "The ideas that hold the whole derivatives video series together: margins and mark-to-market, hedge ratio, cost of carry, DV01 and swaps, option payoffs, put-call parity, binomial trees, Black-Scholes and implied volatility. With formulas and examples.",
    tags: ["derivatives", "futures", "options", "black-scholes", "education"],
    readTime: "12 min",
    author: "Redazione",
    datePublished: "2026-10-15",
    dateUpdated: "2026-10-15",
    coverImage: CoverDerivatiPillar,
    canonical: `${DOMAIN}/en/kb/derivati-concetti-chiave`,
    content: (
      <div>
        <h1>Derivatives: the eight key steps of the series, from futures to Black-Scholes</h1>

        <p style={KB_DISC}>
          Educational and informational content only. It does not constitute investment advice, a
          recommendation or a solicitation. Derivatives are complex instruments and can lead to
          losses exceeding the capital invested. Numerical examples are hypothetical.
        </p>

        <p>
          The derivatives video series now runs to a dozen episodes, and there is no need to
          summarise them all. These are the eight steps that hold up the rest: the ones worth having
          clear before looking at a pricing model or reading a structured product's prospectus. Each
          step points to the episode that covers it in full, with video and companion note, on the{" "}
          <a href="/en/series/derivati">series page</a>.
        </p>

        <h2>1. Futures work without trust</h2>
        <p>
          A private forward contract holds as long as the counterparty stays solvent. Futures start
          from the opposite assumption: trust is not needed. A clearing house stands between buyer
          and seller, and every evening the position is revalued at the settlement price. Losers pay,
          winners collect. If the account falls below the maintenance margin, a margin call follows.
        </p>
        <p>
          The consequence is a curious one. The system as a whole becomes safer, because no loss can
          build up for months without being paid; the individual trader is more exposed, because
          with a 10% margin a 3% drop in the underlying wipes out 30% of what was posted. →{" "}
          <a href="/en/series/derivati#ep-2">Episode 2</a>
        </p>

        <h2>2. Hedging reduces risk; it does not remove it</h2>
        <p>
          Hedging an exposure with futures swaps price risk for a smaller, different risk, basis
          risk: the gap between spot and futures prices on the day the hedge is closed. The number of
          contracts that minimises the variance of the hedged position depends on correlation and
          volatility:
        </p>
        <BM>{tex`h^{*} = \rho\,\frac{\sigma_S}{\sigma_F}`}</BM>
        <p>
          With a correlation of 0.9 and volatilities of 2% and 2.5%, the optimal ratio is 0.72, not 1.
          For an equity portfolio the same role is played by beta against the index. In both cases
          the estimates come from the past and can let you down exactly when the hedge matters most.
          → <a href="/en/series/derivati#ep-3">Episode 3</a>
        </p>

        <h2>3. A forward price is a calculation, not a forecast</h2>
        <p>
          Buying the underlying today and holding it to expiry must cost the same as buying it
          forward. If it didn't, someone would buy where it is cheap and sell where it is dear until
          the gap closed. Hence the cost-of-carry formula:
        </p>
        <BM>{tex`F_0 = S_0\, e^{(r-q)T}`}</BM>
        <p>
          where <IM>{tex`q`}</IM> is the dividend yield for an index, the foreign interest rate for a
          currency, or the convenience yield net of storage costs for a commodity. With the index at
          100, rates at 4% and dividends at 2%, the one-year price is about 102.02. A future above
          spot does not mean the market expects a rise: it means money costs more than the dividends
          pay. The same logic gives forward interest rates, which are implied by today's zero curve
          rather than predicted. → <a href="/en/series/derivati#ep-4">Episode 4</a> ·{" "}
          <a href="/en/series/derivati#ep-5">Episode 5</a>
        </p>

        <h2>4. Interest-rate risk is measured in money per basis point</h2>
        <p>
          For a bond portfolio the useful question is concrete: how much is lost if yields rise by
          one basis point? That is DV01.
        </p>
        <BM>{tex`\text{DV01} = P \cdot D_{mod} \cdot 0.0001`}</BM>
        <p>
          Ten million with a modified duration of 6 gives €6,000 per basis point; if a bond future has
          a DV01 of €90, the hedge needs about 67 contracts. It works for small parallel shifts of the
          curve, and only for those. An interest rate swap works on the same lever: paying fixed and
          receiving floating is, in effect, being long a floating-rate bond and short a fixed-rate one,
          and the swap is worth the difference between the two. →{" "}
          <a href="/en/series/derivati#ep-6">Episode 6</a> ·{" "}
          <a href="/en/series/derivati#ep-7">Episode 7</a>
        </p>

        <h2>5. An option is an asymmetry, and asymmetry has a price</h2>
        <p>
          A future gains and loses in a straight line. A long option does not: above the strike the
          call follows the underlying, below it the loss stops at the premium paid.
        </p>
        <BM>{tex`\text{call} = \max(S_T - K,\,0) \qquad \text{put} = \max(K - S_T,\,0)`}</BM>
        <p>
          That bent shape costs money. The premium holds intrinsic value plus a time value that pays
          for the chance of favourable moves before expiry, and that erodes day after day. On the
          other side, the option writer collects the premium and takes on the risk the buyer has
          offloaded. Multi-leg strategies (covered calls, protective puts, spreads, straddles) do not
          create value from nothing: they move gains and losses from one part of the chart to another,
          and those that look almost safe usually hide a rare but large loss. →{" "}
          <a href="/en/series/derivati#ep-8">Episode 8</a> ·{" "}
          <a href="/en/series/derivati#ep-10">Episode 10</a>
        </p>

        <h2>6. Calls and puts are tied by a constraint</h2>
        <p>
          Before any model there is a relationship that leaves no room for opinion. A call plus a
          deposit equal to the discounted strike pays exactly the same at expiry as a put plus the
          underlying, so today they must cost the same:
        </p>
        <BM>{tex`c + K e^{-rT} = p + S_0`}</BM>
        <p>
          With underlying and strike at 100, rates at 3% and one year to expiry, if the call is worth
          10 the put must be worth 7.04. If prices across an option chain break parity, net of costs
          and dividends, something somewhere is wrong. →{" "}
          <a href="/en/series/derivati#ep-9">Episode 9</a>
        </p>

        <h2>7. Pricing by replication</h2>
        <p>
          The most important conceptual step in the series is also the easiest to see on a binomial
          tree. If an underlying at 100 can rise to 110 or fall to 90, half a share minus one call
          with strike 100 is worth 45 either way. A riskless portfolio must earn the risk-free rate;
          at 5% it is worth 42.81 today, so the call is worth 50 minus 42.81, that is 7.19.
        </p>
        <p>
          The probability that the price goes up never enters the calculation. Only what can be bought
          and sold today does. The same idea underpins far more complex models, and it explains why
          an option's price does not depend on the buyer's optimism. The number of shares that makes
          the portfolio riskless, 0.5 here, is the delta. →{" "}
          <a href="/en/series/derivati#ep-11">Episode 11</a>
        </p>

        <h2>8. Black-Scholes and implied volatility</h2>
        <p>
          When the tree's steps become infinitesimal, the underlying follows geometric Brownian
          motion: uncertainty grows with the square root of time and the log of the price is normally
          distributed. A 20% annual volatility becomes roughly 5.8% over a month. Itô's lemma then
          leads to the Black-Scholes formula:
        </p>
        <BM>{tex`c = S_0 N(d_1) - K e^{-rT} N(d_2)`}</BM>
        <p>
          Of its five inputs, four can be observed in the market; volatility cannot. That is why the
          market uses the formula backwards: it starts from the price and backs out implied
          volatility. With underlying and strike at 100, rates at 3% and 20% volatility, the call is
          worth about 9.41; if it trades at 10.50, the market is pricing a volatility close to 22.8%.
          The fact that implied volatility changes from one strike to the next, the smile, is the most
          visible sign of the model's limits: jumps, fat tails, volatility that does not stand still.
          → <a href="/en/series/derivati#ep-12">Episode 12</a>
        </p>

        <h2>The common thread</h2>
        <p>
          The eight steps tell the same story from different angles. A derivative's price does not
          depend on what we think will happen, but on what can be replicated today with instruments
          that already trade, and arbitrage is the mechanism that keeps prices together.
          Understanding this does not make derivatives less risky. It does help you recognise when a
          product promises something the numbers cannot deliver.
        </p>
        <p>
          The full episodes, each with its own note, are in the{" "}
          <a href="/en/series/derivati">Derivatives series</a>. For the basics:{" "}
          <a href="/en/kb/derivati-spiegati-semplice">Derivatives: what they are and how they work</a>.
        </p>

        <h2>Sources</h2>
        <p>
          J. C. Hull, <em>Options, Futures and Other Derivatives</em>, 10th ed., ch. 2-7 and 10-15 ·
          Borsa Italiana, derivatives glossary.
        </p>

        <p style={KB_DISC}>
          This information is for educational purposes only. It is not a recommendation or
          personalised advice. Past performance is not a guarantee of future results.
        </p>
      </div>
    ),
  },
];

// Append the FIRE article and the new KB articles to the array
const allArticlesEN = [...articles.slice(0, articles.length), fireArticleEN, compoundArticleEN, ...newKbArticlesEN];

export const getArticleBySlug = (slug) => allArticlesEN.find((a) => a.slug === slug);

export default allArticlesEN;
